import { useRef, useEffect, useLayoutEffect, useMemo, useCallback, useState, forwardRef, useImperativeHandle, useId } from 'react';

import { BryntumScheduler } from '@bryntum/scheduler-react';
import { Scheduler, Menu } from '@bryntum/scheduler';
import '@bryntum/scheduler/scheduler.css';
// A Bryntum theme must be bundled so it is present when the widget initialises.
// Without it Bryntum treats the theme as "missing" and auto-injects one from its
// CDN, picking svalbard-dark/light from the OS `prefers-color-scheme` — which
// made the timeline render dark in OS dark mode.
import '@bryntum/scheduler/stockholm-light.css';
import '@/styles/bryntum-evo-theme.css';
import { useBryntumTheme } from '@/contexts/BryntumThemeContext';
import { useDataMode } from '@/contexts/DataModeContext';

import { useScheduler, getDefaultStatusColor } from '@/contexts/SchedulerContext';
import { getCancelledFill, getCancelledOutlineColor, isCancelledBooking } from '@/lib/cancelled-booking-style';
import { isDemandBooking } from '@/lib/demand-booking';
import { Resource, Booking, Absence, BankHoliday, CalendarView, FieldConfiguration, BookingType, StatusConfig, SchedulerGroup, WorkingHoursProfile, RecurrenceSettings } from '@/types';
import type { BookingTypeUsageDto } from '@/services/api';
import { recurrenceSettingsApi } from '@/services/api';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, isValid } from 'date-fns';
import { convertTimezone, getTimezoneOffset } from '@/utils/timezone-utils';
import { escapeHtml } from '@/utils/escape-html';
import { getDynamicTooltipFields, getTooltipFieldValue, getTooltipFieldLabel, isFieldOnTooltip as isFieldOnTooltipShared, isColourField } from '@/utils/tooltip-fields';
import { partitionConflictBookings } from '@/utils/resolve-conflict-selection';
import { bookingsFromSelectedEvents, bulkActionTargets, type SelectedEventLike } from '@/lib/bookingSelection';
import { calculateDailyUtilization, calculatePeriodUtilization, computeCappedHourlyTotal, getWorkingHoursForDay as getProfileWorkingHoursForDay } from '@/utils/heatmap-utils';
import { calculateHeatmapTickUtilisation } from '@/utils/heatmap-tick-utilisation';
import { DEFAULT_UTILIZATION_TIERS } from '@/utils/utilization-labels';
import { getUtilizationColor, getUtilizationLabel } from '@/utils/utilization-labels';
import { getBookingColor } from '@/hooks/use-booking-color';
import { getRelativeLuminance } from '@/lib/color-utils';
import { TIMEZONES, getTimezoneAbbreviation } from '@/lib/timezones';
import { toast } from 'sonner';

// Generate unique ID with timestamp and random component to avoid conflicts in React Strict Mode
const generateUniqueId = () => `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

// Grouping mode type for timeline view
export type TimelineGroupingMode = 'resource' | 'project-resource' | 'role-resource' | 'customer';



interface BryntumSchedulerTimelineProps {
  selectedDate: Date;
  calendarView: CalendarView;
  zoomLevel: number;
  selectedTimezone?: string;
  resources: Resource[];
  bookings: Booking[];
  /** Ids of recurring occurrences being deleted; rendered greyed-out and locked. */
  lockedBookingIds?: Set<string>;
  resourceTimeRanges?: Array<{
    id: string;
    resourceId: string;
    name: string;
    startDate: Date;
    endDate: Date;
    timeRangeColor?: string;
    cls?: string;
    style?: string;
  }>;
  timeRanges?: Array<{
    id: string;
    name: string;
    startDate: Date;
    endDate: Date;
    cls?: string;
  }>;
  groupingMode?: TimelineGroupingMode;
  schedulerGroups?: SchedulerGroup[];
  statusConfigs?: StatusConfig[];
  isHeatmapMode?: boolean;
  absences?: Absence[];
  bankHolidays?: BankHoliday[];
  searchQuery?: string;
  enableTimezones?: boolean;
  onEditBooking?: (booking: Booking, contextResourceId?: string) => void;
  onResolveConflict?: (bookings: Booking[]) => void;
  onDeleteBooking?: (booking: Booking) => void;
  /** Delete every booking of a Ctrl/Cmd-click multi-selection in one go. */
  onDeleteBookings?: (bookings: Booking[]) => void;
  onDuplicateBooking?: (booking: Booking) => void;
  onMoveBooking?: (booking: Booking) => void;
  onStatusChange?: (booking: Booking, newStatus: string, statusColor: string) => void;
  /** Change status for every booking of a Ctrl/Cmd-click multi-selection. */
  onStatusChangeBookings?: (bookings: Booking[], newStatus: string, statusColor: string) => void;
  onCopyBooking?: (booking: Booking) => void;
  onSlotClick?: (resource: Resource, date: Date, startTime: { hour: number; minute: number }, bookingData?: Partial<Booking>) => void;
  onDragSelect?: (
    resource: Resource,
    date: Date,
    startTime: { hour: number; minute: number },
    endTime: { hour: number; minute: number },
    bookingData?: Partial<Booking>,
    endDate?: Date
  ) => void;
  onBookingDragEnd?: (
    booking: Booking,
    originalResource: Resource,
    newResourceId: string,
    newStartTime: Date,
    newEndTime: Date,
    isResize?: boolean
  ) => void;
  onAddBooking?: (resource: Resource, date: Date) => void;
  onQuickAddBooking?: (resource: Resource, date: Date) => void;
  onPasteBooking?: (resource: Resource, date: Date, startTime: { hour: number; minute: number }, bookingData?: Booking) => void;
  copiedBooking?: Booking | null;
  /** What-if scenario handed off from the heatmap — drives ghost tile rendering */
  whatIfScenario?: import('@/types').WhatIfScenario | null;
  /** Booking type usage permissions for the logged-in user — used to restrict
   *  context-menu actions and drag/resize on bookings the user cannot edit. */
  bookingTypeUsages?: BookingTypeUsageDto[] | null;
  /** Role permission: whether the user may create bookings. When false the
   *  Add/Quick Add/Paste context-menu items are hidden. Defaults to true. */
  canCreateBooking?: boolean;
  /** Whether quick add is enabled. When false (Quick Add Default set to
   *  "None") the Quick Add Booking context-menu item is hidden. Defaults to true. */
  quickAddEnabled?: boolean;
  /** Role permission: whether the user may edit bookings. When false the event
   *  context menu is reduced to View only. Defaults to true. */
  canEditBooking?: boolean;
  /** Role permission: whether the user may delete bookings. When false the
   *  Delete Booking context-menu item is hidden. Defaults to true. */
  canDeleteBooking?: boolean;
  /** Role permission: whether the user may override read-only booking types.
   *  When true, bookings of a read-only booking type are still editable — the
   *  event context menu shows "Edit Booking" (not "View Booking") and
   *  drag/resize are allowed — matching the backend override behaviour in
   *  BookingsController.Update. Defaults to true. */
  canOverrideReadOnly?: boolean;
}

export interface BryntumSchedulerTimelineHandle {
  revertEvent: (bookingId: string, originalResourceId: string, originalStartTime: Date, originalEndTime: Date) => void;
  forceStoreRefresh: () => void;
  cleanupPhantomEvents: () => void;
}

interface BryntumResource {
  id: string;
  name: string;
  role?: string;
  department?: string;
  imageUrl?: string;
}

interface BryntumEvent {
  id: string;
  resourceId: string;
  name: string;
  startDate: Date;
  endDate: Date;
  eventColor?: string;
  cls?: string;
  originalBooking?: Booking;
  isHeatmapEvent?: boolean;
  isWhatIfGhost?: boolean;
  isPlanPreview?: boolean;
  isAbsenceEvent?: boolean;
  absenceId?: string;
  eventStyle?: string;
  heatmapData?: {
    bookedHours: number;
    availableHours: number;
    utilization: number;
    band: string;
    bandColor: string;
    bookings: Booking[];
    confirmedHours: number;
    plannedHours: number;
    absenceHours: number;
    absenceReasons: string[];
    hasHardConflict: boolean;
    weeklyAvgUtilization: number | null;
    /** True when ALL bookings in this cell are non-confirmed (planned/tentative) */
    isPlannedOnly: boolean;
    /** True when the cell has BOTH confirmed and planned bookings */
    isMixed: boolean;
    /**
     * Ratio of planned hours to total booked hours (0–1).
     * 0 = fully confirmed, 1 = fully planned, 0.6 = 60% planned.
     */
    plannedRatio: number;
    /** True when the resource's customWorkingProfile is being used */
    isCustomProfile: boolean;
    /** Working hours for this cell's start day from the active profile */
    dailyHours: number;
  };
}

// Show a context menu at the pointer, flipping it above/left of the cursor when
// it would overflow the viewport. Without this, a menu opened near the bottom of
// the screen is constrained to the remaining space and becomes scrollable instead
// of repositioning above the click point.
function showMenuAtPointer(menu: Menu, clientX: number, clientY: number): void {
  const margin = 4;

  // Render once near the top-left first: with full space below/right, Bryntum
  // lays the menu out at its natural size instead of constraining it into a
  // scrollable box. Both showBy calls run synchronously in this task, so the
  // browser only paints the final position — the first placement is never seen.
  menu.showBy([margin, margin]);

  const el = menu.element as HTMLElement | undefined;
  if (!el) {
    menu.showBy([clientX, clientY]);
    return;
  }

  const rect = el.getBoundingClientRect();
  const menuHeight = rect.height;
  const menuWidth = rect.width;

  // Flip above/left of the cursor when the menu would overflow the viewport.
  const y = clientY + menuHeight > window.innerHeight - margin
    ? Math.max(margin, clientY - menuHeight)
    : clientY;
  const x = clientX + menuWidth > window.innerWidth - margin
    ? Math.max(margin, clientX - menuWidth)
    : clientX;

  menu.showBy([x, y]);
}

export const BryntumSchedulerTimeline = forwardRef<BryntumSchedulerTimelineHandle, BryntumSchedulerTimelineProps>(({
  selectedDate,
  calendarView,
  zoomLevel,
  selectedTimezone = 'UTC',
  resources,
  bookings,
  lockedBookingIds,
  resourceTimeRanges = [],
  timeRanges = [],
  groupingMode = 'resource',
  schedulerGroups = [],
  statusConfigs = [],
  isHeatmapMode = false,
  absences = [],
  bankHolidays = [],
  searchQuery = '',
  enableTimezones = false,
  onEditBooking,
  onResolveConflict,
  onDeleteBooking,
  onDeleteBookings,
  onMoveBooking,
  onStatusChange,
  onStatusChangeBookings,
  onCopyBooking,
  onSlotClick,
  onDragSelect,
  onBookingDragEnd,
  onAddBooking,
  onQuickAddBooking,
  onPasteBooking,
  copiedBooking,
  whatIfScenario,
  bookingTypeUsages,
  canCreateBooking = true,
  quickAddEnabled = true,
  canEditBooking = true,
  canDeleteBooking = true,
  canOverrideReadOnly = true,
}, ref) => {
  const { getScreenMode } = useDataMode();
  const currentMode = getScreenMode('calendar');

  // SAFETY CHECK: Ensure selectedDate is always valid
  const safeSelectedDate = useMemo(() => {
    if (selectedDate instanceof Date && !isNaN(selectedDate.getTime())) {
      return selectedDate;
    }
    console.warn('Invalid selectedDate received, using current date:', selectedDate);
    return new Date();
  }, [selectedDate]);

  const schedulerRef = useRef<BryntumScheduler>(null);
  const initialDatesRef = useRef({ startDate: safeSelectedDate, endDate: safeSelectedDate });
  // Track whether the initial computed dates have been applied to the ref.
  // After the first valid startDate/endDate are set, we stop updating the ref
  // so the BryntumScheduler wrapper never sees date prop changes. All subsequent
  // date updates are handled imperatively via setTimeSpan() in effects, which
  // prevents the "Invalid start/end dates" error caused by the wrapper applying
  // startDate and endDate as individual prop changes during large date jumps
  // (e.g. clicking "Today" after navigating weeks into the future).
  const hasSetInitialDates = useRef(false);
  // Populated on first render (after viewPreset useMemo) — see the assignment
  // below the viewPreset declaration.  Typed as any to avoid a forward-ref
  // initialisation order problem.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const initialViewPresetRef = useRef<any>(null);
  const [allGroupsExpanded, setAllGroupsExpanded] = useState(true);

  // SVG icons for the global expand/collapse button (show available ACTION)
  // Minus-in-square = "Collapse all" = shown when all groups are currently expanded
  // Plus-in-square = "Expand all" = shown when all groups are currently collapsed
  const COLLAPSE_ALL_ICON = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="8" y1="12" x2="16" y2="12"/></svg>`;
  const EXPAND_ALL_ICON = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>`;

  /** Update the global expand/collapse button DOM to reflect the available action */
  const updateExpandCollapseButton = useCallback((expanded: boolean) => {
    const button = document.getElementById('expand-collapse-all-btn');
    if (button) {
      button.innerHTML = expanded ? COLLAPSE_ALL_ICON : EXPAND_ALL_ICON;
      button.title = expanded ? 'Collapse all' : 'Expand all';
    }
  }, []);

  // Ref mirrors allGroupsExpanded so the sync callback never has a stale closure
  const allGroupsExpandedRef = useRef(allGroupsExpanded);
  allGroupsExpandedRef.current = allGroupsExpanded;

  /** Check whether all parent (group) nodes share the same expanded/collapsed state and sync the button */
  const syncGlobalExpandState = useCallback(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler || scheduler.isDestroyed) return;

    const store = scheduler.resourceStore as unknown as { allRecords?: Array<{ isParent?: boolean; expanded?: boolean }> };
    const parents = (store.allRecords ?? []).filter(r => r.isParent);
    if (parents.length === 0) return;

    const allExpanded = parents.every(r => r.expanded);
    const allCollapsed = parents.every(r => !r.expanded);

    if (allExpanded && !allGroupsExpandedRef.current) {
      setAllGroupsExpanded(true);
      updateExpandCollapseButton(true);
    } else if (allCollapsed && allGroupsExpandedRef.current) {
      setAllGroupsExpanded(false);
      updateExpandCollapseButton(false);
    }
  }, [updateExpandCollapseButton]);

  // Build a Set of booking-type IDs the user has full-access to for O(1) lookups.
  const fullAccessBookingTypeIds = useMemo(() => {
    if (!bookingTypeUsages) return null; // still loading — treat all as editable
    const ids = new Set<string>();
    for (const u of bookingTypeUsages) {
      if (u.isFullAccess) ids.add(String(u.bookingTypeId));
    }
    return ids;
  }, [bookingTypeUsages]);

  /** Returns true when the logged-in user may NOT edit the given booking. */
  const isBookingRestricted = useCallback((booking: Booking): boolean => {
    // Users with the "Override Read-Only Bookings" permission bypass the
    // per-booking-type read-only restriction, matching backend behaviour
    // (see canOverrideReadOnly in BookingsController.Update) and the booking
    // modal (isBookingReadOnly in ComprehensiveBookingModal).
    if (canOverrideReadOnly) return false;
    if (!fullAccessBookingTypeIds) return false; // still loading — allow
    if (!booking.bookingTypeId) return false; // no type — allow
    return !fullAccessBookingTypeIds.has(String(booking.bookingTypeId));
  }, [fullAccessBookingTypeIds, canOverrideReadOnly]);

  // Generate a stable unique ID for the scheduler instance (does not change with groupingMode).
  // Switching grouping modes updates the resource/event stores in-place via a dedicated effect.
  const schedulerIdRef = useRef<string | undefined>(undefined);
  if (!schedulerIdRef.current) {
    schedulerIdRef.current = `bryntum-scheduler-${generateUniqueId()}`;
  }
  const schedulerId = schedulerIdRef.current;

  // NOTE: the Bryntum remount key (schedulerKey) is computed further below,
  // after the bryntumEvents useMemo, because it also has to observe when the
  // navigated span's asynchronously-fetched content first becomes available.
  // See the "Scheduler remount key" block just below the bryntumEvents memo.

  // Stable refs for all utilisation inputs so the renderer closure
  // (captured once at mount) always reads fresh data.
  const bookingsRef = useRef(bookings);
  bookingsRef.current = bookings;

  // Public holidays are excluded from utilisation capacity, so the renderer
  // needs fresh access to them via a ref.
  const bankHolidaysRef = useRef(bankHolidays);
  bankHolidaysRef.current = bankHolidays;

  // Absences also reduce utilisation capacity, so the renderer reads them
  // from a ref for the same reason.
  const absencesRef = useRef(absences);
  absencesRef.current = absences;

  // Cache of recurrence settings fetched from the API, keyed by recurrenceGroupId.
  // Used by the tooltip to show "Occurs every ..." for recurring bookings whose
  // parent (with inline recurrence data) is outside the current date-range view.
  const recurrenceSettingsCacheRef = useRef<Map<string, RecurrenceSettings>>(new Map());

  // Bumped by SchedulerContext whenever a series' recurrence settings change (e.g. the
  // repeat end date is shortened after removing recurrence on an "all future" edit) so
  // the cache below refetches and tooltips show the new end date without a reload.
  const { recurrenceSettingsVersion } = useScheduler();
  const lastRecurrenceVersionRef = useRef(recurrenceSettingsVersion);
  const lastRecurrenceModeRef = useRef(currentMode);

  // Fetch recurrence settings from the API for recurring bookings whose
  // inline recurrence data is missing (child instances whose parent is
  // outside the current date-range).
  useEffect(() => {
    if (lastRecurrenceModeRef.current !== currentMode) {
      lastRecurrenceModeRef.current = currentMode;
      recurrenceSettingsCacheRef.current.clear();
    }

    // A recurrence-settings change invalidates every cached entry; drop them so the
    // affected groups are refetched with their new end date.
    if (lastRecurrenceVersionRef.current !== recurrenceSettingsVersion) {
      lastRecurrenceVersionRef.current = recurrenceSettingsVersion;
      recurrenceSettingsCacheRef.current.clear();
    }

    const groupIds = [...new Set(
      bookings
        .filter((b: Booking) => b.recurrenceGroupId && !b.recurrence)
        .map((b: Booking) => b.recurrenceGroupId as string)
    )].filter(id => !recurrenceSettingsCacheRef.current.has(id));

    if (groupIds.length === 0) return;

    if (currentMode === 'demo') {
      for (const groupId of groupIds) {
        const recurrence = bookings.find(
          (booking: Booking) =>
            booking.recurrenceGroupId === groupId
            && booking.recurrence
            && booking.recurrence.pattern !== 'none'
        )?.recurrence;
        if (recurrence) {
          recurrenceSettingsCacheRef.current.set(groupId, recurrence);
        }
      }
      return;
    }

    let cancelled = false;
    Promise.allSettled(
      groupIds.map(gid =>
        recurrenceSettingsApi.getByRecurrenceGroupId(gid)
          .then(rs => {
            if (cancelled || !rs) return;
            // Map weekly/monthly-specific fields from the API response
            let daysOfWeek: string[] | undefined;
            let monthlyRecurrenceType: RecurrenceSettings['monthlyRecurrenceType'];
            let dayOfMonth: number | undefined;
            let relativeWeekday: RecurrenceSettings['relativeWeekday'];

            if (rs.pattern === 'weekly' && rs.daysOfWeek) {
              try {
                const parsed = JSON.parse(rs.daysOfWeek);
                daysOfWeek = Array.isArray(parsed) ? parsed : [rs.daysOfWeek];
              } catch {
                daysOfWeek = [rs.daysOfWeek];
              }
            }

            if (rs.pattern === 'monthly') {
              if (rs.weekOfMonth && rs.daysOfWeek) {
                monthlyRecurrenceType = 'relative-weekday';
                const occurrenceMap: Record<number, 'first' | 'second' | 'third' | 'fourth' | 'last'> = {
                  1: 'first', 2: 'second', 3: 'third', 4: 'fourth', 5: 'last',
                };
                relativeWeekday = {
                  occurrence: occurrenceMap[rs.weekOfMonth] || 'first',
                  weekday: rs.daysOfWeek,
                };
              } else if (rs.dayOfMonth) {
                monthlyRecurrenceType = 'day-of-month';
                dayOfMonth = rs.dayOfMonth;
              }
            }

            recurrenceSettingsCacheRef.current.set(gid, {
              pattern: rs.pattern as RecurrenceSettings['pattern'],
              interval: rs.interval,
              endType: rs.endType as RecurrenceSettings['endType'],
              occurrences: rs.endAfterOccurrences,
              endDate: rs.endDate,
              daysOfWeek,
              monthlyRecurrenceType,
              dayOfMonth,
              relativeWeekday,
            });
          })
          .catch(() => { /* API unavailable (e.g. demo mode) — skip */ })
      )
    );
    return () => { cancelled = true; };
  }, [bookings, currentMode, recurrenceSettingsVersion]);

  const resourcesRef = useRef(resources);
  resourcesRef.current = resources;

  // startDateRef / endDateRef are initialised with safeSelectedDate and
  // updated with the real computed startDate/endDate after the useMemo below.
  const startDateRef = useRef<Date>(safeSelectedDate);
  const endDateRef = useRef<Date>(safeSelectedDate);

  // globalWorkingProfileRef is initialised as null and updated after
  // `settings` is destructured from useScheduler() further below.
  const globalWorkingProfileRef = useRef<WorkingHoursProfile | null>(null);

  // Stable ref for settings so the column renderer (captured once at mount)
  // always reads fresh utilisation tier colours and labels.
  const settingsRef = useRef<typeof settings | null>(null);

  // Lookup map of resourceId → Resource for O(1) access inside the column
  // renderer (which is captured once at mount via a closure over this ref).
  const resourceByIdRef = useRef<Map<string, Resource>>(new Map());

  // Track previously applied viewPreset and time span so the store-sync
  // only reassigns them when they actually change.  Unnecessary reassignment
  // of viewPreset causes Bryntum to reconfigure the time axis which can
  // trigger a full event-layout recalculation.
  const lastSyncedViewPresetRef = useRef<unknown>(null);
  const lastSyncedStartRef = useRef<Date | null>(null);
  const lastSyncedEndRef = useRef<Date | null>(null);
  // Reference to the bryntumResources array last applied to the resourceStore.
  // The store-sync only rebuilds the resource store when this reference changes
  // (bryntumResources is memoised and stays referentially stable on pure date
  // navigation), so plain next/prev/date-picker passes no longer detach every
  // event from its resource.
  const prevAppliedResourcesRef = useRef<unknown>(null);
  // Set by the setTimeSpan owner (the useLayoutEffect below) whenever it
  // advances the axis, and consumed once by the store-sync effect so it knows
  // the visible span moved this pass and can force an event re-layout.
  const timeSpanAdvancedRef = useRef(false);

  // Cleanup effect to destroy scheduler instance on unmount
  // Using useLayoutEffect for synchronous cleanup before next render
  useLayoutEffect(() => {
    // Check if an instance with this ID already exists and destroy it
    const existingWidget = (window as any).bryntum?.query?.(schedulerId);
    if (existingWidget && !existingWidget.isDestroyed) {
      console.warn(`Destroying existing scheduler with ID: ${schedulerId}`);
      existingWidget.destroy();
    }

    return () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (scheduler && !scheduler.isDestroyed) {
        scheduler.destroy();
      }
    };
  }, [schedulerId]);



  const { settings, projects, projectDetails, clients, costCentres } = useScheduler();
  const { isThemeLoaded } = useBryntumTheme();

  // Drag/drop is always enabled — the Confirm Drag/Drop preference only controls
  // whether a confirmation dialog is shown after the drop, not whether dragging
  // is allowed at all.
  const isDragEnabled = true;

  // Quarterly (week cells) and Yearly (month cells) views use multi-day cells.
  // Drag/drop and resize are disabled here because the time resolution is too
  // coarse for meaningful booking manipulation. Day-level checks (non-working
  // day / non-business hour) are also skipped, since a single cell spans many
  // days and would otherwise always match a weekend and block create/menu.
  const isCoarseGranularityView = calendarView === 'quarter' || calendarView === 'year';

  // Helper function to check if a specific day is a non-working day for a resource
  const isNonWorkingDay = useCallback((resource: Resource, date: Date): boolean => {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const dayKey = dayNames[date.getDay()];

    // Check if resource has custom working profile enabled
    if (resource.useCustomWorkingProfile && resource.customWorkingProfile) {
      const hours = resource.customWorkingProfile[dayKey] || 0;
      return hours === 0;
    }

    // Otherwise use global working profile
    const hours = settings.globalWorkingProfile?.[dayKey] || 0;
    return hours === 0;
  }, [settings.globalWorkingProfile]);

  // Helper function to check if a specific time falls within non-business hours (Day view only)
  const isNonBusinessHour = useCallback((date: Date): boolean => {
    if (calendarView !== 'day') return false;

    const bizStartRaw = settings.businessHoursStart ?? 9;
    const bizEndRaw = settings.businessHoursEnd ?? 17;

    // Convert date's time to a decimal hour for comparison (e.g. 10:30 → 10.5)
    const timeDecimal = date.getHours() + date.getMinutes() / 60;

    return timeDecimal < bizStartRaw || timeDecimal >= bizEndRaw;
  }, [calendarView, settings.businessHoursStart, settings.businessHoursEnd]);

  const toggleAllGroups = useCallback(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) {
      return;
    }

    const shouldExpand = !allGroupsExpanded;

    // Access the tree feature directly
    const treeFeature = (scheduler as unknown as { features?: { tree?: { collapseAll?: () => void; expandAll?: () => void } } }).features?.tree;

    if (treeFeature) {
      if (shouldExpand && treeFeature.expandAll) {
        treeFeature.expandAll();
      } else if (!shouldExpand && treeFeature.collapseAll) {
        treeFeature.collapseAll();
      }
    }

    // Update button icon to reflect the new state
    updateExpandCollapseButton(shouldExpand);

    setAllGroupsExpanded(shouldExpand);
  }, [allGroupsExpanded, updateExpandCollapseButton]);


  const cleanupPhantomEvents = useCallback(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    const eventStore = scheduler.eventStore;
    const bookingIds = new Set(bookings.map((b: Booking) => String(b.id)));
    const toRemove = (eventStore as any).allRecords?.filter(
      (rec: { id?: string; isPhantom?: boolean; data?: any; originalData?: any }) => {
        if (rec.isPhantom) return true;
        const eventId = String(rec.id || '');
        // Never remove heatmap events — they are keyed by tick index, not booking IDs,
        // and are managed entirely by the heatmapEvents useMemo + the store-sync effect.
        if ((rec as any).data?.isHeatmapEvent || (rec as any).isHeatmapEvent || eventId.startsWith('heatmap-')) return false;
        // Never remove what-if ghost events — they are managed by the whatIfScenario useEffect
        if (eventId.startsWith('__whatif_ghost__') || (rec as any).data?.isWhatIfGhost) return false;
        // Never remove absence events — they are managed by the bryntumEvents useMemo
        if (eventId.startsWith('absence-evt-') || (rec as any).data?.isAbsenceEvent) return false;
        // Plan-preview ghost events: only keep them while they are in the active bookings set
        if (eventId.startsWith('plan-preview-') || (rec as any).data?.isPlanPreview) return !bookingIds.has(eventId);
        const hasOriginal = !!(rec as any).data?.originalBooking || !!(rec as any).originalData?.originalBooking;
        // Strip multi-resource suffix to check against base booking IDs
        const baseEventId = eventId.includes('__mr__') ? eventId.split('__mr__')[0] : eventId;
        // Remove any event that isn't mapped from our data (no originalBooking)
        // OR whose id doesn't exist in our current bookings set
        return !hasOriginal || (baseEventId && !bookingIds.has(baseEventId));
      }
    ) || [];
    if (toRemove.length > 0) {
      eventStore.remove(toRemove);
    }
  }, [bookings]);

  // Ref to bryntumEvents — initialised after bryntumEvents useMemo, updated
  // every render so the imperative handle always reads the latest value.
  const bryntumEventsRef = useRef<BryntumEvent[]>([]);

  useImperativeHandle(ref, () => ({
    revertEvent: (bookingId: string, originalResourceId: string, originalStartTime: Date, originalEndTime: Date) => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler || scheduler.isDestroyed) return;

      const eventStore = scheduler.eventStore;
      const eventRecord = eventStore.getById(String(bookingId));

      if (eventRecord) {

        eventRecord.beginBatch();
        (eventRecord as any).startDate = originalStartTime;
        (eventRecord as any).endDate = originalEndTime;
        (eventRecord as any).resourceId = originalResourceId;
        eventRecord.endBatch();
      }
    },
    forceStoreRefresh: () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler || scheduler.isDestroyed) return;

      // Re-sync every event in Bryntum's store against the React-derived
      // bryntumEvents array.  Since React bookings have NOT been mutated
      // (the user discarded), the diff will reset any drag/resize-modified
      // events back to their original positions.
      const eventsData = bryntumEventsRef.current;
      const newEventsById = new Map<string, (typeof eventsData)[number]>();
      eventsData.forEach(e => newEventsById.set(String(e.id), e));

      scheduler.suspendRefresh();
      try {
        (scheduler.eventStore.getRange() as any[]).forEach((existing: any) => {
          const origData = newEventsById.get(String(existing.id));
          if (origData) {
            const d = existing.data ?? {};
            const startChanged = String(d.startDate) !== String(origData.startDate);
            const endChanged = String(d.endDate) !== String(origData.endDate);
            const resChanged = String(d.resourceId ?? '') !== String(origData.resourceId ?? '');
            if (startChanged || endChanged || resChanged) {
              existing.set(origData);
            }
          }
        });
      } finally {
        scheduler.resumeRefresh(true);
      }
    },
    cleanupPhantomEvents,
  }), [cleanupPhantomEvents]);

  // ── What-if ghost tile ────────────────────────────────────────────────────
  // Ghost tile injection is handled directly inside the main store-sync effect
  // below so that it is always atomic with the removeAll/add cycle and fires
  // exactly when the Bryntum scheduler is confirmed to be ready.
  // These constants are used by both the sync effect and cleanupPhantomEvents.
  const GHOST_EVENT_ID_PREFIX = '__whatif_ghost__';
  const MOVING_CLS = 'whatif-moving';

  // Helper: extract first-letter-of-first-name + first-letter-of-last-name
  const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 0) return '?';
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
    return (first + last).toUpperCase();
  };

  // Helper function to generate avatar URL - returns photoUrl or empty string
  // so the renderer can decide whether to show an <img> or the teal-gradient initials fallback
  const getAvatarUrl = (name: string, photoUrl?: string): string => {
    if (photoUrl && photoUrl.trim() !== '' && photoUrl !== 'null' && photoUrl !== 'undefined') return photoUrl;
    return '';
  };

  // Create tree structure with groups as parent nodes and resources as children
  // Uses the resources prop which is already filtered by ResourceCalendar
  const bryntumResources = useMemo(() => {
    // Helper function to check if a resource/project/booking matches the search query
    const matchesSearch = (searchFields: string[]): boolean => {
      if (!searchQuery.trim()) return true; // No search query, show all

      const query = searchQuery.toLowerCase();
      return searchFields.some(field =>
        field && field.toLowerCase().includes(query)
      );
    };

    // Compute max overlapping bookings per resource to dynamically size rows
    const BASE_ROW_HEIGHT = 50;
    const EXTRA_HEIGHT_PER_BOOKING = 25;
    const MAX_BOOKINGS_BEFORE_GROW = 2;

    const maxOverlapByResource = new Map<string | number, number>();
    const resourceBookingsMap = new Map<string | number, Array<{ start: number; end: number }>>();
    bookings.forEach(b => {
      const rid = b.resourceId;
      if (!rid) return;
      if (!resourceBookingsMap.has(rid)) resourceBookingsMap.set(rid, []);
      resourceBookingsMap.get(rid)!.push({
        start: new Date(b.startTime).getTime(),
        end: new Date(b.endTime).getTime(),
      });
    });
    resourceBookingsMap.forEach((intervals, rid) => {
      const points: Array<{ time: number; type: number }> = [];
      intervals.forEach(iv => {
        points.push({ time: iv.start, type: 1 });
        points.push({ time: iv.end, type: -1 });
      });
      points.sort((a, b) => a.time - b.time || a.type - b.type);
      let concurrent = 0;
      let maxConcurrent = 0;
      points.forEach(p => {
        concurrent += p.type;
        if (concurrent > maxConcurrent) maxConcurrent = concurrent;
      });
      maxOverlapByResource.set(rid, maxConcurrent);
    });

    const getDynamicRowHeight = (resourceId: string | number): number | undefined => {
      // In heatmap mode each row has exactly one event bar per tick — no stacking
      // occurs, so the row should always use the base height.
      if (isHeatmapMode) return undefined;
      const maxOverlap = maxOverlapByResource.get(resourceId) ?? 0;
      return maxOverlap > MAX_BOOKINGS_BEFORE_GROW
        ? BASE_ROW_HEIGHT + (maxOverlap - MAX_BOOKINGS_BEFORE_GROW) * EXTRA_HEIGHT_PER_BOOKING
        : undefined;
    };

    // Project & Resource grouping mode
    if (groupingMode === 'project-resource') {

      // Build map of projects to resources that have bookings for those projects
      const projectResourceMap = new Map<string, Set<string>>();

      bookings.forEach(booking => {

        if (booking.projectId && booking.projectId !== '') {
          if (!projectResourceMap.has(booking.projectId)) {
            projectResourceMap.set(booking.projectId, new Set());
          }
          projectResourceMap.get(booking.projectId)!.add(booking.resourceId ?? "");
        }
      });

      // Create hierarchical resource structure: Projects > Resources
      const hierarchicalResources: Array<{
        id: string;
        name: string;
        expanded: boolean;
        icon: boolean;
        iconCls: string;
        isProjectGroup: boolean;
        projectColor?: string;
        projectCode?: string;
        children: Array<{
          id: string;
          originalResourceId: string;
          projectId: string;
          name: string;
          role?: string;
          department: string;
          schedulerGroup: string;
          imageUrl: string;
          icon: boolean;
          iconCls: string;
        }>;
      }> = [];

      projectDetails.forEach(project => {
        const resourcesForProject = projectResourceMap.get(String(project.id));

        // Only include projects that have bookings with resources
        if (resourcesForProject && resourcesForProject.size > 0) {
          const projectNode = {
            id: `project-${project.id}`,
            name: project.name,
            expanded: true,
            icon: false,
            iconCls: '',
            isProjectGroup: true,
            projectColor: project.color,
            projectCode: project.billingCode,
            children: [] as Array<{
              id: string;
              originalResourceId: string;
              projectId: string;
              name: string;
              role?: string;
              department: string;
              schedulerGroup: string;
              imageUrl: string;
              icon: boolean;
              iconCls: string;
            }>,
          };

          // Add resources as children under this project
          resourcesForProject.forEach(resourceId => {
            const resource = resources.find(r => String(r.id) === String(resourceId));
            if (resource) {
              // Get bookings for this resource and project to search in their fields
              const resourceProjectBookings = bookings.filter(
                b => String(b.resourceId) === String(resource.id) && String(b.projectId) === String(project.id)
              );

              // Collect all searchable fields for this resource-project combination
              const searchFields = [
                resource.name,
                resource.schedulerGroup || '',
                resource.department || '',
                project.billingCode || '',
                project.name,
                ...resourceProjectBookings.flatMap(booking => {
                  const costCentre = costCentres.find(cc => String(cc.id) === String(booking.costCentreId));
                  return [
                    costCentre?.code || '',
                    costCentre?.name || '',
                    booking.description || ''
                  ];
                })
              ];

              // Only add resource if it matches the search query
              if (matchesSearch(searchFields)) {
                const dynamicHeight = getDynamicRowHeight(resource.id);
                projectNode.children.push({
                  id: `${project.id}-${resource.id}`, // Unique ID for this project-resource combination
                  originalResourceId: resource.id, // Store original resource ID for booking operations
                  projectId: String(project.id), // Store project ID for filtering
                  name: resource.name,
                  role: resource.role,
                  department: resource.department || 'Unassigned',
                  schedulerGroup: resource.schedulerGroup || 'Unassigned',
                  imageUrl: getAvatarUrl(resource.name, resource.photoUrl),
                  icon: false,
                  iconCls: '',
                  ...(dynamicHeight !== undefined && { rowHeight: dynamicHeight }),
                });
              }
            }
          });

          // Sort children (resources) alphabetically by name in ascending order
          projectNode.children.sort((a, b) => (a.name || '').localeCompare((b as any).name || ''));

          // Only add project if it has children after filtering OR if the project itself matches the search
          const projectSearchFields = [project.name, project.billingCode || ''];
          if (projectNode.children.length > 0 || matchesSearch(projectSearchFields)) {
            hierarchicalResources.push(projectNode);
          }
        }
      });

      // Sort projects alphabetically by name in ascending order
      hierarchicalResources.sort((a, b) => (a.name || '').localeCompare((b as any).name || ''));

      return hierarchicalResources;
    }

    // Role & Resource grouping mode - groups resources by their role
    if (groupingMode === 'role-resource') {

      // Group resources by role
      const roleResourceMap = new Map<string, Resource[]>();

      resources.forEach(resource => {
        // Special handling for Unallocated resource - use "System" as role
        const roleName = String(resource.id) === 'unallocated' ? 'System' : (resource.role || 'Unassigned');
        if (!roleResourceMap.has(roleName)) {
          roleResourceMap.set(roleName, []);
        }
        roleResourceMap.get(roleName)!.push(resource);
      });

      // Create hierarchical resource structure: Roles > Resources
      const hierarchicalResources: Array<{
        id: string;
        name: string;
        expanded: boolean;
        icon: boolean;
        iconCls: string;
        isRoleGroup: boolean;
        children: Array<{
          id: string;
          originalResourceId: string;
          roleId: string;
          name: string;
          role?: string;
          department: string;
          schedulerGroup: string;
          imageUrl: string;
          icon: boolean;
          iconCls: string;
        }>;
      }> = [];

      // Sort roles alphabetically, but put "Unassigned" and "System" at the end
      const sortedRoles = Array.from(roleResourceMap.keys()).sort((a, b) => {
        if (a === 'System') return 1; // System at the very end
        if (b === 'System') return -1;
        if (a === 'Unassigned') return 1;
        if (b === 'Unassigned') return -1;
        return a.localeCompare(b);
      });

      sortedRoles.forEach(roleName => {
        const roleResources = roleResourceMap.get(roleName)!;

        const roleNode = {
          id: `role-${roleName}`,
          name: roleName,
          // Collapse the "System" role group by default
          expanded: roleName !== 'System',
          icon: false,
          iconCls: '',
          isRoleGroup: true,
          // Sort children (resources) alphabetically by name in ascending order
          children: roleResources
            .sort((a, b) => (a.name || '').localeCompare((b as any).name || ''))
            .filter(resource => {
              // Get bookings for this resource to search in their fields
              const resourceBookings = bookings.filter(b => String(b.resourceId) === String(resource.id));

              // Collect all searchable fields for this resource
              const searchFields = [
                resource.name,
                resource.schedulerGroup || '',
                resource.department || '',
                roleName,
                ...resourceBookings.flatMap(booking => {
                  const project = projectDetails.find(p => p.id.toString() === booking.projectId);
                  const costCentre = costCentres.find(cc => String(cc.id) === String(booking.costCentreId));
                  return [
                    project?.billingCode || '',
                    project?.name || '',
                    booking.projectName || '',
                    costCentre?.code || '',
                    costCentre?.name || '',
                    booking.description || ''
                  ];
                })
              ];

              return matchesSearch(searchFields);
            })
            .map(resource => {
              const dynamicHeight = getDynamicRowHeight(resource.id);
              return {
                id: `${roleName}-${resource.id}`, // Unique ID for this role-resource combination
                originalResourceId: resource.id, // Store original resource ID for booking operations
                roleId: roleName, // Store role for reference
                name: resource.name,
                role: resource.role,
                department: resource.department || 'Unassigned',
                schedulerGroup: resource.schedulerGroup || 'Unassigned',
                imageUrl: getAvatarUrl(resource.name, resource.photoUrl),
                icon: false,
                iconCls: '',
                ...(dynamicHeight !== undefined && { rowHeight: dynamicHeight }),
              };
            }),
        };

        // Only add role if it has children after filtering OR if the role itself matches the search
        const roleSearchFields = [roleName];
        if (roleNode.children.length > 0 || matchesSearch(roleSearchFields)) {
          hierarchicalResources.push(roleNode);
        }
      });

      return hierarchicalResources;
    }

    // Customer grouping mode - groups resources by the customer name on their bookings
    if (groupingMode === 'customer') {

      // Build a map of customer name -> Set of resource IDs that have bookings for that customer
      const customerResourceMap = new Map<string, Set<string>>();

      bookings.forEach(booking => {
        // Skip unallocated resource
        if (booking.resourceId === 'unallocated') return;

        // Derive customer name: use booking.customer, or look up via projectDetails clientName
        let customerName: string = booking.customer?.trim() || '';
        if (!customerName && booking.projectId) {
          const project = projectDetails.find(p => String(p.id) === String(booking.projectId));
          customerName = project?.clientName?.trim() || '';
        }
        if (!customerName) customerName = 'Unassigned';

        if (!customerResourceMap.has(customerName)) {
          customerResourceMap.set(customerName, new Set());
        }
        customerResourceMap.get(customerName)!.add(String(booking.resourceId));
      });

      // Sort customer names alphabetically, Unassigned at the end
      const sortedCustomers = Array.from(customerResourceMap.keys()).sort((a, b) => {
        if (a === 'Unassigned') return 1;
        if (b === 'Unassigned') return -1;
        return a.localeCompare(b);
      });

      const hierarchicalResources: Array<{
        id: string;
        name: string;
        expanded: boolean;
        icon: boolean;
        iconCls: string;
        isCustomerGroup: boolean;
        children: Array<{
          id: string;
          originalResourceId: string;
          customerId: string;
          name: string;
          role?: string;
          department: string;
          schedulerGroup: string;
          imageUrl: string;
          icon: boolean;
          iconCls: string;
        }>;
      }> = [];

      sortedCustomers.forEach(customerName => {
        const resourceIdsForCustomer = customerResourceMap.get(customerName)!;

        const customerNode = {
          id: `customer-${customerName}`,
          name: customerName,
          expanded: true,
          icon: false,
          iconCls: '',
          isCustomerGroup: true,
          children: [] as Array<{
            id: string;
            originalResourceId: string;
            customerId: string;
            name: string;
            role?: string;
            department: string;
            schedulerGroup: string;
            imageUrl: string;
            icon: boolean;
            iconCls: string;
          }>,
        };

        resourceIdsForCustomer.forEach(resourceId => {
          const resource = resources.find(r => String(r.id) === resourceId);
          if (resource) {
            // Collect searchable fields for this customer-resource combination
            const resourceCustomerBookings = bookings.filter(b => {
              if (String(b.resourceId) !== String(resource.id)) return false;
              let bCustomer: string = b.customer?.trim() || '';
              if (!bCustomer && b.projectId) {
                const project = projectDetails.find(p => String(p.id) === String(b.projectId));
                bCustomer = project?.clientName?.trim() || '';
              }
              if (!bCustomer) bCustomer = 'Unassigned';
              return bCustomer === customerName;
            });

            const searchFields = [
              resource.name,
              resource.schedulerGroup || '',
              resource.department || '',
              customerName,
              ...resourceCustomerBookings.flatMap(booking => {
                const project = projectDetails.find(p => String(p.id) === String(booking.projectId));
                const costCentre = costCentres.find(cc => String(cc.id) === String(booking.costCentreId));
                return [
                  project?.billingCode || '',
                  project?.name || '',
                  booking.projectName || '',
                  costCentre?.code || '',
                  costCentre?.name || '',
                  booking.description || '',
                ];
              }),
            ];

            if (matchesSearch(searchFields)) {
              const dynamicHeight = getDynamicRowHeight(resource.id);
              customerNode.children.push({
                id: `${customerName}-${resource.id}`,
                originalResourceId: resource.id,
                customerId: customerName,
                name: resource.name,
                role: resource.role,
                department: resource.department || 'Unassigned',
                schedulerGroup: resource.schedulerGroup || 'Unassigned',
                imageUrl: getAvatarUrl(resource.name, resource.photoUrl),
                icon: false,
                iconCls: '',
                ...(dynamicHeight !== undefined && { rowHeight: dynamicHeight }),
              });
            }
          }
        });

        // Sort children alphabetically by resource name
        customerNode.children.sort((a, b) => (a.name || '').localeCompare((b as any).name || ''));

        const customerSearchFields = [customerName];
        if (customerNode.children.length > 0 || matchesSearch(customerSearchFields)) {
          hierarchicalResources.push(customerNode);
        }
      });


      return hierarchicalResources;
    }

    // Default Resource grouping mode (by department or scheduler group)
    if (settings.groupingMethod === 'scheduler-group') {
      // Use DB scheduler groups: group resources by schedulerGroupId,
      // only show groups that have resources, put unassigned resources under "Unassigned"
      const groupIdToName = new Map<number, string>();
      schedulerGroups.forEach(g => groupIdToName.set(Number(g.id), g.name));

      const groupedByGroupId: Record<string, Resource[]> = {};

      resources.forEach((resource: Resource) => {
        if (resource.schedulerGroupId != null && groupIdToName.has(resource.schedulerGroupId)) {
          const groupName = groupIdToName.get(resource.schedulerGroupId)!;
          if (!groupedByGroupId[groupName]) {
            groupedByGroupId[groupName] = [];
          }
          groupedByGroupId[groupName].push(resource);
        } else {
          // Resource has no group or group not found — put under "Unassigned"
          if (!groupedByGroupId['Unassigned']) {
            groupedByGroupId['Unassigned'] = [];
          }
          groupedByGroupId['Unassigned'].push(resource);
        }
      });

      const treeData = Object.entries(groupedByGroupId)
        .sort(([a], [b]) => {
          if (a === 'System') return 1;
          if (b === 'System') return -1;
          if (a === 'Unassigned') return 1;
          if (b === 'Unassigned') return -1;
          return a.localeCompare(b);
        })
        .map(([groupName, groupResources]) => {
          const filteredGroupResources = groupResources
            .filter((resource: Resource) => {
              const resourceBookings = bookings.filter(b => String(b.resourceId) === String(resource.id));
              const searchFields = [
                resource.name,
                resource.schedulerGroup || '',
                resource.department || '',
                groupName,
                ...resourceBookings.flatMap(booking => {
                  const project = projectDetails.find(p => String(p.id) === String(booking.projectId));
                  const costCentre = costCentres.find(cc => String(cc.id) === String(booking.costCentreId));
                  return [
                    project?.billingCode || '',
                    project?.name || '',
                    booking.projectName || '',
                    costCentre?.code || '',
                    costCentre?.name || '',
                    booking.description || ''
                  ];
                })
              ];
              return matchesSearch(searchFields);
            });

          return {
            id: `group-${groupName}`,
            name: groupName,
            expanded: groupName !== 'System',
            icon: false,
            iconCls: '',
            children: filteredGroupResources
              .sort((a, b) => (a.name || '').localeCompare((b as any).name || ''))
              .map((resource: Resource) => {
                const dynamicHeight = getDynamicRowHeight(resource.id);
                return {
                  id: resource.id,
                  name: resource.name,
                  role: resource.role,
                  department: resource.department || 'Unassigned',
                  schedulerGroup: resource.schedulerGroup || 'Unassigned',
                  imageUrl: getAvatarUrl(resource.name, resource.photoUrl),
                  icon: false,
                  iconCls: '',
                  ...(dynamicHeight !== undefined && { rowHeight: dynamicHeight }),
                };
              }),
          };
        })
        .filter(group => {
          const groupSearchFields = [group.name];
          return group.children.length > 0 || matchesSearch(groupSearchFields);
        });

      return treeData;
    }

    // Default: group by department
    const groupedResources: Record<string, Resource[]> = {};

    resources.forEach((resource: Resource) => {
      const groupName = resource.department || 'Unassigned';
      if (!groupedResources[groupName]) {
        groupedResources[groupName] = [];
      }
      groupedResources[groupName].push(resource);
    });

    // Create tree structure with sorting
    const treeData = Object.entries(groupedResources)
      // Sort groups alphabetically, but put "System" and "Unassigned" at the end
      .sort(([a], [b]) => {
        if (a === 'System') return 1;
        if (b === 'System') return -1;
        if (a === 'Unassigned') return 1;
        if (b === 'Unassigned') return -1;
        return a.localeCompare(b);
      })
      .map(([groupName, groupResources]) => {
        // Filter resources based on search query
        const filteredGroupResources = groupResources
          .filter((resource: Resource) => {
            // Get bookings for this resource to search in their fields
            const resourceBookings = bookings.filter(b => String(b.resourceId) === String(resource.id));

            // Collect all searchable fields for this resource
            const searchFields = [
              resource.name,
              resource.schedulerGroup || '',
              resource.department || '',
              groupName,
              ...resourceBookings.flatMap(booking => {
                const project = projectDetails.find(p => String(p.id) === String(booking.projectId));
                const costCentre = costCentres.find(cc => String(cc.id) === String(booking.costCentreId));
                return [
                  project?.billingCode || '',
                  project?.name || '',
                  booking.projectName || '',
                  costCentre?.code || '',
                  costCentre?.name || '',
                  booking.description || ''
                ];
              })
            ];

            return matchesSearch(searchFields);
          });

        return {
          id: `group-${groupName}`,
          name: groupName,
          // Collapse the "System" group (containing Unallocated) by default
          expanded: groupName !== 'System',
          icon: false,
          iconCls: '',
          // Sort children (resources) alphabetically by name in ascending order
          children: filteredGroupResources
            .sort((a, b) => (a.name || '').localeCompare((b as any).name || ''))
            .map((resource: Resource) => {
              const dynamicHeight = getDynamicRowHeight(resource.id);
              return {
                id: resource.id,
                name: resource.name,
                role: resource.role,
                department: resource.department || 'Unassigned',
                schedulerGroup: resource.schedulerGroup || 'Unassigned',
                imageUrl: getAvatarUrl(resource.name, resource.photoUrl),
                icon: false,
                iconCls: '',
                ...(dynamicHeight !== undefined && { rowHeight: dynamicHeight }),
              };
            }),
        };
      })
      // Filter out groups with no children after filtering
      .filter(group => {
        // Keep group if it has children OR if the group name itself matches the search
        const groupSearchFields = [group.name];
        return group.children.length > 0 || matchesSearch(groupSearchFields);
      });

    return treeData;
  }, [resources, bookings, projects, projectDetails, costCentres, schedulerGroups, settings.groupingMethod, groupingMode, searchQuery]);

  // Uses the bookings prop which is already filtered by ResourceCalendar
  const bryntumEvents: BryntumEvent[] = useMemo(() => {
    // In heatmap mode, return empty array to hide all bookings
    if (isHeatmapMode) {
      return [];
    }

    const bookingEvents: BryntumEvent[] = bookings
      .filter((booking: Booking) => {
        // For the Unallocated resource, only show bookings with booking type "Project" (bt-project-001 or bt-1)
        if (booking.resourceId === 'unallocated') {
          // Check if the booking type is "Project"
          const isProjectBooking = booking.bookingTypeId === 'bt-project-001' ||
            booking.bookingTypeId === 'bt-1' ||
            String(booking.bookingTypeId) === '1';
          return isProjectBooking;
        }
        // For all other resources, show all bookings
        return true;
      })
      .flatMap((booking: Booking) => {
        // Parse booking times - stored without Z suffix to prevent UTC interpretation
        // Use the times directly without any timezone conversion to avoid 5:30 AM offset for IST users
        const startDate = new Date(booking.startTime);
        const endDate = new Date(booking.endTime);

        // Shift based on difference between booking timezone and current header timezone
        // Pass booking's start date so DST is calculated for the correct date
        const sourceTz = (booking as any).timezone || 'Europe/London';
        const targetTz = selectedTimezone || sourceTz;
        const offsetMin = enableTimezones ? getTimezoneOffset(sourceTz, targetTz, startDate) : 0;
        let displayStartDate = new Date(startDate.getTime() + offsetMin * 60000);
        let displayEndDate = new Date(endDate.getTime() + offsetMin * 60000);

        // For half-day AM/PM bookings, use the actual stored start/end times
        // Morning bookings are stored as 00:00–11:59, Afternoon as 12:00–23:59
        // No clamping to business hours — display matches stored data

        // Build CSS class list for the event
        const clsList: string[] = [];
        const isLocked = !!lockedBookingIds && lockedBookingIds.has(String(booking.id));
        if (booking.isFiltered) clsList.push('filtered-booking');
        if (booking.recurrencePending) clsList.push('pending-recurring-booking');
        if (isLocked) clsList.push('booking-deleting');
        if (booking.recurrenceGroupId) clsList.push('recurring-booking');
        if (isDemandBooking(booking)) clsList.push('demand-booking');
        if ((booking as any).isPlanPreview) clsList.push('plan-preview-booking');
        if ((booking as any).isPlanPreview) clsList.push('b-suggested');

        // Determine which resource IDs to display this booking on.
        // For multi-resource bookings, expand into one event per assigned resource.
        const displayResourceIds: string[] = booking.resourceIds && booking.resourceIds.length > 1
          ? booking.resourceIds.map(String)
          : [booking.resourceId ?? ""];

        return displayResourceIds.map((resId, idx) => {
          // Map resourceId based on grouping mode
          let resourceId: string;
          if (groupingMode === 'project-resource' && booking.projectId) {
            resourceId = `${booking.projectId}-${resId}`;
          } else if (groupingMode === 'role-resource') {
            const resource = resources.find(r => String(r.id) === String(resId));
            const roleName = resource?.role || 'Unassigned';
            resourceId = `${roleName}-${resId}`;
          } else if (groupingMode === 'customer') {
            let customerName: string = booking.customer?.trim() || '';
            if (!customerName && booking.projectId) {
              const project = projectDetails.find(p => String(p.id) === String(booking.projectId));
              customerName = project?.clientName?.trim() || '';
            }
            if (!customerName) customerName = 'Unassigned';
            resourceId = `${customerName}-${resId}`;
          } else {
            resourceId = resId;
          }

          // Use composite ID for additional resource copies to keep Bryntum event IDs unique.
          // The primary resource (idx === 0) keeps the original booking ID for compatibility.
          const eventId = idx === 0 ? String(booking.id) : `${booking.id}__mr__${idx}`;

          const isPlanPreview = !!(booking as any).isPlanPreview;

          return {
            id: eventId,
            resourceId: resourceId,
            name: booking.projectName || booking.description || 'Booking',
            startDate: displayStartDate,
            endDate: displayEndDate,
            eventColor: getBookingColor(booking, settings.statusConfigs, settings.colorOverrides),
            cls: clsList.length > 0 ? clsList.join(' ') : undefined,
            originalBooking: booking,
            isPlanPreview,
            // Locked occurrences (mid-deletion) cannot be dragged or resized.
            ...(isLocked ? { draggable: false, resizable: false } : {}),
          };
        });
      });

    // Add annual-leave absences as dashed event bars and public-holiday
    // absences as line-style event bars (coloured with the Public Holiday
    // colour configured in Settings).
    const absenceColor = settings.absenceColor || '#94a3b8';
    const publicHolidayColor = settings.publicHolidayColor || '#fbbf24';
    const absenceEvents: BryntumEvent[] = absences
      .flatMap(absence => {
        const reason = absence.reason.toLowerCase().replace(/[-_]/g, ' ');
        const isAnnualLeave = reason.includes('annual leave');
        const isPublicHoliday = reason.includes('public holiday') || reason.includes('bank holiday');
        if (!isAnnualLeave && !isPublicHoliday) return [];

        const absStart = new Date(absence.startDate);
        const absEnd = new Date(absence.endDate);
        // Ensure end date covers the full day
        const displayEnd = new Date(absEnd);
        if (displayEnd.getHours() === 0 && displayEnd.getMinutes() === 0) {
          displayEnd.setHours(23, 59, 59, 999);
        }

        const resId = String(absence.resourceId);
        let resourceId: string;
        if (groupingMode === 'role-resource') {
          const resource = resources.find(r => String(r.id) === resId);
          const roleName = resource?.role || 'Unassigned';
          resourceId = `${roleName}-${resId}`;
        } else {
          resourceId = resId;
        }

        return [{
          id: `absence-evt-${absence.id}-${resourceId}-${absStart.getTime()}`,
          absenceId: String(absence.id),
          resourceId,
          name: isPublicHoliday
            ? (absence.reason || 'Public Holiday')
            : (settings.absenceLabelOverride || 'Annual Leave'),
          startDate: absStart,
          endDate: displayEnd,
          eventColor: isPublicHoliday ? publicHolidayColor : absenceColor,
          eventStyle: isPublicHoliday ? 'line' : 'dashed',
          // Keeps the selection ring off absence tiles, which are never bookings.
          cls: 'b-absence-event',
          isAbsenceEvent: true,
        }];
      });

    return [...bookingEvents, ...absenceEvents];
  }, [bookings, lockedBookingIds, groupingMode, resources, projectDetails, isHeatmapMode, selectedTimezone, enableTimezones, settings.businessHoursStart, settings.businessHoursEnd, settings.statusConfigs, settings.colorOverrides, absences, settings.absenceColor, settings.absenceLabelOverride, settings.publicHolidayColor]);

  // Keep the ref in sync every render so forceStoreRefresh reads the latest data
  bryntumEventsRef.current = bryntumEvents;

  // ── Scheduler remount key ────────────────────────────────────────────────
  // The Bryntum React wrapper only lays out reliably against its INITIAL props
  // (startDate/endDate/events/resourceTimeRanges).  Same-instance date
  // navigation (next/prev arrows, Today, date picker) does not reliably
  // re-render booking bars, absence/public-holiday bars or resource time ranges
  // for the new span — historically they only appeared after the component was
  // remounted by switching to another section and back.  A remount is also the
  // only 100%-reliable way to apply a new viewPreset (imperative setViewPreset
  // races the wrapper's own prop cycle).
  //
  // (1) Key the scheduler on the visible DATE RANGE (calendarView +
  //     selectedDate, which together fully determine startDate/endDate) so any
  //     next/prev/Today/date-picker navigation remounts the instance against
  //     the new dates.
  const dateRangeKey = `${calendarView}-${safeSelectedDate.getTime()}`;

  // (2) The data for a newly-navigated span is fetched asynchronously and
  //     usually arrives AFTER the date-range remount, so the instance first
  //     mounts with empty/stale events.  Bryntum's incremental store-sync does
  //     NOT lay those late-arriving records onto a freshly-mounted instance —
  //     they only render when the instance mounts while the data is already
  //     present (exactly what happens on a tab switch).  So, the first time
  //     content becomes available following a navigation, bump a one-shot
  //     epoch to force one more remount that mounts WITH the data.  After that
  //     healthy data-present mount the instance renders trickle-in updates
  //     (e.g. separately-fetched absences) and in-span drag/resize/add through
  //     the incremental diff — the epoch does NOT advance again until the next
  //     navigation, so in-span mutations never remount and never shrink
  //     unrelated bars.
  const contentReady = bryntumEvents.length > 0 || (resourceTimeRanges?.length ?? 0) > 0;
  const lastDateRangeKeyRef = useRef(dateRangeKey);
  // Start awaiting so the very first data arrival after initial mount also gets
  // a clean data-present mount (e.g. when the Planner mounts before its bookings
  // have loaded).
  const awaitingDataMountRef = useRef(true);
  const dataEpochRef = useRef(0);
  if (dateRangeKey !== lastDateRangeKeyRef.current) {
    lastDateRangeKeyRef.current = dateRangeKey;
    awaitingDataMountRef.current = true;
  }
  if (awaitingDataMountRef.current && contentReady) {
    awaitingDataMountRef.current = false;
    dataEpochRef.current += 1;
  }

  // The one-shot data-present remount only applies in normal (non-heatmap)
  // mode; heatmap events are derived synchronously and keyed purely on the
  // date range.
  const schedulerKey = isHeatmapMode
    ? `${schedulerId}-heatmap-${dateRangeKey}`
    : `${schedulerId}-${dateRangeKey}-${dataEpochRef.current}`;

  // When the key changes (heatmap view switch, date navigation, or the one-shot
  // data-present remount) the Bryntum instance remounts, so reset the one-shot
  // refs and let the set-once logic further below re-populate initialDatesRef /
  // initialViewPresetRef with the current span's dates and preset for the
  // freshly mounted instance.
  const prevSchedulerKeyRef = useRef<string>(schedulerKey);
  if (schedulerKey !== prevSchedulerKeyRef.current) {
    prevSchedulerKeyRef.current = schedulerKey;
    hasSetInitialDates.current = false;
    initialViewPresetRef.current = null;
  }

  const { startDate, endDate } = useMemo<{ startDate: Date; endDate: Date }>(() => {
    let start: Date;
    let end: Date;

    // CRITICAL: Always create a fresh Date object to avoid mutation issues
    // This prevents race conditions when selectedDate changes rapidly (e.g., navigating then clicking Today)
    const validDate = new Date(safeSelectedDate.getTime());

    // In Day view, restrict the timeline to visible hours from settings
    const visibleHoursStart = settings.visibleHoursStart ?? 7;
    const visibleHoursEnd = settings.visibleHoursEnd ?? 19;

    switch (calendarView) {
      case 'day':
        start = new Date(validDate);
        start.setHours(Math.floor(visibleHoursStart), Math.round((visibleHoursStart % 1) * 60), 0, 0);
        end = new Date(validDate);
        end.setHours(Math.floor(visibleHoursEnd), Math.round((visibleHoursEnd % 1) * 60), 0, 0);
        break;
      case 'week':
        // Create new Date objects to avoid mutation issues
        start = startOfWeek(new Date(validDate.getTime()), { weekStartsOn: 1 });
        end = endOfWeek(new Date(validDate.getTime()), { weekStartsOn: 1 });
        break;
      case 'month':
        // CRITICAL: Create fresh Date objects for date-fns functions
        start = startOfMonth(new Date(validDate.getTime()));
        end = endOfMonth(new Date(validDate.getTime()));
        break;
      case 'quarter':
        start = startOfQuarter(new Date(validDate.getTime()));
        end = endOfQuarter(new Date(validDate.getTime()));
        break;
      case 'year':
        start = startOfYear(new Date(validDate.getTime()));
        end = endOfYear(new Date(validDate.getTime()));
        break;
      default:
        // Create new Date objects to avoid mutation issues
        start = startOfWeek(new Date(validDate.getTime()), { weekStartsOn: 1 });
        end = endOfWeek(new Date(validDate.getTime()), { weekStartsOn: 1 });
    }

    // CRITICAL VALIDATION: Ensure dates are valid before returning
    const isStartValid = start instanceof Date && !isNaN(start.getTime());
    const isEndValid = end instanceof Date && !isNaN(end.getTime());

    // If dates are inverted, swap them (this can happen during rapid state changes)
    if (isStartValid && isEndValid && start.getTime() >= end.getTime()) {
      console.warn('⚠︝ WARNING: Dates are inverted, swapping them:', {
        originalStart: start.toString(),
        originalEnd: end.toString(),
        calendarView,
        selectedDate: validDate.toString()
      });
      const temp = start;
      start = end;
      end = temp;
    }

    const isOrderCorrect = isStartValid && isEndValid && start.getTime() < end.getTime();

    if (!isOrderCorrect) {
      console.error('🚨 CRITICAL: Invalid date range calculated in useMemo:', {
        start: start?.toString(),
        end: end?.toString(),
        selectedDate: validDate.toString(),
        calendarView,
        startTime: start?.getTime(),
        endTime: end?.getTime(),
        isStartValid,
        isEndValid,
        isOrderCorrect,
        reason: !isStartValid ? 'Invalid start' : !isEndValid ? 'Invalid end' : 'Start >= End'
      });
      // Fallback to a valid week range using current date
      const now = new Date();
      start = startOfWeek(now, { weekStartsOn: 1 });
      end = endOfWeek(now, { weekStartsOn: 1 });
    }

    // Final safety check before returning - swap if still inverted
    if (start.getTime() >= end.getTime()) {
      console.error('🚨 CRITICAL: Dates still inverted after fallback! Swapping as last resort.');
      const temp = start;
      start = end;
      end = temp;
    }

    return { startDate: start, endDate: end };
  }, [safeSelectedDate, calendarView, settings.visibleHoursStart, settings.visibleHoursEnd]);

  startDateRef.current = startDate;
  endDateRef.current = endDate;
  globalWorkingProfileRef.current = settings.globalWorkingProfile;
  settingsRef.current = settings;
  // Rebuild the id→Resource lookup each render so the column renderer
  // (captured once at mount) always resolves the full Resource object.
  const newResMap = new Map<string, Resource>();
  for (const r of resources) newResMap.set(String(r.id), r);
  resourceByIdRef.current = newResMap;

  // Set initialDatesRef ONCE with the first valid computed date range so that
  // the Bryntum scheduler mounts with correct dates. We intentionally do NOT
  // update this ref on subsequent renders — all date changes after mount are
  // handled imperatively via setTimeSpan() in effects. This prevents the
  // BryntumScheduler React wrapper from detecting date prop changes and trying
  // to apply startDate/endDate individually, which causes the "Invalid
  // start/end dates" error during large backward date jumps (e.g. clicking
  // "Today" after navigating 2-3 weeks into the future, where the new endDate
  // is before the old startDate).
  if (!hasSetInitialDates.current && startDate < endDate) {
    initialDatesRef.current = { startDate, endDate };
    hasSetInitialDates.current = true;
  }

  const viewPreset = useMemo(() => {
    // an extra class is added so CSS can target the parent cell for full-cell highlighting.
    switch (calendarView) {
      case 'day':
        // Day View zoom levels:
        // Min (0): Default hourly view
        // Max (1): 15 minute view with 15-min intervals in header
        if (zoomLevel >= 1) {
          return {
            tickWidth: 30,
            shiftIncrement: 1,
            shiftUnit: 'day',
            timeResolution: {
              unit: 'minute',
              increment: 15,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'dddd, MMMM Do YYYY',
              },
              {
                unit: 'hour',
                dateFormat: 'H',
              },
              {
                unit: 'minute',
                increment: 15,
                dateFormat: 'mm',
              },
            ],
          };
        }
        return {
          base: 'hourAndDay',
          timeResolution: {
            unit: 'hour',
            increment: 1,
          },
        };
      case 'week':
        // Week View zoom levels:
        // Min (0): Daily view (current)
        // Low (1): 12 hours view
        // Med (2): 6 hours view
        // High (3): 3 hours view
        // Max (4): 1 hour view
        if (zoomLevel === 4) {
          return {
            tickWidth: 60,
            shiftIncrement: 1,
            shiftUnit: 'week',
            timeResolution: {
              unit: 'hour',
              increment: 1,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 1,
                dateFormat: 'HH',
              },
            ],
          };
        } else if (zoomLevel === 3) {
          return {
            tickWidth: 50,
            shiftIncrement: 1,
            shiftUnit: 'week',
            timeResolution: {
              unit: 'hour',
              increment: 3,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 3,
                dateFormat: 'HH:mm',
              },
            ],
          };
        } else if (zoomLevel === 2) {
          return {
            tickWidth: 60,
            shiftIncrement: 1,
            shiftUnit: 'week',
            timeResolution: {
              unit: 'hour',
              increment: 6,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 6,
                dateFormat: 'HH:mm',
              },
            ],
          };
        } else if (zoomLevel === 1) {
          return {
            tickWidth: 80,
            shiftIncrement: 1,
            shiftUnit: 'week',
            timeResolution: {
              unit: 'hour',
              increment: 12,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 12,
                dateFormat: 'HH:mm',
              },
            ],
          };
        }
        // Default week view (zoomLevel 0) - show exactly 7 days (Monday to Sunday)
        return {
          tickWidth: 100,
          shiftIncrement: 1,
          shiftUnit: 'week',
          timeResolution: {
            unit: 'day',
            increment: 1,
          },
          headers: [
            {
              unit: 'week',
              dateFormat: 'YYYY MMMM DD',
            },
            {
              unit: 'day',
              dateFormat: 'DD ddd',
            },
          ],
        };
      case 'month':
        // Month View zoom levels (same as Week view):
        // Min (0): Daily view (default)
        // Low (1): 12 hours view
        // Med (2): 6 hours view
        // High (3): 3 hours view
        // Max (4): 1 hour view
        if (zoomLevel === 4) {
          return {
            tickWidth: 60,
            shiftIncrement: 1,
            shiftUnit: 'month',
            timeResolution: {
              unit: 'hour',
              increment: 1,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 1,
                dateFormat: 'HH',
              },
            ],
          };
        } else if (zoomLevel === 3) {
          return {
            tickWidth: 50,
            shiftIncrement: 1,
            shiftUnit: 'month',
            timeResolution: {
              unit: 'hour',
              increment: 3,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 3,
                dateFormat: 'HH:mm',
              },
            ],
          };
        } else if (zoomLevel === 2) {
          return {
            tickWidth: 60,
            shiftIncrement: 1,
            shiftUnit: 'month',
            timeResolution: {
              unit: 'hour',
              increment: 6,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 6,
                dateFormat: 'HH:mm',
              },
            ],
          };
        } else if (zoomLevel === 1) {
          return {
            tickWidth: 80,
            shiftIncrement: 1,
            shiftUnit: 'month',
            timeResolution: {
              unit: 'hour',
              increment: 12,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 12,
                dateFormat: 'HH:mm',
              },
            ],
          };
        }
        // Default month view (zoomLevel 0) - show exactly the days from start to end of the current month
        return {
          tickWidth: 50,
          shiftIncrement: 1,
          shiftUnit: 'month',
          timeResolution: {
            unit: 'day',
            increment: 1,
          },
          headers: [
            {
              unit: 'month',
              dateFormat: 'YYYY MMMM',
            },
            {
              unit: 'day',
              dateFormat: 'DD',
            },
          ],
        };
      case 'quarter':
        // Quarter view - show months as top header and weeks (W1, W2, etc.) as second header
        return {
          tickWidth: 80,
          shiftIncrement: 3,
          shiftUnit: 'month',
          timeResolution: {
            unit: 'week',
            increment: 1,
          },
          headers: [
            {
              unit: 'month',
              dateFormat: 'MMMM',
            },
            {
              unit: 'week',
              renderer: (startDate: Date) => {
                // Calculate week number of the year
                const start = new Date(startDate.getFullYear(), 0, 1);
                const diff = startDate.getTime() - start.getTime();
                const oneWeek = 1000 * 60 * 60 * 24 * 7;
                const weekNum = Math.ceil((diff / oneWeek) + 1);
                return `W${weekNum}`;
              },
            },
          ],
        };
      case 'year':
        // Year view - show months (Jan, Feb, Mar, etc.) as columns
        return {
          tickWidth: 100,
          shiftIncrement: 1,
          shiftUnit: 'year',
          timeResolution: {
            unit: 'month',
            increment: 1,
          },
          headers: [
            {
              unit: 'month',
              dateFormat: 'MMM',
            },
          ],
        };
      default:
        return {
          base: 'weekAndDay',
          tickWidth: 100,
          shiftIncrement: 1,
          shiftUnit: 'week',
          timeResolution: {
            unit: 'day',
            increment: 1,
          },
        };
    }
  }, [calendarView, zoomLevel]);

  // Capture the viewPreset on first render so the <BryntumScheduler> component
  // always receives a stable initial value.  All subsequent preset changes are
  // applied imperatively inside the store-sync useEffect (within suspendRefresh)
  // to prevent Bryntum's React wrapper from calling setViewPreset unguarded and
  // wiping the event store.
  if (!initialViewPresetRef.current) {
    initialViewPresetRef.current = viewPreset;
  }

  const heatmapEvents: BryntumEvent[] = useMemo(() => {
    if (!isHeatmapMode) return [];

    const events: BryntumEvent[] = [];
    const workingProfile = settings.globalWorkingProfile ?? ({} as WorkingHoursProfile);
    const businessHoursStart = settings.businessHoursStart || 9;

    const getWorkingHoursForDay = (resource: Resource, date: Date): number =>
      getProfileWorkingHoursForDay(resource, date, workingProfile);

    const ticks: Array<{ start: Date; end: Date }> = [];
    let tickCurrent = new Date(startDate);

    while (tickCurrent < endDate) {
      const next = new Date(tickCurrent);
      switch (calendarView) {
        case 'day':
          next.setHours(next.getHours() + 1);
          break;
        case 'week':
        case 'month':
          next.setDate(next.getDate() + 1);
          break;
        case 'quarter':
          next.setDate(next.getDate() + 7);
          break;
        case 'year':
          next.setMonth(next.getMonth() + 1);
          break;
        default:
          next.setDate(next.getDate() + 1);
      }
      ticks.push({
        start: new Date(tickCurrent),
        end: next > endDate ? new Date(endDate) : next,
      });
      tickCurrent = next;
    }

    const resourceMappings: Array<{ resource: Resource; bryntumId: string; projectFilter?: string; customerFilter?: string }> = [];

    if (groupingMode === 'project-resource') {
      resources.forEach((resource) => {
        const projectIds = new Set<string>();
        bookings.forEach((b) => {
          if (String(b.resourceId) === String(resource.id) && b.projectId) {
            projectIds.add(b.projectId);
          }
        });
        if (projectIds.size === 0) {
          resourceMappings.push({ resource, bryntumId: resource.id });
        } else {
          projectIds.forEach((projectId) => {
            resourceMappings.push({
              resource,
              bryntumId: `${projectId}-${resource.id}`,
              projectFilter: projectId,
            });
          });
        }
      });
    } else if (groupingMode === 'role-resource') {
      resources.forEach((resource) => {
        const roleName = resource.role || 'Unassigned';
        resourceMappings.push({ resource, bryntumId: `${roleName}-${resource.id}` });
      });
    } else if (groupingMode === 'customer') {
      // Build a map of customerName -> Set of resource IDs (same logic as the row builder)
      const customerResourceMap = new Map<string, Set<string>>();
      bookings.forEach((b) => {
        if (b.resourceId === 'unallocated') return;
        let customerName: string = b.customer?.trim() || '';
        if (!customerName && b.projectId) {
          const project = projectDetails.find(p => String(p.id) === String(b.projectId));
          customerName = project?.clientName?.trim() || '';
        }
        if (!customerName) customerName = 'Unassigned';
        if (!customerResourceMap.has(customerName)) {
          customerResourceMap.set(customerName, new Set());
        }
        customerResourceMap.get(customerName)!.add(String(b.resourceId));
      });

      customerResourceMap.forEach((resourceIds, customerName) => {
        resourceIds.forEach((resourceId) => {
          const resource = resources.find(r => String(r.id) === resourceId);
          if (resource) {
            resourceMappings.push({
              resource,
              bryntumId: `${customerName}-${resource.id}`,
              customerFilter: customerName,
            });
          }
        });
      });
    } else {
      resources.forEach((resource) => {
        resourceMappings.push({ resource, bryntumId: resource.id });
      });
    }

    resourceMappings.forEach(({ resource, bryntumId, projectFilter, customerFilter }) => {
      const resIdStr = String(resource.id);
      const resourceBookings = bookings.filter((b) => {
        // Match on primary resourceId OR within the resourceIds array
        const matchesPrimary = b.resourceId != null && String(b.resourceId) === resIdStr;
        const matchesMulti = !matchesPrimary && Array.isArray(b.resourceIds) &&
          b.resourceIds.some(id => String(id) === resIdStr);
        if (!matchesPrimary && !matchesMulti) return false;
        if (projectFilter && String(b.projectId) !== String(projectFilter)) return false;
        if (customerFilter) {
          // Derive booking customer name the same way as the row builder
          let bCustomer: string = b.customer?.trim() || '';
          if (!bCustomer && b.projectId) {
            const project = projectDetails.find(p => String(p.id) === String(b.projectId));
            bCustomer = project?.clientName?.trim() || '';
          }
          if (!bCustomer) bCustomer = 'Unassigned';
          if (bCustomer !== customerFilter) return false;
        }
        return true;
      });

      ticks.forEach((tick, tickIndex) => {
        const {
          workingHours,
          availableHours,
          bookedHours,
          confirmedHours,
          plannedHours,
          absenceHours,
          utilisation: utilization,
          hasHardConflict,
          matchedBookings,
        } = calculateHeatmapTickUtilisation({
          resource,
          tick,
          bookings: resourceBookings,
          globalWorkingProfile: workingProfile,
          absences,
          bankHolidays,
          bookingTypes: settings.bookingTypes,
          businessHoursStart,
        });

        // ── Absence / bank-holiday reasons shown in the tooltip ──────────────
        const absenceReasonSet = new Set<string>();
        absences
          .filter((abs) => String(abs.resourceId) === String(resource.id))
          .forEach((abs) => {
            const absStart = new Date(abs.startDate); absStart.setHours(0, 0, 0, 0);
            const absEnd = new Date(abs.endDate); absEnd.setHours(23, 59, 59, 999);
            if (absStart >= tick.end || absEnd <= tick.start) return;
            absenceReasonSet.add(abs.reason || 'Absence');
          });
        bankHolidays.forEach((bh) => {
          const bhDate = new Date(bh.date); bhDate.setHours(0, 0, 0, 0);
          const bhEnd = new Date(bhDate); bhEnd.setHours(23, 59, 59, 999);
          if (bhDate < tick.end && bhEnd > tick.start && getWorkingHoursForDay(resource, bhDate) > 0) {
            absenceReasonSet.add(bh.name || 'Bank Holiday');
          }
        });

        let color: string;
        let band: string;
        if (workingHours === 0) {
          color = '#f5f5f5'; band = 'Non-working';
        } else {
          // bookedHours === 0 (unbooked) is handled by the early-return below,
          // so here we always have actual utilisation to colour.
          color = bookedHours === 0
            ? 'transparent'
            : getUtilizationColor(utilization, settings.utilisationTiers);
          band = bookedHours === 0
            ? 'Unbooked'
            : getUtilizationLabel(utilization, settings.utilisationTiers);
        }

        // ── Planned-only / mixed detection ────────────────────────────────
        // isPlannedOnly: has bookings but NONE are confirmed/completed.
        // isMixed:       has BOTH confirmed and planned hours in the same cell.
        // plannedRatio:  share of total booked hours that are unconfirmed (0–1).
        const isPlannedOnly = matchedBookings.length > 0 && confirmedHours === 0 && plannedHours > 0;
        const isMixed       = confirmedHours > 0 && plannedHours > 0;
        const plannedRatio  = bookedHours > 0 ? plannedHours / bookedHours : 0;

        // Forecast views: Month, Quarter, Year
        const isForecastView = calendarView === 'month' || calendarView === 'quarter' || calendarView === 'year';

        // Per-view threshold: use forecastSettings if present, fall back to legacy single value
        const fs = settings.forecastSettings;
        const warnThreshold = fs
          ? (calendarView === 'month' ? fs.monthThreshold
            : calendarView === 'quarter' ? fs.quarterThreshold
            : calendarView === 'year' ? fs.yearThreshold
            : 70)
          : (settings.plannedOnlyWarningThreshold ?? 70);
        const showPlannedPrefix = fs ? fs.showPlannedPrefix : true;

        // ── CSS class composition ──────────────────────────────────────────
        // Priority order: conflict > mixed > planned-only > confirmed
        // Forecast ticks are too narrow for glyph prefixes, so status is carried
        // entirely by the background treatments below.
        let cellCls = 'b-heatmap-event';
        if (hasHardConflict) {
          cellCls += ' b-heatmap-conflict';
        } else if (isMixed && isForecastView && workingHours > 0) {
          // Mix of confirmed + planned — show split bar, intensity based on planned ratio
          cellCls += plannedRatio > 0.5
            ? ' b-heatmap-mixed b-heatmap-mixed-majority'   // majority unconfirmed
            : ' b-heatmap-mixed';                           // minority unconfirmed
        } else if (isPlannedOnly && isForecastView && showPlannedPrefix && workingHours > 0) {
          // Fully unconfirmed — muted dashed treatment, corner flag above threshold
          cellCls += utilization >= warnThreshold
            ? ' b-heatmap-planned b-heatmap-planned-high'
            : ' b-heatmap-planned';
        }

        // ── Cell label ────────────────────────────────────────────────────
        // Forecast views (Month / Quarter / Year) show the bare percentage so a
        // 3-digit value stays readable in a ~50px tick; the ⚠ conflict glyph is
        // kept on the wider day / week ticks. Status detail lives in the tooltip.
        const cellLabel = (() => {
          if (workingHours === 0) return '';
          if (bookedHours === 0) return ''; // empty day — no label needed
          const pct = `${Math.round(utilization)}%`;
          if (hasHardConflict && !isForecastView) return `⚠ ${pct}`;
          return pct;
        })();

        // Skip creating an event for non-working day ticks, and for ticks whose
        // capacity has been fully removed by bank holidays / absences — they have
        // no available hours and should appear as empty (no overlay / label).
        if (workingHours === 0 || availableHours === 0) return;

        // Skip creating an event for genuinely unbooked ticks — transparent
        // Bryntum event nodes still render visible borders/outlines even with
        // color = 'transparent', producing stray lines on otherwise empty rows.
        if (bookedHours === 0) return;

        events.push({
          id: `heatmap-${bryntumId}-${tickIndex}`,
          resourceId: bryntumId,
          name: cellLabel,
          startDate: tick.start,
          endDate: tick.end,
          eventColor: color,
          cls: cellCls,
          isHeatmapEvent: true,
          heatmapData: {
            bookedHours: Math.round(bookedHours * 10) / 10,
            availableHours: Math.round(availableHours * 10) / 10,
            utilization: Math.round(utilization),
            band,
            bandColor: color,
            bookings: matchedBookings,
            confirmedHours: Math.round(confirmedHours * 10) / 10,
            plannedHours: Math.round(plannedHours * 10) / 10,
            absenceHours: Math.round(absenceHours * 10) / 10,
            absenceReasons: Array.from(absenceReasonSet),
            hasHardConflict,
            weeklyAvgUtilization: null, // populated in second pass below
            isPlannedOnly,
            isMixed,
            plannedRatio: Math.round(plannedRatio * 100) / 100,
            isCustomProfile: !!(resource.customWorkingProfile && Object.keys(resource.customWorkingProfile).length > 0),
            dailyHours: getWorkingHoursForDay(resource, tick.start),
          },
        });
      });
    });

    // ── Second pass: compute weekly average utilization per resource per week ──
    // Key: `${resourceId}::${ISO-week-string}` e.g. "res-1::2026-W16"
    // This means every event gets the avg for *its own* calendar week, regardless
    // of what week the view is anchored to — fixing the bug where events outside
    // startOfWeek(viewStart) got weeklyAvgUtilization = null.
    const weekBuckets = new Map<string, { sum: number; count: number }>();

    events.forEach((ev) => {
      if (!ev.heatmapData || ev.heatmapData.availableHours === 0) return;
      // Derive ISO week key from the event's own start date
      const evWeekStart = startOfWeek(ev.startDate as Date, { weekStartsOn: 1 });
      const isoKey = format(evWeekStart, 'yyyy-MM-dd');           // e.g. "2026-04-14" (Monday)
      const bucketKey = `${ev.resourceId}::${isoKey}`;
      const bucket = weekBuckets.get(bucketKey) ?? { sum: 0, count: 0 };
      bucket.sum += ev.heatmapData.utilization;
      bucket.count += 1;
      weekBuckets.set(bucketKey, bucket);
    });

    // Stamp the correct week's average onto every event
    events.forEach((ev) => {
      if (!ev.heatmapData) return;
      const evWeekStart = startOfWeek(ev.startDate as Date, { weekStartsOn: 1 });
      const isoKey = format(evWeekStart, 'yyyy-MM-dd');
      const bucketKey = `${ev.resourceId}::${isoKey}`;
      const bucket = weekBuckets.get(bucketKey);
      ev.heatmapData.weeklyAvgUtilization = bucket
        ? Math.round(bucket.sum / bucket.count)
        : null;
    });

    return events;
  }, [isHeatmapMode, startDate, endDate, calendarView, resources, bookings, absences, bankHolidays, groupingMode, settings]);

  const findResourceById = useCallback((id: string): Resource | undefined => {
    // First try direct match
    let resource = resources.find((r: Resource) => String(r.id) === String(id));

    // In project-resource mode, the ID might be a combined "projectId-resourceId"
    // Try to extract the actual resource ID
    if (!resource && groupingMode === 'project-resource' && id.includes('-') && !id.startsWith('group-') && !id.startsWith('project-')) {
      const parts = id.split('-');
      if (parts.length >= 2) {
        // The resource ID is everything after the first part (project ID)
        const actualResourceId = parts.slice(1).join('-');
        resource = resources.find((r: Resource) => String(r.id) === String(actualResourceId));
      }
    }

    // In role-resource mode, the ID might be a combined "roleName-resourceId"
    // Try to extract the actual resource ID
    if (!resource && groupingMode === 'role-resource' && id.includes('-') && !id.startsWith('group-') && !id.startsWith('role-')) {
      const parts = id.split('-');
      if (parts.length >= 2) {
        // The resource ID is everything after the first part (role name)
        const actualResourceId = parts.slice(1).join('-');
        resource = resources.find((r: Resource) => String(r.id) === String(actualResourceId));
      }
    }

    // In customer mode, the ID might be a combined "customerName-resourceId"
    // Try to match the actual resource by finding one whose ID the composite ends with
    if (!resource && groupingMode === 'customer' && id.includes('-') && !id.startsWith('group-') && !id.startsWith('customer-')) {
      // Find any resource whose id appears at the end of the composite ID after a hyphen
      resource = resources.find((r: Resource) => {
        const rid = String(r.id);
        return id === `${id.slice(0, id.length - rid.length - 1)}-${rid}` && id.endsWith(`-${rid}`);
      });
      if (!resource) {
        // Fallback: take everything after the first hyphen
        const dashIdx = id.indexOf('-');
        if (dashIdx !== -1) {
          const actualResourceId = id.slice(dashIdx + 1);
          resource = resources.find((r: Resource) => String(r.id) === actualResourceId);
        }
      }
    }

    return resource;
  }, [resources, groupingMode]);

  // Extract the customer name from a customer-mode composite row id
  // ("customerName-resourceId"). Customer names may contain hyphens, so strip
  // the real resource id by suffix match. Returns undefined outside customer
  // mode, for group rows, or for the 'Unassigned' bucket.
  const extractCustomerName = useCallback((combinedId: string): string | undefined => {
    if (groupingMode !== 'customer') return undefined;
    if (!combinedId.includes('-') || combinedId.startsWith('group-') || combinedId.startsWith('customer-')) {
      return undefined;
    }
    const matched = resources.find((r: Resource) => combinedId.endsWith(`-${String(r.id)}`));
    const realId = matched ? String(matched.id) : combinedId.slice(combinedId.indexOf('-') + 1);
    const name = combinedId.slice(0, combinedId.length - realId.length - 1);
    if (!name || name === 'Unassigned') return undefined;
    return name;
  }, [resources, groupingMode]);

  const findBookingById = useCallback((id: string): Booking | undefined => {
    // Strip multi-resource display suffix if present (e.g., "123__mr__1" → "123")
    const baseId = id.includes('__mr__') ? id.split('__mr__')[0] : id;
    return bookings.find((b: Booking) => String(b.id) === String(baseId));
  }, [bookings]);



  // ─── Heatmap rich tooltip ────────────────────────────────────────────────────
  const generateHeatmapTooltipHtml = useCallback((hData: NonNullable<BryntumEvent['heatmapData']>, tickStart: Date): string => {
    // Don't show a tooltip at all when there are no bookings on this cell
    if (hData.bookings.length === 0) return '';

    // Determine if bandColor is dark so we pick a contrasting text colour
    const hexToRgb = (hex: string) => {
      const h = hex.replace('#', '');
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return { r, g, b };
    };
    const rgb = hexToRgb(hData.bandColor || '#6b7280');
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    const headerTextColor = luminance > 0.55 ? '#1f2937' : '#ffffff';

    // Forecast view context for tooltip
    const isForecastViewTooltip = calendarView === 'month' || calendarView === 'quarter' || calendarView === 'year';
    const fsTooltip = settings.forecastSettings;
    const warnThresholdTooltip = fsTooltip
      ? (calendarView === 'month' ? fsTooltip.monthThreshold
        : calendarView === 'quarter' ? fsTooltip.quarterThreshold
        : calendarView === 'year' ? fsTooltip.yearThreshold
        : 70)
      : (settings.plannedOnlyWarningThreshold ?? 70);
    const showSplitTooltip = fsTooltip ? fsTooltip.showConfirmedPlannedSplit : true;

    // Format helpers
    const fmtTime = (iso: string) => {
      const d = new Date(iso);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };
    const fmtDuration = (startIso: string, endIso: string, durationType: string, halfDayPeriod?: string): string => {
      if (durationType === 'full-day') return 'Full day';
      if (durationType === 'half-day-am') return 'Half day (AM)';
      if (durationType === 'half-day-pm') return 'Half day (PM)';
      if (durationType === 'half-day') return halfDayPeriod === 'pm' ? 'Half day (PM)' : 'Half day (AM)';
      const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
      const totalMins = Math.round(ms / 60000);
      const h = Math.floor(totalMins / 60);
      const m = totalMins % 60;
      if (h === 0) return `${m}m`;
      if (m === 0) return `${h}h`;
      return `${h}h ${m}m`;
    };
    const capitalize = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
    const statusDot = (status: string): string => {
      const sc = settings.statusConfigs?.find(s => String(s.id) === status?.toLowerCase());
      const c = sc?.color || getDefaultStatusColor(status);
      return `<span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${c};margin-right:4px;flex-shrink:0;margin-top:2px;"></span>`;
    };

    // Dynamic colours for confirmed/planned split visualisations
    const confirmedColor = settings.statusConfigs?.find(s => String(s.id) === 'confirmed')?.color || getDefaultStatusColor('confirmed');
    const plannedColor = settings.statusConfigs?.find(s => String(s.id) === 'planned')?.color || getDefaultStatusColor('planned');

    const clockSvg = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:1px;"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`;
    const tagSvg = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:1px;"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>`;
    const briefcaseSvg = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:1px;"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>`;
    const pinSvg = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:1px;"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>`;

    // ── Confirmed / Planned split label ──────────────────────────────────────
    const confirmedPlannedDetail = (() => {
      // Respect the showConfirmedPlannedSplit toggle on all views
      if (!showSplitTooltip) return '';
      const parts: string[] = [];
      if (hData.confirmedHours > 0) {
        parts.push(`<span style="display:inline-flex;align-items:center;gap:3px;"><span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${confirmedColor};flex-shrink:0;"></span><span>${hData.confirmedHours}h confirmed</span></span>`);
      }
      if (hData.plannedHours > 0) {
        parts.push(`<span style="display:inline-flex;align-items:center;gap:3px;"><span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${plannedColor};flex-shrink:0;"></span><span>${hData.plannedHours}h planned</span></span>`);
      }
      if (parts.length === 0) return '';
      return `<div style="font-size:11px;color:#6b7280;padding:5px 12px 2px;display:flex;gap:10px;flex-wrap:wrap;">${parts.join('')}</div>`;
    })();

    // ── Forecast banner (Month/Quarter/Year views) ────────────────────────────
    // Covers three states: fully planned-only, mixed-majority-planned, mixed-minority-planned
    const forecastBanner = (() => {
      if (!isForecastViewTooltip || hData.bookedHours === 0) return '';

      const { isPlannedOnly, isMixed, plannedRatio, utilization } = hData;
      if (!isPlannedOnly && !isMixed) return ''; // fully confirmed — no banner needed

      // ── Determine banner tier ──────────────────────────────────────────
      // Tier A: fully planned-only, high risk (≥ threshold)
      // Tier B: fully planned-only, low risk (< threshold)
      // Tier C: mixed, majority planned (>50%)
      // Tier D: mixed, minority planned (≤50%)
      type BannerTier = 'A' | 'B' | 'C' | 'D';
      const tier: BannerTier = isPlannedOnly
        ? (utilization >= warnThresholdTooltip ? 'A' : 'B')
        : (plannedRatio > 0.5 ? 'C' : 'D');

      const plannedPct = Math.round((plannedRatio ?? 0) * 100);
      const confirmedPct = 100 - plannedPct;

      const config: Record<BannerTier, { bg: string; border: string; text: string; iconColor: string; label: string; icon: 'warn' | 'info' }> = {
        A: {
          bg: '#fef3c7', border: '#fde68a', text: '#92400e', iconColor: '#d97706',
          label: `Forecast — all ${hData.bookedHours}h unconfirmed at ${utilization}% utilisation`,
          icon: 'warn',
        },
        B: {
          bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af', iconColor: '#3b82f6',
          label: `Forecast — all bookings are planned (unconfirmed)`,
          icon: 'info',
        },
        C: {
          bg: '#fff7ed', border: '#fed7aa', text: '#9a3412', iconColor: '#ea580c',
          label: showSplitTooltip
            ? `Forecast — ${plannedPct}% planned, ${confirmedPct}% confirmed`
            : `Forecast — mostly planned (unconfirmed)`,
          icon: 'warn',
        },
        D: {
          bg: '#f0fdf4', border: '#bbf7d0', text: '#166534', iconColor: '#16a34a',
          label: showSplitTooltip
            ? `Mixed — ${confirmedPct}% confirmed, ${plannedPct}% planned`
            : `Mixed — confirmed and planned`,
          icon: 'info',
        },
      };

      const { bg, border, text, iconColor, label, icon } = config[tier];
      const svgIcon = icon === 'warn'
        ? `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="${iconColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`
        : `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="${iconColor}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`;

      // Split ratio bar — shows confirmed / planned proportion visually
      const splitBar = (isMixed && showSplitTooltip)
        ? `<div style="margin-top:5px;height:4px;border-radius:2px;overflow:hidden;background:#e5e7eb;display:flex;">
             <div style="width:${confirmedPct}%;background:${confirmedColor};"></div>
             <div style="width:${plannedPct}%;background:${plannedColor};"></div>
           </div>
           <div style="display:flex;justify-content:space-between;margin-top:2px;">
             <span style="font-size:10px;color:#6b7280;"><span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${confirmedColor};margin-right:3px;"></span>${hData.confirmedHours}h confirmed</span>
             <span style="font-size:10px;color:#6b7280;"><span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${plannedColor};margin-right:3px;"></span>${hData.plannedHours}h planned</span>
           </div>`
        : '';

      return `<div style="padding:6px 12px ${isMixed ? '8px' : '6px'};background:${bg};border-bottom:1px solid ${border};">
        <div style="display:flex;align-items:center;gap:6px;">
          ${svgIcon}
          <span style="font-size:11px;color:${text};font-weight:500;">${label}</span>
        </div>
        ${splitBar}
      </div>`;
    })();

    // ── Absence / leave row ───────────────────────────────────────────────────
    const absenceRow = hData.absenceHours > 0
      ? `<div style="display:flex;align-items:center;gap:6px;padding:5px 12px;background:#fef3c7;border-top:1px solid #fde68a;border-bottom:1px solid #fde68a;">
           <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
           <span style="font-size:11px;color:#92400e;font-weight:500;">${hData.absenceHours}h absence${hData.absenceReasons.length ? ` — ${escapeHtml(hData.absenceReasons.join(', '))}` : ''}</span>
         </div>`
      : '';

    // ── Conflict banner ───────────────────────────────────────────────────────
    // One booking (the anchor) stays with the original resource; only the
    // surplus bookings are handed to the Batch Assignment modal to reassign.
    const { surplus: surplusBookings } = partitionConflictBookings(hData.bookings);
    const allConflictIds = surplusBookings.map(b => b.id).join(',');
    const conflictCount = surplusBookings.length;
    const conflictBanner = hData.hasHardConflict
      ? `<div style="display:flex;align-items:center;gap:6px;padding:6px 12px;background:#fffbeb;border-bottom:1px solid #fde68a;">
           <div style="display:flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;background:#f59e0b;flex-shrink:0;">
             <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
           </div>
           <div style="flex:1;min-width:0;">
             <div style="font-size:11px;color:#92400e;font-weight:700;line-height:1.3;">Scheduling Conflict</div>
           </div>
           <button
             data-heatmap-resolve-bookings="${allConflictIds}"
             style="flex-shrink:0;display:inline-flex;align-items:center;gap:3px;padding:3px 9px;font-size:10px;font-weight:700;color:#ffffff;background:#f59e0b;border:1px solid #d97706;border-radius:4px;cursor:pointer;white-space:nowrap;line-height:1.6;"
             onmouseover="this.style.background='#d97706'"
             onmouseout="this.style.background='#f59e0b'"
           >
             <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
             Resolve ${conflictCount > 1 ? `(${conflictCount})` : ''}
           </button>
         </div>`
      : '';

    // ── Weekly context bar ────────────────────────────────────────────────────
    const weeklyBar = hData.weeklyAvgUtilization !== null
      ? (() => {
          const pct = Math.min(hData.weeklyAvgUtilization, 100);
          const barColor = pct > 100 ? '#fecaca' : pct >= 91 ? '#fde68a' : pct >= 71 ? '#bbf7d0' : '#bfdbfe';
          return `
            <div style="padding:6px 12px 7px;border-top:1px solid rgba(0,0,0,0.06);background:#f9fafb;">
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:3px;">
                <span style="font-size:10px;font-weight:600;color:#9ca3af;letter-spacing:0.04em;text-transform:uppercase;">Avg utilisation this week</span>
                <span style="font-size:11px;font-weight:700;color:rgba(0,0,0,0.75);">${hData.weeklyAvgUtilization}%</span>
              </div>
              <div style="height:4px;background:#e5e7eb;border-radius:2px;overflow:hidden;">
                <div style="height:100%;width:${pct}%;background:${barColor};border-radius:2px;transition:width 0.2s;"></div>
              </div>
            </div>`;
        })()
      : '';

    // ── Summary stats strip ───────────────────────────────────────────────────
    // Use the reconciled total (confirmedHours + plannedHours) so the numbers
    // are always internally consistent. Label the middle column contextually:
    //   • Planned only  → "Planned"   (no confirmed hours at all)
    //   • Mixed         → "Total"     (confirmed + planned combined)
    //   • Confirmed     → "Booked"    (all confirmed, classic label)
    const totalHours   = Math.round((hData.confirmedHours + hData.plannedHours) * 10) / 10;
    const bookedLabel  = hData.isPlannedOnly ? 'Planned' : hData.isMixed ? 'Total' : 'Booked';
    const bookedValue  = hData.isPlannedOnly ? hData.plannedHours : hData.isMixed ? totalHours : hData.confirmedHours;
    const remainingVal = Math.round((hData.availableHours - totalHours) * 10) / 10;

    const summaryStrip = `
      <div style="display:flex;gap:0;border-top:1px solid rgba(0,0,0,0.08);border-bottom:1px solid rgba(0,0,0,0.08);background:#f9fafb;">
        <div style="flex:1;text-align:center;padding:6px 4px;border-right:1px solid rgba(0,0,0,0.08);">
          <div style="font-size:11px;color:#6b7280;margin-bottom:1px;">Available</div>
          <div style="font-size:13px;font-weight:600;color:#1f2937;">${hData.availableHours}h</div>
        </div>
        <div style="flex:1;text-align:center;padding:6px 4px;border-right:1px solid rgba(0,0,0,0.08);">
          <div style="font-size:11px;color:#6b7280;margin-bottom:1px;">${bookedLabel}</div>
          <div style="font-size:13px;font-weight:600;color:#1f2937;">${bookedValue}h</div>
        </div>
        <div style="flex:1;text-align:center;padding:6px 4px;">
          <div style="font-size:11px;color:#6b7280;margin-bottom:1px;">${remainingVal < 0 ? 'Overbooked' : 'Remaining'}</div>
          <div style="font-size:13px;font-weight:600;color:${remainingVal < 0 ? '#b91c1c' : '#1f2937'};">${remainingVal < 0 ? `+${Math.abs(remainingVal)}h` : `${remainingVal}h`}</div>
        </div>
      </div>
      ${forecastBanner ? '' : confirmedPlannedDetail}`;

    // Booking rows
    let bookingRows = '';
    if (hData.bookings.length === 0) {
      bookingRows = `<div style="padding:10px 12px;font-size:12px;color:#9ca3af;font-style:italic;">No bookings</div>`;
    } else {
      hData.bookings.forEach((b, idx) => {
        const isLast = idx === hData.bookings.length - 1;
        const isHalfDayAm = b.durationType === 'half-day-am' || (b.durationType === 'half-day' && b.halfDayPeriod !== 'pm');
        const isHalfDayPm = b.durationType === 'half-day-pm' || (b.durationType === 'half-day' && b.halfDayPeriod === 'pm');
        const timeStr = (b.durationType === 'full-day')
          ? 'All day'
          : isHalfDayAm
            ? `${fmtTime(b.startTime)} – midday (AM)`
            : isHalfDayPm
              ? `Midday – ${fmtTime(b.endTime)} (PM)`
              : `${fmtTime(b.startTime)} – ${fmtTime(b.endTime)}`;
        const duration = fmtDuration(b.startTime, b.endTime, b.durationType, b.halfDayPeriod);

        bookingRows += `
          <div style="padding:8px 12px;${isLast ? '' : 'border-bottom:1px solid #f3f4f6;'}display:flex;align-items:center;gap:8px;">
            <!-- Project + status -->
            <div style="flex:1;min-width:0;">
              <div style="font-size:12px;font-weight:600;color:#111827;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(b.projectName || b.description || 'Booking')}</div>
              <div style="display:flex;align-items:center;gap:4px;margin-top:1px;">
                ${statusDot(b.status)}<span style="font-size:10px;color:#6b7280;">${capitalize(b.status || '')}</span>
                <span style="font-size:10px;color:#d1d5db;">·</span>
                <span style="font-size:10px;color:#6b7280;">${timeStr}</span>
              </div>
            </div>
            <!-- Edit button -->
            <button
              data-heatmap-edit-booking="${b.id}"
              style="display:inline-flex;align-items:center;gap:3px;padding:2px 8px;font-size:10px;font-weight:500;color:#3b82f6;background:#eff6ff;border:1px solid #bfdbfe;border-radius:4px;cursor:pointer;white-space:nowrap;flex-shrink:0;"
              onmouseover="this.style.background='#dbeafe'"
              onmouseout="this.style.background='#eff6ff'"
            >
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Edit
            </button>
          </div>`;
      });
    }

    const dateLabel = format(tickStart, calendarView === 'week' || calendarView === 'month' ? 'EEE d MMM yyyy' : calendarView === 'day' ? 'EEE d MMM yyyy, HH:mm' : 'd MMM yyyy');

    return `
      <div style="width:300px;max-width:min(300px,calc(100vw - 32px));font-family:inherit;border-radius:8px;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,0.15);display:flex;flex-direction:column;">
        <!-- Header -->
        <div style="background:${hData.bandColor};padding:10px 12px;">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:2px;">
            <div style="display:flex;align-items:center;gap:5px;">
              ${hData.hasHardConflict ? `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="${headerTextColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>` : ''}
              <span style="font-size:13px;font-weight:700;color:${headerTextColor};">${hData.band}</span>
            </div>
            <span style="font-size:13px;font-weight:700;color:${headerTextColor};">${hData.utilization}%</span>
          </div>
          <div style="font-size:11px;color:${headerTextColor};opacity:0.85;">${dateLabel} &nbsp;·&nbsp; ${hData.bookedHours}h booked</div>
        </div>
        <!-- Working profile indicator -->
        <div style="display:flex;align-items:center;justify-content:space-between;padding:5px 12px;border-bottom:1px solid rgba(0,0,0,0.06);background:#f9fafb;">
          <span style="font-size:10px;color:#9ca3af;">${hData.isCustomProfile ? 'Custom Profile' : 'Standard Profile'}</span>
          <span style="font-size:10px;font-weight:600;color:#374151;">${hData.dailyHours}h/day</span>
        </div>
        <!-- Forecast / planned-only banner (blue = low risk, amber = high risk) -->
        ${forecastBanner}
        <!-- Summary strip -->
        ${summaryStrip}
        <!-- Absence row (amber) -->
        ${absenceRow}
        <!-- Conflict banner (red) -->
        ${conflictBanner}
        <!-- Bookings -->
        <div style="flex:1 1 auto;min-height:0;max-height:340px;overflow-y:auto;background:#ffffff;">
          ${hData.bookings.length > 0 ? `<div style="padding:7px 12px 3px;font-size:10px;font-weight:700;color:#9ca3af;letter-spacing:0.06em;text-transform:uppercase;display:flex;align-items:center;gap:5px;"><span style="display:inline-flex;align-items:center;justify-content:center;min-width:16px;height:16px;border-radius:8px;background:#e5e7eb;color:#6b7280;font-size:10px;font-weight:700;padding:0 4px;">${hData.bookings.length}</span>${hData.bookings.length !== 1 ? 'Bookings' : 'Booking'}</div>` : ''}
          ${bookingRows}
        </div>
        <!-- Weekly context bar -->
        ${weeklyBar}
      </div>`;
  }, [calendarView, settings.statusConfigs]);
  // ─────────────────────────────────────────────────────────────────────────────

  const generateTooltipHtml = useCallback((booking: Booking): string => {
    const bookingType = settings.bookingTypes.find((bt: BookingType) => String(bt.id) === String(booking.bookingTypeId));

    let fieldConfigurations: FieldConfiguration[] = [];
    if (bookingType?.fieldConfigurations) {
      try {
        fieldConfigurations = typeof bookingType.fieldConfigurations === 'string'
          ? JSON.parse(bookingType.fieldConfigurations)
          : bookingType.fieldConfigurations || [];
      } catch (error) {
        console.error('Error parsing field configurations:', error);
      }
    }

    const getFieldValue = (fieldName: string): string | null =>
      getTooltipFieldValue(booking, fieldName, { bookingType, projectDetails, clients, costCentres });

    const header = booking.description || booking.projectName || 'Booking';

    const startTime = booking.startTime ? new Date(booking.startTime) : null;
    const endTime = booking.endTime ? new Date(booking.endTime) : null;
    const isValidStartTime = startTime && isValid(startTime);
    const isValidEndTime = endTime && isValid(endTime);

    // The From/To dates follow the timezone currently selected on the planner
    // header (shift by the offset between the booking's timezone and the header
    // timezone), while the timezone line below reports the booking's own timezone.
    // Use the booking's start date so DST is calculated for the correct date.
    const bookingTzTooltip = (booking as any).timezone || settings.timelineDefaultTimezone || 'UTC';
    const targetTzTooltip = selectedTimezone || bookingTzTooltip;
    const tooltipOffsetMin = enableTimezones
      ? getTimezoneOffset(bookingTzTooltip, targetTzTooltip, isValidStartTime ? startTime as Date : new Date())
      : 0;
    const tooltipStart = isValidStartTime
      ? new Date((startTime as Date).getTime() + tooltipOffsetMin * 60000)
      : null;
    const tooltipEnd = isValidEndTime
      ? new Date((endTime as Date).getTime() + tooltipOffsetMin * 60000)
      : null;
    // Half-day bookings use their actual stored start/end times for tooltip display
    // Morning = 00:00–11:59, Afternoon = 12:00–23:59 — no business hours override

    const statusConfig = settings.statusConfigs?.find((s) => String(s.id) === String(booking.status));
    const statusColor = statusConfig?.color || getDefaultStatusColor(booking.status);
    const statusText = statusConfig?.customText || statusConfig?.text || booking.status;

    // Helper to check if a specific field has showTooltip enabled (case-insensitive)
    const isFieldOnTooltip = (name: string): boolean => isFieldOnTooltipShared(fieldConfigurations, name);
    // Helper to get the display label (customName from booking type screen, or default fieldName)
    const getFieldLabel = (name: string): string => getTooltipFieldLabel(fieldConfigurations, name);

    const showStatus = isFieldOnTooltip('Status');
    const showFrom = isFieldOnTooltip('From');
    const showTo = isFieldOnTooltip('To');

    const tooltipFields = getDynamicTooltipFields(fieldConfigurations);

    // ── Booking type indicator ──
    const durationLabel = (() => {
      switch (booking.durationType) {
        case 'full-day': return '◼ Full Day';
        case 'half-day':
          return booking.halfDayPeriod === 'pm' ? '◑ Afternoon' : '◝ Morning';
        case 'half-day-am': return '◝ Morning';
        case 'half-day-pm': return '◑ Afternoon';
        case 'hourly': return '❰ Hourly';
        default: return '◼ Full Day';
      }
    })();

    // ── Recurrence info ──
    // Detect recurring via recurrenceGroupId (child instances may not carry recurrence themselves).
    // Try three sources: (1) inline field, (2) sibling in current view, (3) API-fetched cache.
    const isRecurring = !!booking.recurrenceGroupId;
    const recurrence = booking.recurrence
      || (isRecurring
        ? bookings.find((b: Booking) => b.recurrenceGroupId === booking.recurrenceGroupId && b.recurrence && b.recurrence.pattern !== 'none')?.recurrence
        : undefined)
      || (isRecurring && booking.recurrenceGroupId
        ? recurrenceSettingsCacheRef.current.get(booking.recurrenceGroupId)
        : undefined);
    const repeatSvg = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/><path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/></svg>`;
    const isFromDemand = isDemandBooking(booking);
    const layersSvg = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>`;
    const repeatTypeLabel = recurrence && recurrence.pattern !== 'none'
      ? recurrence.pattern.charAt(0).toUpperCase() + recurrence.pattern.slice(1)
      : '';

    let html = `<div style="padding: 6px 8px; max-width: 340px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">`;

    // ── Header section ──
    html += `<div style="font-weight: 700; font-size: 14px; color: #1f2937; line-height: 1.3;">${escapeHtml(header)}</div>`;
    // Sub-header: duration indicator + status + repeat
    html += `<div style="display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 6px;">`;
    html += `<span style="font-size: 11px; color: #6b7280;">${durationLabel}</span>`;
    if (showStatus && statusText) {
      html += `<span style="display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 9999px; font-size: 10px; font-weight: 500; background-color: ${statusColor}15; color: ${statusColor}; border: 1px solid ${statusColor}30;">`;
      html += `<span style="width: 5px; height: 5px; border-radius: 50%; background-color: ${statusColor};"></span>`;
      html += `${statusText}`;
      html += `</span>`;
    }
    if (isRecurring) {
      html += `<span style="display: inline-flex; align-items: center; gap: 3px; font-size: 11px; color: #6b7280;">${repeatSvg} ${repeatTypeLabel || 'Repeat'}</span>`;
    }
    if (isFromDemand) {
      html += `<span style="display: inline-flex; align-items: center; gap: 3px; font-size: 11px; color: #6b7280;">${layersSvg} Open Demand</span>`;
    }
    html += `</div>`;

    // ── Light horizontal ruler ──
    html += `<hr style="border: none; border-top: 1px solid #e5e7eb; margin: 6px 0;" />`;

    // ── Content section: dates ──
    // Times are always shown in 24-hour format (e.g. "09:00", "17:30")
    const isFullDay = booking.durationType === 'full-day';
    const isPlanPreviewBooking = !!(booking as any).isPlanPreview;
    const fmtTooltipTime = (d: Date) => format(d, 'HH:mm');
    // Full-day bookings store an exclusive end (e.g. next-day 00:00), so the raw
    // end date can read one day past the booking. Show the inclusive last day
    // by stepping back one millisecond before formatting the date.
    const fullDayEndDate = tooltipEnd && isValid(tooltipEnd)
      ? new Date(tooltipEnd.getTime() - 1)
      : tooltipEnd;

    if ((showFrom || showTo) && tooltipStart && isValid(tooltipStart)) {
      html += `<div style="display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: #6b7280;">`;
      if (isPlanPreviewBooking) {
        if (showFrom) {
          // A proposed day covers only the hours the planner found free, so show
          // that window rather than claiming a full day.
          const hasWindow = !isFullDay && tooltipEnd && isValid(tooltipEnd) && tooltipEnd > tooltipStart;
          const previewHours = hasWindow
            ? (tooltipEnd!.getTime() - tooltipStart.getTime()) / (1000 * 60 * 60)
            : 0;
          const previewDuration = hasWindow
            ? `${format(tooltipStart, 'HH:mm')} – ${format(tooltipEnd!, 'HH:mm')} (${previewHours % 1 === 0 ? previewHours : previewHours.toFixed(1)}h)`
            : 'Full day';

          html += `<div style="display: flex; align-items: center; gap: 6px;">`;
          html += `<span style="color: #9ca3af;">${getFieldLabel('From')}:</span>`;
          html += `<span>${format(tooltipStart, 'EEE, MMM d, yyyy')}</span>`;
          html += `</div>`;
          html += `<div style="display: flex; align-items: center; gap: 6px;">`;
          html += `<span style="color: #9ca3af;">Duration:</span>`;
          html += `<span>${previewDuration}</span>`;
          html += `</div>`;
        }
      } else {
        if (showFrom) {
          html += `<div style="display: flex; align-items: center; gap: 6px;">`;
          html += `<span style="color: #9ca3af;">${getFieldLabel('From')}:</span>`;
          html += `<span>${format(tooltipStart, 'EEE, MMM d, yyyy')}${isFullDay ? '' : ` at ${fmtTooltipTime(tooltipStart)}`}</span>`;
          html += `</div>`;
        }
        if (showTo && tooltipEnd && isValid(tooltipEnd)) {
          html += `<div style="display: flex; align-items: center; gap: 6px;">`;
          html += `<span style="color: #9ca3af;">${getFieldLabel('To')}:</span>`;
          html += `<span>${isFullDay ? format(fullDayEndDate as Date, 'EEE, MMM d, yyyy') : `${format(tooltipEnd, 'EEE, MMM d, yyyy')} at ${fmtTooltipTime(tooltipEnd)}`}</span>`;
          html += `</div>`;
        }
      }
      // ── Timezone line ── Shown below the To date when the timezone feature is
      // enabled and the booking has a timezone set. Reflects the booking's own
      // timezone, not the timezone currently selected on the planner header
      // (the header timezone is what the From/To times above are displayed in).
      if (enableTimezones && booking.timezone) {
        const tzEntry = TIMEZONES.find((tz) => tz.value === bookingTzTooltip);
        const tzLabel = tzEntry ? tzEntry.label : bookingTzTooltip;
        const tzAbbr = getTimezoneAbbreviation(
          bookingTzTooltip,
          isValidStartTime ? startTime as Date : new Date()
        );
        html += `<div style="display: flex; align-items: center; gap: 6px;">`;
        html += `<span style="color: #9ca3af;">Timezone:</span>`;
        html += `<span>${escapeHtml(tzLabel)}${tzAbbr ? ` (${escapeHtml(tzAbbr)})` : ''}</span>`;
        html += `</div>`;
      }
      html += `</div>`;
    }

    // ── Total hours for hourly bookings — only shown when From/To are on tooltip ──
    if ((showFrom || showTo) && booking.durationType === 'hourly' && tooltipStart && isValid(tooltipStart) && tooltipEnd && isValid(tooltipEnd)) {
      const tooltipResource = resources.find((r: Resource) => String(r.id) === String(booking.resourceId));
      const totalHours = computeCappedHourlyTotal(tooltipStart, tooltipEnd, tooltipResource, settings.globalWorkingProfile);
      if (totalHours > 0) {
        const displayHours = totalHours % 1 === 0 ? totalHours.toString() : totalHours.toFixed(1);
        const hoursLabel = Number(displayHours) === 1 ? 'hour' : 'hours';
        html += `<div style="margin: 8px 0;">`;
        html += `<div style="display: flex; align-items: center; gap: 6px; font-size: 12px; color: #6b7280;">`;
        html += `<span>❰</span>`;
        html += `<span>${displayHours} ${hoursLabel}</span>`;
        html += `</div>`;
        html += `</div>`;
      }
    }

    // ── Recurrence occurrence line ──
    if (isRecurring) {
      html += `<div style="display: flex; align-items: center; gap: 4px; font-size: 11px; color: #6b7280; margin-top: 6px;">`;
      html += `<span style="display: inline-flex; align-items: center;">${repeatSvg}</span>`;
      if (recurrence && recurrence.pattern !== 'none') {
        const intervalNum = recurrence.interval || 1;
        const capitalizeWord = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
        let patternText = '';
        if (recurrence.pattern === 'daily') {
          const label = intervalNum === 1 ? 'day' : `${intervalNum} days`;
          patternText = `Occurs every ${label}`;
        } else if (recurrence.pattern === 'weekly') {
          const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
          const dayNames = recurrence.daysOfWeek && recurrence.daysOfWeek.length > 0
            ? [...recurrence.daysOfWeek]
                .sort((a, b) => dayOrder.indexOf(a.toLowerCase()) - dayOrder.indexOf(b.toLowerCase()))
                .map(d => capitalizeWord(d))
                .join(', ')
            : '';
          const weekLabel = intervalNum === 1 ? 'week' : `${intervalNum} weeks`;
          patternText = dayNames
            ? `Occurs every ${weekLabel} on ${dayNames}`
            : `Occurs every ${weekLabel}`;
        } else if (recurrence.pattern === 'monthly') {
          const monthLabel = intervalNum === 1 ? 'month' : `${intervalNum} months`;
          if (recurrence.monthlyRecurrenceType === 'relative-weekday' && recurrence.relativeWeekday) {
            patternText = `Occurs every ${monthLabel} on the ${recurrence.relativeWeekday.occurrence} ${recurrence.relativeWeekday.weekday}`;
          } else if (recurrence.dayOfMonth) {
            patternText = `Occurs every ${monthLabel} on day ${recurrence.dayOfMonth}`;
          } else {
            patternText = `Occurs every ${monthLabel}`;
          }
        } else {
          const label = intervalNum === 1 ? recurrence.pattern : `${intervalNum} ${recurrence.pattern}s`;
          patternText = `Occurs every ${label}`;
        }
        let recurrenceEndLabel = '';
        if (recurrence.endType === 'on-date' && recurrence.endDate) {
          try {
            const endDt = new Date(recurrence.endDate);
            if (isValid(endDt)) {
              recurrenceEndLabel = ` until ${format(endDt, 'EEE, MMM d, yyyy')}`;
            }
          } catch { /* ignore */ }
        } else if (recurrence.endType === 'after' && recurrence.occurrences) {
          recurrenceEndLabel = ` for ${recurrence.occurrences} occurrences`;
        }
        html += `<span>${patternText}${recurrenceEndLabel}</span>`;
      } else {
        html += `<span>Recurring booking</span>`;
      }
      html += `</div>`;
    }

    if (tooltipFields.length > 0) {
      html += `<div style="border-top: 1px solid #f3f4f6; padding-top: 6px; margin-top: 6px;">`;
      for (const field of tooltipFields) {
        const fieldValue = getFieldValue(field.fieldName);
        if (fieldValue) {
          const fieldLabel = field.customName || field.fieldName;
          html += `<div style="display: flex; gap: 8px; font-size: 11px; margin-bottom: 3px; line-height: 1.4; align-items: center;">`;
          html += `<span style="color: #9ca3af; min-width: 90px; flex-shrink: 0;">${escapeHtml(fieldLabel)}:</span>`;
          if (isColourField(field.fieldName)) {
            html += `<span style="display: inline-block; width: 14px; height: 14px; border-radius: 4px; background-color: ${escapeHtml(fieldValue)}; flex-shrink: 0;" title="${escapeHtml(fieldValue)}"></span>`;
          } else {
            html += `<span style="color: #374151; flex: 1; word-break: break-word;">${escapeHtml(fieldValue)}</span>`;
          }
          html += `</div>`;
        }
      }
      html += `</div>`;
    }

    // ── Resource avatar(s) section ──
    const allResourceIds: string[] = [];
    if (booking.resourceIds && booking.resourceIds.length > 0) {
      allResourceIds.push(...booking.resourceIds);
    } else if (booking.resourceId) {
      allResourceIds.push(String(booking.resourceId));
    }

    const matchedResources = allResourceIds
      .map((rid) => resources.find((r) => String(r.id) === String(rid)))
      .filter(Boolean) as Resource[];

    if (matchedResources.length > 0) {
      const MAX_VISIBLE = 5;
      const visible = matchedResources.slice(0, MAX_VISIBLE);
      const overflow = matchedResources.length - MAX_VISIBLE;
      const isMulti = matchedResources.length > 1;
      const avatarSize = 26;
      const tealBg = 'oklch(50% .0896 194.8)';

      html += `<div style="border-top: 1px solid #f3f4f6; padding-top: 6px; margin-top: 6px;">`;

      if (isMulti) {
        html += `<div style="font-size: 11px; color: #9ca3af; margin-bottom: 4px; display: flex; align-items: center; gap: 4px;">`;
        html += `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`;
        html += `<span>${matchedResources.length} Resources</span>`;
        html += `</div>`;
      }

      const tooltipId = `res-overflow-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      const renderAvatar = (res: Resource) => {
        const resInitials = (() => {
          const parts = res.name.trim().split(/\s+/);
          const first = parts[0]?.[0] || '';
          const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
          return (first + last).toUpperCase();
        })();
        const hasResPhoto = !!res.photoUrl && res.photoUrl.trim() !== '' && res.photoUrl !== 'null' && res.photoUrl !== 'undefined';
        const resNameEsc = escapeHtml(res.name);
        const resInitialsEsc = escapeHtml(resInitials);
        const resPhotoEsc = escapeHtml(res.photoUrl || '');
        let avatarHtml = '';
        if (hasResPhoto) {
          avatarHtml += `<div title="${resNameEsc}" style="width: ${avatarSize}px; height: ${avatarSize}px; border-radius: 6px; overflow: hidden; flex-shrink: 0;">`;
          avatarHtml += `<img src="${resPhotoEsc}" alt="${resNameEsc}" style="width: 100%; height: 100%; object-fit: cover; display: block;" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />`;
          avatarHtml += `<div style="width: ${avatarSize}px; height: ${avatarSize}px; border-radius: 6px; display: none; align-items: center; justify-content: center; background: ${tealBg}; color: rgba(255,255,255,0.9); font-weight: 600; font-size: 10px; letter-spacing: 0.5px;">${resInitialsEsc}</div>`;
          avatarHtml += `</div>`;
        } else {
          avatarHtml += `<div title="${resNameEsc}" style="width: ${avatarSize}px; height: ${avatarSize}px; border-radius: 6px; display: flex; align-items: center; justify-content: center; background: ${tealBg}; color: rgba(255,255,255,0.9); font-weight: 600; font-size: 10px; letter-spacing: 0.5px; flex-shrink: 0;">${resInitialsEsc}</div>`;
        }
        return avatarHtml;
      };

      html += `<div style="display: flex; align-items: center; gap: 4px; flex-wrap: wrap;">`;
      for (const res of visible) {
        html += renderAvatar(res);
      }

      if (overflow > 0) {
        const hiddenResources = matchedResources.slice(MAX_VISIBLE);
        // Expand button (visible by default)
        html += `<div id="${tooltipId}-expand" title="Show ${overflow} more resource${overflow > 1 ? 's' : ''}" onclick="event.stopPropagation(); document.getElementById('${tooltipId}-hidden').style.display='contents'; document.getElementById('${tooltipId}-expand').style.display='none'; document.getElementById('${tooltipId}-collapse').style.display='flex';" style="width: ${avatarSize}px; height: ${avatarSize}px; border-radius: 6px; display: flex; align-items: center; justify-content: center; background: ${tealBg}; color: rgba(255,255,255,0.9); font-weight: 600; font-size: 10px; flex-shrink: 0; cursor: pointer; transition: opacity 0.15s;" onmouseover="this.style.opacity='0.8'" onmouseout="this.style.opacity='1'">+${overflow}</div>`;
        // Hidden overflow avatars container
        html += `<span id="${tooltipId}-hidden" style="display: none; gap: 4px;">`;
        for (const res of hiddenResources) {
          html += renderAvatar(res);
        }
        html += `</span>`;
        // Collapse button (hidden by default)
        html += `<div id="${tooltipId}-collapse" title="Show fewer" onclick="event.stopPropagation(); document.getElementById('${tooltipId}-hidden').style.display='none'; document.getElementById('${tooltipId}-expand').style.display='flex'; document.getElementById('${tooltipId}-collapse').style.display='none';" style="width: ${avatarSize}px; height: ${avatarSize}px; border-radius: 6px; display: none; align-items: center; justify-content: center; background: ${tealBg}; color: rgba(255,255,255,0.9); font-weight: 600; font-size: 10px; flex-shrink: 0; cursor: pointer; transition: opacity 0.15s;" onmouseover="this.style.opacity='0.8'" onmouseout="this.style.opacity='1'">&minus;</div>`;
      }

      if (!isMulti) {
        html += `<span style="font-size: 12px; font-weight: 500; color: #374151; margin-left: 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${escapeHtml(matchedResources[0].name)}</span>`;
      }

      html += `</div>`;

      if (isMulti) {
        html += `<div style="font-size: 10px; color: #9ca3af; margin-top: 4px; line-height: 1.3; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 320px;">`;
        html += matchedResources.map((r) => escapeHtml(r.name)).join(', ');
        html += `</div>`;
      }

      html += `</div>`;
    }

    html += `</div>`;
    return html;
  }, [settings.bookingTypes, settings.statusConfigs, settings.timelineDefaultTimezone, settings.businessHoursStart, settings.businessHoursEnd, projects, projectDetails, clients, costCentres, resources, selectedTimezone, enableTimezones, bookings]);

  const generateAbsenceTooltipHtml = useCallback((eventData: BryntumEvent): string => {
    const absenceColor = settings.absenceColor || '#94a3b8';
    // Show the same text on the tooltip as the one rendered on the booking bar
    // (see eventRenderer absence branch: eventRecord.data?.name || 'Annual Leave').
    const absenceLabel = eventData.name || 'Annual Leave';

    // Resolve resource from composite IDs (e.g. role-resource or project-resource grouping)
    const rawResourceId = String(eventData.resourceId || '');
    const parts = rawResourceId.split('-');
    const baseResourceId = parts.length > 1 ? parts[parts.length - 1] : rawResourceId;
    const resource = resources.find(r => String(r.id) === baseResourceId);
    const resourceName = resource?.name || '';

    // Look up the original absence for the reason field
    const absenceId = eventData.absenceId != null
      ? String(eventData.absenceId)
      : String(eventData.id || '').replace('absence-evt-', '');
    const absence = absences.find(a => String(a.id) === absenceId);

    const startDate = eventData.startDate instanceof Date ? eventData.startDate : new Date(eventData.startDate as unknown as string);
    const endDate = eventData.endDate instanceof Date ? eventData.endDate : new Date(eventData.endDate as unknown as string);

    let html = `<div style="padding: 6px 8px; max-width: 300px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">`;

    html += `<div style="display: flex; align-items: center; gap: 8px;">`;
    html += `<div style="width: 10px; height: 10px; border-radius: 2px; background-color: ${absenceColor}; border: 1px dashed rgba(255,255,255,0.4); flex-shrink: 0;"></div>`;
    html += `<div style="font-weight: 700; font-size: 14px; color: #1f2937;">${escapeHtml(absenceLabel)}</div>`;
    html += `</div>`;

    if (absence?.reason) {
      html += `<div style="font-size: 12px; color: #6b7280; margin-top: 4px;">${escapeHtml(absence.reason)}</div>`;
    }

    html += `<hr style="border: none; border-top: 1px solid #e5e7eb; margin: 6px 0;" />`;

    html += `<div style="display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: #6b7280;">`;
    if (isValid(startDate)) {
      html += `<div style="display: flex; align-items: center; gap: 6px;">`;
      html += `<span style="color: #9ca3af;">Start:</span>`;
      html += `<span>${format(startDate, 'EEE, MMM d, yyyy')}</span>`;
      html += `</div>`;
    }
    if (isValid(endDate)) {
      html += `<div style="display: flex; align-items: center; gap: 6px;">`;
      html += `<span style="color: #9ca3af;">End:</span>`;
      html += `<span>${format(endDate, 'EEE, MMM d, yyyy')}</span>`;
      html += `</div>`;
    }
    html += `</div>`;

    if (resourceName) {
      html += `<hr style="border: none; border-top: 1px solid #e5e7eb; margin: 6px 0;" />`;
      const resInitials = (() => {
        const nameParts = resourceName.trim().split(/\s+/);
        const first = nameParts[0]?.[0] || '';
        const last = nameParts.length > 1 ? nameParts[nameParts.length - 1]?.[0] || '' : '';
        return (first + last).toUpperCase();
      })();
      const tealBg = 'oklch(50% .0896 194.8)';
      html += `<div style="display: flex; align-items: center; gap: 6px;">`;
      if (resource?.photoUrl && resource.photoUrl.trim() !== '' && resource.photoUrl !== 'null' && resource.photoUrl !== 'undefined') {
        html += `<img src="${escapeHtml(resource.photoUrl)}" alt="${escapeHtml(resourceName)}" style="width: 24px; height: 24px; border-radius: 6px; object-fit: cover;" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />`;
        html += `<div style="width: 24px; height: 24px; border-radius: 6px; display: none; align-items: center; justify-content: center; background: ${tealBg}; color: rgba(255,255,255,0.9); font-weight: 600; font-size: 10px;">${escapeHtml(resInitials)}</div>`;
      } else {
        html += `<div style="width: 24px; height: 24px; border-radius: 6px; display: flex; align-items: center; justify-content: center; background: ${tealBg}; color: rgba(255,255,255,0.9); font-weight: 600; font-size: 10px;">${escapeHtml(resInitials)}</div>`;
      }
      html += `<span style="font-size: 12px; font-weight: 500; color: #374151;">${escapeHtml(resourceName)}</span>`;
      html += `</div>`;
    }

    html += `</div>`;
    return html;
  }, [resources, absences, settings.absenceColor]);

  const generateTooltipHtmlRef = useRef(generateTooltipHtml);
  generateTooltipHtmlRef.current = generateTooltipHtml;
  const generateAbsenceTooltipHtmlRef = useRef(generateAbsenceTooltipHtml);
  generateAbsenceTooltipHtmlRef.current = generateAbsenceTooltipHtml;
  const generateHeatmapTooltipHtmlRef = useRef(generateHeatmapTooltipHtml);
  generateHeatmapTooltipHtmlRef.current = generateHeatmapTooltipHtml;
  const findBookingByIdRef = useRef(findBookingById);
  findBookingByIdRef.current = findBookingById;
  // Latest set of locked (mid-deletion) booking ids for use inside effect-bound handlers.
  const lockedBookingIdsRef = useRef(lockedBookingIds);
  lockedBookingIdsRef.current = lockedBookingIds;

  const isDateInAbsence = useCallback((resourceId: string, date: Date): boolean => {
    // Resolve base resource ID from composite grouping IDs
    const parts = resourceId.split('-');
    const baseId = parts.length > 1 ? parts[parts.length - 1] : resourceId;
    return absences.some(absence => {
      if (String(absence.resourceId) !== baseId) return false;
      const absStart = new Date(absence.startDate);
      const absEnd = new Date(absence.endDate);
      // Ensure end covers the full day
      if (absEnd.getHours() === 0 && absEnd.getMinutes() === 0) {
        absEnd.setHours(23, 59, 59, 999);
      }
      return date >= absStart && date <= absEnd;
    });
  }, [absences]);

  const isDateInAbsenceRef = useRef(isDateInAbsence);
  isDateInAbsenceRef.current = isDateInAbsence;

  const handleEventClick = useCallback((
    { eventRecord, event }: { eventRecord: { data: BryntumEvent }; event?: MouseEvent },
  ) => {
    if (eventRecord.data?.isAbsenceEvent) return;
    // Ctrl/Cmd-click builds up a selection (Bryntum's own multiEventSelect) rather
    // than opening the booking, so several bookings can be picked and then deleted
    // together from the right-click menu.
    if (event?.ctrlKey || event?.metaKey) return;
    const booking = findBookingById(eventRecord.data.id);
    if (booking && onEditBooking) {
      onEditBooking(booking, eventRecord.data.resourceId);
    }
  }, [findBookingById, onEditBooking]);

  const handleEventDblClick = useCallback(({ eventRecord }: { eventRecord: { data: BryntumEvent } }) => {
    if (eventRecord.data?.isAbsenceEvent) return false;
    const booking = findBookingById(eventRecord.data.id);
    if (booking && onEditBooking) {
      onEditBooking(booking, eventRecord.data.resourceId);
    }
    // Return false to prevent Bryntum's default event edit behavior
    return false;
  }, [findBookingById, onEditBooking]);

  const handleScheduleClick = useCallback(({ resourceRecord, date }: { resourceRecord: { data: BryntumResource }; date: Date }) => {
    const id = resourceRecord.data.id as unknown as string;
    // Block interactions on grouping headers (group-, project-, role-, customer-)
    if (id?.startsWith('group-') || id?.startsWith('project-') || id?.startsWith('role-') || id?.startsWith('customer-')) return false as unknown as void;

    // Block booking creation on cells covered by an absence
    if (isDateInAbsence(id, date)) return;

    const resource = findResourceById(id);
    if (resource) {
      // Block booking creation on non-working days.
      // Skipped in Quarter/Year views where a cell spans many days: the click date
      // is the 1st of the period and may land on a weekend, which must not block create.
      if (!isCoarseGranularityView && isNonWorkingDay(resource, date)) return;
      // Block booking creation during non-business hours (Day view only)
      if (isNonBusinessHour(date)) return;

      if (onSlotClick) {
        // Extract projectId from combined ID in project-resource mode
        // Combined IDs are in format: "projectId-resourceId"
        let bookingData: Partial<Booking> | undefined;
        const combinedId = id;
        if (groupingMode === 'project-resource' && combinedId.includes('-') && !combinedId.startsWith('group-') && !combinedId.startsWith('project-')) {
          const parts = combinedId.split('-');
          if (parts.length >= 2) {
            const projectId = parts[0];
            bookingData = { projectId };
          }
        }
        const customerName = extractCustomerName(combinedId);
        if (customerName) {
          bookingData = { ...bookingData, customer: customerName };
        }

        onSlotClick(resource, date, { hour: date.getHours(), minute: date.getMinutes() }, bookingData);
      }
    }
  }, [findResourceById, onSlotClick, groupingMode, extractCustomerName, isNonWorkingDay, isNonBusinessHour, isDateInAbsence, isCoarseGranularityView]);

  const handleScheduleDblClick = useCallback(({ resourceRecord, eventRecord, date }: { resourceRecord: { data: BryntumResource }; date: Date, eventRecord: { startDate: Date; endDate: Date; remove?: () => void }; }) => {
    const id = resourceRecord.data.id as unknown as string;
    // Block interactions on grouping headers (group-, project-, role-, customer-)
    if (id?.startsWith('group-') || id?.startsWith('project-') || id?.startsWith('role-') || id?.startsWith('customer-')) return false as unknown as void;

    // Block booking creation on cells covered by an absence
    if (isDateInAbsence(id, date)) return false as unknown as void;

    const resource = findResourceById(id);
    if (resource) {
      // Block booking creation on non-working days.
      // Skipped in Quarter/Year views (see handleScheduleClick) where a cell spans many days.
      if (!isCoarseGranularityView && isNonWorkingDay(resource, date)) return false as unknown as void;
      // Block booking creation during non-business hours (Day view only)
      if (isNonBusinessHour(date)) return false as unknown as void;
    }
    if (resource && onDragSelect) {
      cleanupPhantomEvents();
      let endTime: Date;

      // For Year view, set end time to the last day of the clicked month
      if (calendarView === 'year') {
        endTime = new Date(date.getFullYear(), date.getMonth() + 1, 0); // Last day of month
      } else {
        endTime = new Date(date);
        endTime.setHours(endTime.getHours() + 1);
      }

      // Extract projectId from combined ID in project-resource mode
      // Combined IDs are in format: "projectId-resourceId"
      let bookingData: Partial<Booking> | undefined;
      const combinedId = resourceRecord.data.id;
      if (groupingMode === 'project-resource' && combinedId.includes('-') && !combinedId.startsWith('group-') && !combinedId.startsWith('project-')) {
        const parts = combinedId.split('-');
        if (parts.length >= 2) {
          const projectId = parts[0];
          bookingData = { projectId };
        }
      }
      const customerName = extractCustomerName(combinedId);
      if (customerName) {
        bookingData = { ...bookingData, customer: customerName };
      }

      // DYNAMIC GRANULARITY DETECTION:
      // Determine durationType based on the timeline's zoom level and calendar view.
      // This ensures the booking form opens with the correct duration type based on
      // what the user sees on the timeline, not just the nominal view mode.

      // Determine if the timeline is showing hourly granularity based on calendarView and zoomLevel:
      // - Day view: always hourly (zoomLevel 0 = hourly, zoomLevel 1 = 15-min)
      // - Week/Month view: zoomLevel 0 = daily, zoomLevel 1-4 = hourly
      // - Quarter view: weekly granularity (not hourly)
      // - Year view: monthly granularity (not hourly)
      const isHourlyGranularity =
        calendarView === 'day' || // Day view is always hourly
        ((calendarView === 'week' || calendarView === 'month') && zoomLevel >= 1); // Week/Month zoomed in

      if (isHourlyGranularity) {
        bookingData = { ...bookingData, durationType: 'hourly' };
      }

      // Clean up any remaining phantom events as a safety net
      cleanupPhantomEvents();

      // Otherwise, let the handler decide based on the view type (will default to 'full-day')

      onDragSelect(
        resource,
        date,
        { hour: date.getHours(), minute: date.getMinutes() },
        { hour: endTime.getHours(), minute: endTime.getMinutes() },
        bookingData,
        endTime
      );
    }
    // Always block Bryntum's default create-on-dblclick
    return false as unknown as void;
  }, [findResourceById, onDragSelect, calendarView, zoomLevel, groupingMode, extractCustomerName, isNonWorkingDay, isNonBusinessHour, isDateInAbsence, isCoarseGranularityView]);

  const handleEventDrop = useCallback(({ eventRecords, context }: { eventRecords: Array<{ data: BryntumEvent }>; context: { newResource?: { data: BryntumResource }; startDate: Date; endDate: Date } }) => {
    if (eventRecords.length === 0) return;

    const eventRecord = eventRecords[0];
    const booking = findBookingById(eventRecord.data.id);
    // Use booking.resourceId from React state (not updated yet) to get the ORIGINAL resource
    // eventRecord.data.resourceId might have been updated by Bryntum after the drag
    const originalResource = booking ? findResourceById(booking.resourceId ?? "") : null;
    const newResourceId = context.newResource?.data.id || eventRecord.data.resourceId;

    // VALIDATION: Prevent dropping bookings on group/parent nodes
    // Group nodes have IDs that start with "group-" (e.g., "group-Department Name")
    // or "project-" (e.g., "project-123" for project groups in project-resource mode)
    // or "role-" (e.g., "role-Developer" for role groups in role-resource mode)
    // Only actual resources (leaf nodes) should accept bookings
    const isGroupNode = newResourceId.startsWith('group-') || newResourceId.startsWith('project-') || newResourceId.startsWith('role-') || newResourceId.startsWith('customer-');
    if (isGroupNode) {

      // Revert the booking to its original position
      if (booking && originalResource) {
        const originalStartTime = new Date(booking.startTime);
        const originalEndTime = new Date(booking.endTime);

        // Determine the correct resource ID to revert to based on grouping mode
        let revertResourceId: string;
        if (groupingMode === 'project-resource' && booking.projectId) {
          revertResourceId = `${booking.projectId}-${originalResource.id}`;
        } else if (groupingMode === 'role-resource') {
          const roleName = originalResource.role || 'Unassigned';
          revertResourceId = `${roleName}-${originalResource.id}`;
        } else if (groupingMode === 'customer') {
          let custName: string = booking.customer?.trim() || '';
          if (!custName && booking.projectId) {
            const project = projectDetails.find(p => String(p.id) === String(booking.projectId));
            custName = project?.clientName?.trim() || '';
          }
          if (!custName) custName = 'Unassigned';
          revertResourceId = `${custName}-${originalResource.id}`;
        } else {
          revertResourceId = originalResource.id;
        }

        // Use the exposed revertEvent method to restore the booking
        const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
        if (scheduler) {
          const eventStore = scheduler.eventStore;
          const eventToRevert = eventStore.getById(booking.id);

          if (eventToRevert) {
            eventToRevert.beginBatch();
            (eventToRevert as any).startDate = originalStartTime;
            (eventToRevert as any).endDate = originalEndTime;
            (eventToRevert as any).resourceId = revertResourceId;
            eventToRevert.endBatch();
          }
        }
      }

      // Return early without calling onBookingDragEnd
      return;
    }

    // In project-resource or role-resource mode, extract the actual resource ID from the combined ID
    // Combined IDs are in format: "projectId-resourceId" or "roleName-resourceId"
    let actualNewResourceId = newResourceId;
    if (groupingMode === 'project-resource' && newResourceId.includes('-') && !newResourceId.startsWith('group-')) {
      // Extract the resource ID from the combined project-resource ID
      const parts = newResourceId.split('-');
      if (parts.length >= 2) {
        // The resource ID is everything after the first part (project ID)
        actualNewResourceId = parts.slice(1).join('-');
      }
    } else if (groupingMode === 'role-resource' && newResourceId.includes('-') && !newResourceId.startsWith('group-') && !newResourceId.startsWith('role-')) {
      // Extract the resource ID from the combined role-resource ID
      const parts = newResourceId.split('-');
      if (parts.length >= 2) {
        // The resource ID is everything after the first part (role name)
        actualNewResourceId = parts.slice(1).join('-');
      }
    } else if (groupingMode === 'customer' && newResourceId.includes('-') && !newResourceId.startsWith('group-') && !newResourceId.startsWith('customer-')) {
      // Extract the resource ID from the combined customer-resource ID
      // Try matching by suffix first (handles hyphens in customer names)
      const matchedResource = resources.find((r: Resource) => {
        const rid = String(r.id);
        return newResourceId.endsWith(`-${rid}`);
      });
      if (matchedResource) {
        actualNewResourceId = String(matchedResource.id);
      } else {
        // Fallback: everything after first hyphen
        const dashIdx = newResourceId.indexOf('-');
        if (dashIdx !== -1) actualNewResourceId = newResourceId.slice(dashIdx + 1);
      }
    }

    // VALIDATION: Prevent dropping bookings on non-working days
    // Check if the target date falls on a non-working day for the target resource
    const targetResource = findResourceById(actualNewResourceId);
    if (targetResource && booking && originalResource) {
      const targetDate = context.startDate;

      // Check non-working day first
      if (isNonWorkingDay(targetResource, targetDate)) {

        // Revert the booking to its original position
        const originalStartTime = new Date(booking.startTime);
        const originalEndTime = new Date(booking.endTime);

        // Determine the correct resource ID to revert to based on grouping mode
        let revertResourceId: string;
        if (groupingMode === 'project-resource' && booking.projectId) {
          revertResourceId = `${booking.projectId}-${originalResource.id}`;
        } else if (groupingMode === 'role-resource') {
          const roleName = originalResource.role || 'Unassigned';
          revertResourceId = `${roleName}-${originalResource.id}`;
        } else {
          revertResourceId = originalResource.id;
        }

        // Use the scheduler to restore the booking
        const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
        if (scheduler) {
          const eventStore = scheduler.eventStore;
          const eventToRevert = eventStore.getById(booking.id);

          if (eventToRevert) {
            eventToRevert.beginBatch();
            (eventToRevert as any).startDate = originalStartTime;
            (eventToRevert as any).endDate = originalEndTime;
            (eventToRevert as any).resourceId = revertResourceId;
            eventToRevert.endBatch();
          }
        }

        // Return early without calling onBookingDragEnd
        return;
      }

      // VALIDATION: Prevent dropping bookings during non-business hours (Day view)
      if (isNonBusinessHour(targetDate)) {

        // Revert the booking to its original position
        const originalStartTime = new Date(booking.startTime);
        const originalEndTime = new Date(booking.endTime);

        let revertResourceId: string;
        if (groupingMode === 'project-resource' && booking.projectId) {
          revertResourceId = `${booking.projectId}-${originalResource.id}`;
        } else if (groupingMode === 'role-resource') {
          const roleName = originalResource.role || 'Unassigned';
          revertResourceId = `${roleName}-${originalResource.id}`;
        } else {
          revertResourceId = originalResource.id;
        }

        const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
        if (scheduler) {
          const eventStore = scheduler.eventStore;
          const eventToRevert = eventStore.getById(booking.id);
          if (eventToRevert) {
            eventToRevert.beginBatch();
            (eventToRevert as any).startDate = originalStartTime;
            (eventToRevert as any).endDate = originalEndTime;
            (eventToRevert as any).resourceId = revertResourceId;
            eventToRevert.endBatch();
          }
        }

        return;
      }
    }

    if (booking && originalResource && onBookingDragEnd) {
      // Reverse the display-only timezone shift applied when rendering the event
      // so the dates passed downstream are back in the stored (source) timezone.
      const sourceTz = (booking as any).timezone || 'Europe/London';
      const targetTz = selectedTimezone || sourceTz;
      const offsetMin = enableTimezones ? getTimezoneOffset(sourceTz, targetTz, context.startDate) : 0;
      const correctedStart = new Date(context.startDate.getTime() - offsetMin * 60000);
      const correctedEnd = new Date(context.endDate.getTime() - offsetMin * 60000);
      setTimeout(() => {
        onBookingDragEnd(
          booking,
          originalResource,
          actualNewResourceId,
          correctedStart,
          correctedEnd,
          false
        );
      }, 0);
    }
  }, [findBookingById, findResourceById, onBookingDragEnd, groupingMode, isNonWorkingDay, isNonBusinessHour, enableTimezones, selectedTimezone]);

  const handleEventResizeEnd = useCallback(({ eventRecord }: { eventRecord: { data: BryntumEvent; startDate: Date; endDate: Date } }) => {
    const booking = findBookingById(eventRecord.data.id);
    const resource = findResourceById(eventRecord.data.resourceId);

    if (booking && resource && onBookingDragEnd) {
      // Reverse the display-only timezone shift applied when rendering the event
      // so the dates passed downstream are back in the stored (source) timezone.
      const resizeStart = (eventRecord as any).startDate as Date;
      const resizeEnd = (eventRecord as any).endDate as Date;
      const sourceTz = (booking as any).timezone || 'Europe/London';
      const targetTz = selectedTimezone || sourceTz;
      const offsetMin = enableTimezones ? getTimezoneOffset(sourceTz, targetTz, resizeStart) : 0;
      const correctedStart = new Date(resizeStart.getTime() - offsetMin * 60000);
      const correctedEnd = new Date(resizeEnd.getTime() - offsetMin * 60000);
      setTimeout(() => {
        onBookingDragEnd(
          booking,
          resource,
          booking.resourceId ?? "",
          correctedStart,
          correctedEnd,
          true
        );
      }, 0);
    }
  }, [findBookingById, findResourceById, onBookingDragEnd, groupingMode, enableTimezones, selectedTimezone]);

  const handleDragCreateEnd = useCallback(({ resourceRecord, eventRecord, context }: { resourceRecord: { data: BryntumResource }; eventRecord: { startDate: Date; endDate: Date; remove?: () => void }; context?: { finalize?: (createEvent: boolean) => void } }) => {
    const id = resourceRecord.data.id as unknown as string;
    // Block drag-create on grouping headers (group-, project-, role-, customer-)
    if (id?.startsWith('group-') || id?.startsWith('project-') || id?.startsWith('role-') || id?.startsWith('customer-')) {
      if (context?.finalize) context.finalize(false);
      if (eventRecord.remove) eventRecord.remove();
      cleanupPhantomEvents();
      return false as unknown as void;
    }

    const resource = findResourceById(id);

    // Block drag-create if the dragged range includes any non-working day.
    // Skipped in Quarter/Year views where a single cell spans many days (would always match a weekend).
    if (resource && !isCoarseGranularityView) {
      const dragStart = (eventRecord as any).startDate as Date;
      const dragEnd = (eventRecord as any).endDate as Date;
      // Bryntum's endDate is exclusive (a Mon→Fri drag ends at Sat 00:00), so step
      // back one millisecond to get the last instant actually covered by the drag.
      const lastCoveredInstant = new Date(dragEnd.getTime() - 1);
      const lastCoveredDay = new Date(lastCoveredInstant.getFullYear(), lastCoveredInstant.getMonth(), lastCoveredInstant.getDate());
      const checkDate = new Date(dragStart.getFullYear(), dragStart.getMonth(), dragStart.getDate());
      while (checkDate <= lastCoveredDay) {
        if (isNonWorkingDay(resource, checkDate)) {
          if (context?.finalize) context.finalize(false);
          if (eventRecord.remove) eventRecord.remove();
          cleanupPhantomEvents();
          return false as unknown as void;
        }
        checkDate.setDate(checkDate.getDate() + 1);
      }
    }

    if (resource && onDragSelect) {
      const startDate = (eventRecord as any).startDate;



      let endDate = (eventRecord as any).endDate;

      // For non-day views, Bryntum snaps to the START of the next period:
      // - Week/Month views (day snapping): Mon 00:00 → Tue 00:00 for single day
      // - Quarter view (week snapping): Mon 00:00 → next Mon 00:00 for single week
      // - Year view (month snapping): Jan 1 00:00 → Feb 1 00:00 for single month
      // We subtract one day from endDate to get the actual last day of the booking
      if (calendarView !== 'day') {
        if (endDate.getHours() === 0 && endDate.getMinutes() === 0) {
          const adjustedEndDate = new Date(endDate);
          adjustedEndDate.setDate(adjustedEndDate.getDate() - 1);
          endDate = adjustedEndDate;
        }
      }

      // Extract projectId from combined ID in project-resource mode
      // Combined IDs are in format: "projectId-resourceId"
      let bookingData: Partial<Booking> | undefined;
      const combinedId = resourceRecord.data.id;
      if (groupingMode === 'project-resource' && combinedId.includes('-') && !combinedId.startsWith('group-') && !combinedId.startsWith('project-')) {
        const parts = combinedId.split('-');
        if (parts.length >= 2) {
          const projectId = parts[0];
          bookingData = { projectId };
        }
      }
      const customerName = extractCustomerName(combinedId);
      if (customerName) {
        bookingData = { ...bookingData, customer: customerName };
      }

      // DYNAMIC GRANULARITY DETECTION:
      // Determine durationType based on the ACTUAL timeline granularity (from viewPreset)
      // rather than just the nominal view mode. This allows for zoom-aware pre-selection.

      // Get the actual time resolution from the current viewPreset
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      const currentViewPreset = scheduler?.viewPreset as { timeResolution?: { unit?: string; increment?: number } } | undefined;
      const timeResolutionUnit = currentViewPreset?.timeResolution?.unit;

      // Determine if we should use hourly duration type:
      // Only when the timeline is displaying hourly granularity (hour or minute units)
      // Cross-day drag in day-level views (month, week, quarter) should remain full-day
      const isHourlyGranularity = timeResolutionUnit === 'hour' || timeResolutionUnit === 'minute';

      if (isHourlyGranularity) {
        bookingData = { ...bookingData, durationType: 'hourly' };
      }
      // Otherwise, let the handler decide based on the view type (will default to 'full-day')

      onDragSelect(
        resource,
        startDate,
        { hour: startDate.getHours(), minute: startDate.getMinutes() },
        { hour: endDate.getHours(), minute: endDate.getMinutes() },
        bookingData,
        endDate
      );
    }
    // Prevent Bryntum from finalizing the event creation
    // This tells Bryntum not to add the temporary event to the store
    if (context?.finalize) {
      context.finalize(false);
    }
    // Also explicitly remove the temporary event if it was added
    // This ensures the event is removed even if finalize doesn't work as expected
    if (eventRecord.remove) {
      eventRecord.remove();
    }
    // Clean up any remaining phantom events as a safety net
    cleanupPhantomEvents();
    // Return false to prevent the event from being added
    return false;
  }, [findResourceById, onDragSelect, calendarView, zoomLevel, groupingMode, cleanupPhantomEvents, isNonWorkingDay, isNonBusinessHour, isCoarseGranularityView]);

  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    // Validate dates before setting time span
    const isStartValid = startDate instanceof Date && !isNaN(startDate.getTime());
    const isEndValid = endDate instanceof Date && !isNaN(endDate.getTime());
    const isOrderValid = isStartValid && isEndValid && startDate.getTime() < endDate.getTime();

    if (isOrderValid) {
      scheduler.setTimeSpan(startDate, endDate);
    } else {
      console.error('❌ Cannot set time span: invalid date range', {
        startDate: startDate?.toString(),
        endDate: endDate?.toString(),
        reason: !isStartValid ? 'Invalid start' : !isEndValid ? 'Invalid end' : 'Start >= End'
      });
    }
  }, [startDate, endDate]);


  // Attach click handler to expand/collapse all button
  useEffect(() => {
    const handleExpandCollapseClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const button = target.closest('#expand-collapse-all-btn');
      if (button) {
        e.preventDefault();
        e.stopPropagation();
        toggleAllGroups();
      }
    };

    document.addEventListener('click', handleExpandCollapseClick, true);
    return () => {
      document.removeEventListener('click', handleExpandCollapseClick, true);
    };
  }, [toggleAllGroups]);

  // Sync the global expand/collapse button when individual groups are toggled.
  // Defers the check to requestAnimationFrame so Bryntum has finished updating
  // the record's expanded property before we read it.
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler || scheduler.isDestroyed) return;

    let rafId = 0;
    const deferredSync = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => syncGlobalExpandState());
    };

    type Detacher = (() => void) | { destroy?: () => void } | undefined;
    const detachers: Detacher[] = [];
    const cleanup = (d: Detacher) => {
      if (typeof d === 'function') d();
      else if (d && typeof d.destroy === 'function') d.destroy();
    };

    // Listen on the resource store for tree-toggle events
    const store = scheduler.resourceStore as unknown as {
      on?: (event: string, handler: () => void) => Detacher;
    };
    if (store.on) {
      detachers.push(store.on('toggleNode', deferredSync));
    }

    // Also listen on the Tree feature for expand/collapse events
    const tree = (scheduler as unknown as {
      features?: { tree?: { on?: (event: string, handler: () => void) => Detacher } };
    }).features?.tree;
    if (tree?.on) {
      detachers.push(tree.on('collapseNode', deferredSync));
      detachers.push(tree.on('expandNode', deferredSync));
    }

    // Fallback: listen for the scheduler's own renderRows / refresh event
    const schedulerOn = (scheduler as unknown as {
      on?: (event: string, handler: () => void) => Detacher;
    }).on;
    if (schedulerOn) {
      detachers.push(schedulerOn.call(scheduler, 'renderRows', deferredSync));
    }

    return () => {
      cancelAnimationFrame(rafId);
      detachers.forEach(cleanup);
    };
  }, [syncGlobalExpandState]);

  // Stable ref for booking permission check — used inside event handlers / effects
  const isBookingRestrictedRef = useRef(isBookingRestricted);
  isBookingRestrictedRef.current = isBookingRestricted;

  // Stable refs for role-based booking permissions so the context-menu closures
  // (created once per effect run) always read the latest values.
  const canCreateBookingRef = useRef(canCreateBooking);
  canCreateBookingRef.current = canCreateBooking;
  const quickAddEnabledRef = useRef(quickAddEnabled);
  quickAddEnabledRef.current = quickAddEnabled;
  const canEditBookingRef = useRef(canEditBooking);
  canEditBookingRef.current = canEditBooking;
  const canDeleteBookingRef = useRef(canDeleteBooking);
  canDeleteBookingRef.current = canDeleteBooking;

  // Delegated click handler for "Edit" buttons inside heatmap tooltip booking rows
  const onEditBookingRef = useRef(onEditBooking);
  onEditBookingRef.current = onEditBooking;

  // Delegated click handler for "Resolve" buttons inside heatmap tooltip booking rows
  const onResolveConflictRef = useRef(onResolveConflict);
  onResolveConflictRef.current = onResolveConflict;

  useEffect(() => {
    const handleHeatmapEditClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('[data-heatmap-edit-booking]') as HTMLElement | null;
      if (!btn) return;
      const bookingId = btn.getAttribute('data-heatmap-edit-booking');
      if (!bookingId) return;
      e.preventDefault();
      e.stopPropagation();
      const booking = bookings.find(bk => String(bk.id) === String(bookingId));
      if (booking && onEditBookingRef.current) {
        // Hide the Bryntum tooltip first so it doesn't linger behind the modal
        const scheduler = schedulerRef.current?.instance as any;
        scheduler?.features?.eventTooltip?.tooltip?.hide?.();
        onEditBookingRef.current(booking, booking.resourceId ?? undefined);
      }
    };
    document.addEventListener('click', handleHeatmapEditClick, true);
    return () => {
      document.removeEventListener('click', handleHeatmapEditClick, true);
    };
  }, [bookings]);

  useEffect(() => {
    const handleHeatmapResolveClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('[data-heatmap-resolve-bookings]') as HTMLElement | null;
      if (!btn) return;
      const bookingIdsAttr = btn.getAttribute('data-heatmap-resolve-bookings');
      if (!bookingIdsAttr) return;
      e.preventDefault();
      e.stopPropagation();
      // Decode the comma-separated list of IDs and look up each booking
      const ids = bookingIdsAttr.split(',').map(id => id.trim()).filter(Boolean);
      const cellBookings = ids
        .map(id => bookings.find(bk => String(bk.id) === id))
        .filter((bk): bk is Booking => bk !== undefined);
      if (cellBookings.length > 0 && onResolveConflictRef.current) {
        // Hide the Bryntum tooltip first so it doesn't linger behind the dialog
        const scheduler = schedulerRef.current?.instance as any;
        scheduler?.features?.eventTooltip?.tooltip?.hide?.();
        onResolveConflictRef.current(cellBookings);
      }
    };
    document.addEventListener('click', handleHeatmapResolveClick, true);
    return () => {
      document.removeEventListener('click', handleHeatmapResolveClick, true);
    };
  }, [bookings]);



  // Ensure createEventOnDblClick is disabled after every mount/remount.
  // After navigating away from the calendar and back, the Bryntum React wrapper
  // may not properly re-apply the createEventOnDblClick config from props.
  // This useEffect explicitly sets it on the scheduler instance.
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    (scheduler as any).createEventOnDblClick = false;
  }, []);


  // Prevent Bryntum's default event edit form from opening
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    // Listen for beforeEventEdit to prevent the default edit form
    const detacher = scheduler.on('beforeEventEdit', () => {
      // Return false to prevent Bryntum's event edit form from opening
      return false;
    });

    return () => {
      if (typeof detacher === 'function') {
        detacher();
      }
    };
  }, []);

  // Re-render suggested/plan-preview events when the view preset changes so the
  // correct visual treatment (dashed/solid/stripe) is applied for the new scale.
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as any;
    if (!scheduler) return;

    const detacher = scheduler.on('zoomChange', () => {
      // refreshRows re-invokes eventRenderer for every visible event, picking
      // up the new viewPreset.name without requiring a full data reload.
      scheduler.refreshRows();
    });

    return () => {
      if (typeof detacher === 'function') detacher();
    };
  }, []);

  // Prevent drag and resize on bookings the user does not have full access to
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    const denyEdit = () => {
      toast.error('Permission Denied', {
        description: "You don't have permission to edit this booking.",
      });
      return false;
    };

    const detachDrag = scheduler.on('beforeEventDrag', ({ eventRecord }: { eventRecord: { data: BryntumEvent } }) => {
      if (eventRecord.data?.isAbsenceEvent) return false;
      if (!canEditBookingRef.current) return denyEdit();
      const booking = findBookingById(eventRecord.data.id);
      if (booking && isBookingRestrictedRef.current(booking)) return denyEdit();
    });
    const detachResize = scheduler.on('beforeEventResize', ({ eventRecord }: { eventRecord: { data: BryntumEvent } }) => {
      if (eventRecord.data?.isAbsenceEvent) return false;
      if (!canEditBookingRef.current) return denyEdit();
      const booking = findBookingById(eventRecord.data.id);
      if (booking && isBookingRestrictedRef.current(booking)) return denyEdit();
    });

    return () => {
      if (typeof detachDrag === 'function') detachDrag();
      if (typeof detachResize === 'function') detachResize();
    };
  }, [findBookingById]);

  // Prevent right-click context menu on timeline header
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    // Aggressively disable header-related menu features on the scheduler instance
    // Note: scheduleMenu and eventMenu are NOT disabled here because custom context
    // menus are wired up via scheduler.on() listeners in separate useEffect hooks
    const headerFeatureNames = ['timeAxisHeaderMenu', 'headerMenu', 'cellMenu', 'contextMenu'] as const;
    for (const featureName of headerFeatureNames) {
      const feature = (scheduler.features as unknown as Record<string, unknown>)[featureName] as { disabled?: boolean; menu?: { destroy?: () => void } } | undefined;
      if (feature && typeof feature === 'object') {
        feature.disabled = true;
        // Destroy any already-created menu widget
        if (feature.menu && typeof feature.menu.destroy === 'function') {
          feature.menu.destroy();
        }
      }
    }

    // Block contextmenu at the document level for all header-related elements
    // This catches the event before Bryntum can process it
    const globalHandler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target?.closest('.b-grid-headers, .b-sch-header-row, .b-sch-timeaxiscolumn, .b-sch-header-timeaxis-cell, .b-grid-header-scroller, .b-grid-header-container, .b-timeaxis-cell, .b-sch-header')) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };
    document.addEventListener('contextmenu', globalHandler, true);

    return () => {
      document.removeEventListener('contextmenu', globalHandler, true);
    };
  }, []);

  // Custom context menu for empty schedule cells
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    let contextMenu: Menu | null = null;

    const handleScheduleContextMenu = (event: { resourceRecord?: { id: string }; date?: Date; domEvent: MouseEvent }) => {
      // Prevent default browser context menu
      event.domEvent.preventDefault();

      // Only show menu for resource rows (not group rows)
      if (!event.resourceRecord || !event.date) return;

      // Check if this is a group row (has children)
      const record = event.resourceRecord as unknown as { children?: unknown[] };
      if (record.children && record.children.length > 0) return;

      // Also check if this is a group node (group-, project-, role-, or customer- prefix)
      const resourceId = event.resourceRecord.id;
      if (resourceId.startsWith('group-') || resourceId.startsWith('project-') || resourceId.startsWith('role-') || resourceId.startsWith('customer-')) return;
      const clickDate = event.date;

      // Block context menu on cells covered by an absence
      if (isDateInAbsenceRef.current(resourceId, clickDate)) return;

      // Check if this is a non-working day for the resource - disable context menu if so.
      // Skipped in Quarter/Year views where a cell spans many days (would always match a weekend).
      const resource = findResourceById(resourceId);
      if (!isCoarseGranularityView && resource && isNonWorkingDay(resource, clickDate)) {
        // Don't show context menu on non-working days
        return;
      }

      // Check if this is during non-business hours - disable context menu if so (Day view)
      if (isNonBusinessHour(clickDate)) {
        return;
      }

      // Destroy previous menu if exists
      if (contextMenu) {
        contextMenu.destroy();
      }

      // SVG icons for menu items
      const addIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>';
      const boltIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>';
      const pasteIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>';

      // Build menu items. Add/Quick Add/Paste are all create operations, so they
      // are only shown when the user's role grants the createBooking permission.
      const canCreate = canCreateBookingRef.current;
      const quickAddIsEnabled = quickAddEnabledRef.current;
      const menuItems: Array<{ html: string; onItem: () => void }> = [];

      if (canCreate) {
        menuItems.push(
          {
            html: `<div style="display: flex; align-items: center; gap: 8px;">${addIcon}<span>Add Booking</span></div>`,
            onItem: () => {
              const resource = findResourceById(resourceId);
              if (resource && onAddBooking) {
                // In project-resource mode, pass the composite Bryntum ID so the handler
                // can extract the project ID for pre-populating the booking form.
                // In customer mode, pass the composite ID so the handler can extract the
                // customer name and pre-populate the booking form's customer field.
                if (groupingMode === 'project-resource' && resourceId.includes('-') &&
                  !resourceId.startsWith('group-') && !resourceId.startsWith('project-')) {
                  onAddBooking({ ...resource, id: resourceId }, clickDate);
                } else if (groupingMode === 'customer' && resourceId.includes('-') &&
                  !resourceId.startsWith('group-') && !resourceId.startsWith('customer-')) {
                  onAddBooking({ ...resource, id: resourceId }, clickDate);
                } else {
                  onAddBooking(resource, clickDate);
                }
              }
            },
          },
        );
      }

      // Quick Add Booking is only shown when the user can create bookings and
      // quick add is enabled (Quick Add Default is not set to "None").
      if (canCreate && quickAddIsEnabled) {
        menuItems.push({
          html: `<div style="display: flex; align-items: center; gap: 8px;">${boltIcon}<span>Quick Add Booking</span></div>`,
          onItem: () => {
            const resource = findResourceById(resourceId);
            if (resource && onQuickAddBooking) {
              // In project-resource mode, pass the composite Bryntum ID so the handler
              // can extract the project ID for pre-populating the booking
              if (groupingMode === 'project-resource' && resourceId.includes('-') &&
                !resourceId.startsWith('group-') && !resourceId.startsWith('project-')) {
                onQuickAddBooking({ ...resource, id: resourceId }, clickDate);
              } else {
                onQuickAddBooking(resource, clickDate);
              }
            }
          },
        });
      }

      // Only add Paste Booking if the user can create and a booking has been copied
      if (canCreate && copiedBooking) {
        menuItems.push({
          html: `<div style="display: flex; align-items: center; gap: 8px;">${pasteIcon}<span>Paste Booking</span></div>`,
          onItem: () => {
            const resource = findResourceById(resourceId);
            if (resource && onPasteBooking && copiedBooking) {
              const startTime = { hour: clickDate.getHours(), minute: clickDate.getMinutes() };
              onPasteBooking(resource, clickDate, startTime, copiedBooking);
            }
          },
        });
      }

      // Nothing the user is allowed to do here — don't show an empty menu.
      if (menuItems.length === 0) return;

      // Create custom context menu with smaller styling
      contextMenu = new Menu({
        anchor: true,
        autoShow: false,
        cls: 'custom-booking-menu',
        items: menuItems,
      });

      // Show menu at mouse position
      showMenuAtPointer(contextMenu, event.domEvent.clientX, event.domEvent.clientY);
    };

    // Add listener for schedule context menu - use the detacher function returned by on()
    const detacher = scheduler.on('scheduleContextMenu', handleScheduleContextMenu);

    return () => {
      // Use the detacher function to remove the listener
      if (typeof detacher === 'function') {
        detacher();
      }
      if (contextMenu) {
        contextMenu.destroy();
      }
    };
  }, [findResourceById, onAddBooking, onQuickAddBooking, onPasteBooking, copiedBooking, isNonWorkingDay, isNonBusinessHour, groupingMode, quickAddEnabled, isCoarseGranularityView]);

  // Custom context menu for events (bookings)
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    let eventContextMenu: Menu | null = null;

    // The bookings currently Ctrl/Cmd-selected on the timeline.
    const readSelection = (): Booking[] => bookingsFromSelectedEvents(
      (scheduler as unknown as { selectedEvents?: SelectedEventLike[] }).selectedEvents ?? [],
      {
        findBooking: id => findBookingByIdRef.current(id),
        isLocked: id => lockedBookingIdsRef.current?.has(id) === true,
        isRestricted: b => isBookingRestrictedRef.current(b),
      },
    );

    // Bryntum selects the event under the pointer on right-click, which would
    // collapse a multi-selection before the menu is built — so the selection is
    // snapshotted in the capture phase, ahead of Bryntum's own mousedown handling.
    let selectionAtRightClick: Booking[] = [];
    const captureSelection = (domEvent: MouseEvent) => {
      if (domEvent.button === 2) selectionAtRightClick = readSelection();
    };
    const schedulerEl = (scheduler as unknown as { element?: HTMLElement }).element;
    schedulerEl?.addEventListener('mousedown', captureSelection, true);

    const handleEventContextMenu = (event: { eventRecord?: { data: BryntumEvent; originalData?: BryntumEvent; get?: (key: string) => unknown }; domEvent: MouseEvent }) => {
      // Prevent default browser context menu
      event.domEvent.preventDefault();

      if (!event.eventRecord) return;

      // Block context menu on absence events
      if (event.eventRecord.data?.isAbsenceEvent) return;

      // Look up the latest booking from the bookings array first so that
      // tooltips and context menus always reflect the most recent data
      // (including changes made via a different resource row for the same
      // multi-resource booking).
      const eventRecord = event.eventRecord;
      const eventId = (eventRecord as unknown as { id?: string }).id || eventRecord.data?.id || (typeof eventRecord.get === 'function' ? eventRecord.get('id') as string : null);
      let booking: Booking | undefined;
      if (eventId) {
        booking = findBookingByIdRef.current(String(eventId));
      }

      // Fallback: try originalBooking stored on the event record
      if (!booking) {
        booking = eventRecord.data?.originalBooking;
      }
      if (!booking && eventRecord.originalData) {
        booking = eventRecord.originalData.originalBooking;
      }
      if (!booking && typeof eventRecord.get === 'function') {
        booking = eventRecord.get('originalBooking') as Booking | undefined;
      }

      if (!booking) {
        console.warn('Could not find booking for event context menu');
        return;
      }

      // Locked occurrences are being deleted in the background — suppress the
      // right-click menu entirely so nothing can be changed on them.
      if (lockedBookingIdsRef.current?.has(String(booking.id))) return;

      // Extract the resource ID from the event record for context-aware conflict filtering
      const contextResourceId = eventRecord.data?.resourceId;

      // Right-clicking inside a multi-selection applies bulk actions to the
      // whole selection; right-clicking anything else targets that booking alone.
      const actionTargets = bulkActionTargets(
        booking,
        selectionAtRightClick.length > 0 ? selectionAtRightClick : readSelection(),
      );
      const isBulk = actionTargets.length > 1;
      const deleteLabel = isBulk ? `Delete ${actionTargets.length} Bookings` : 'Delete Booking';
      const runDelete = () => {
        if (isBulk && onDeleteBookings) {
          onDeleteBookings(actionTargets);
        } else if (onDeleteBooking) {
          onDeleteBooking(booking);
        }
      };

      // Destroy previous menu if exists
      if (eventContextMenu) {
        eventContextMenu.destroy();
      }

      // Role lacks the editBooking permission. If the user can still delete this
      // booking (deleteBooking permission and the booking type isn't read-only),
      // show a menu containing only "Delete Booking"; otherwise show nothing.
      if (!canEditBookingRef.current) {
        if (!canDeleteBookingRef.current || isBookingRestrictedRef.current(booking)) return;

        const deleteOnlyTrashIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>';

        eventContextMenu = new Menu({
          anchor: true,
          autoShow: false,
          cls: 'custom-booking-menu',
          items: [
            {
              html: `<div style="display: flex; align-items: center; gap: 8px;">${deleteOnlyTrashIcon}<span style="font-size: 12px; font-weight: 400;">${deleteLabel}</span></div>`,
              onItem: runDelete,
            },
          ],
        });

        showMenuAtPointer(eventContextMenu, event.domEvent.clientX, event.domEvent.clientY);
        return;
      }

      // Check whether the user has permission to edit this booking. A caller is
      // restricted when the booking type is read-only for them — the menu is
      // then reduced to "View Booking" only.
      const restricted = isBookingRestrictedRef.current(booking);

      // SVG icons for event menu items
      const editIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
      const viewIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';
      const copyIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
      const moveIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/><polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/></svg>';
      const trashIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>';
      const statusIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>';
      const checkIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>';
      const chevronIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>';

      // Filter to only show enabled statuses
      const enabledStatuses = statusConfigs.filter(status => status.enabled);

      // Build menu items for event context menu
      const menuItems: Array<{
        html: string;
        disabled?: boolean;
        onItem?: () => void;
        menu?: Array<{
          html: string;
          onItem: () => void;
        }>;
      }> = restricted
        ? [
          // Restricted users can only view the booking
          {
            disabled: isBulk,
            html: `<div style="display: flex; align-items: center; gap: 8px;">${viewIcon}<span style="font-size: 12px; font-weight: 400;">View Booking</span></div>`,
            onItem: () => {
              if (onEditBooking) {
                onEditBooking(booking, contextResourceId);
              }
            },
          },
        ]
        : [
          {
            disabled: isBulk,
            html: `<div style="display: flex; align-items: center; gap: 8px;">${editIcon}<span style="font-size: 12px; font-weight: 400;">Edit Booking</span></div>`,
            onItem: () => {
              if (onEditBooking) {
                onEditBooking(booking, contextResourceId);
              }
            },
          },
          {
            disabled: isBulk,
            html: `<div style="display: flex; align-items: center; gap: 8px;">${copyIcon}<span style="font-size: 12px; font-weight: 400;">Copy Booking</span></div>`,
            onItem: () => {
              if (onCopyBooking) {
                onCopyBooking(booking);
              }
            },
          },
          {
            disabled: isBulk,
            html: `<div style="display: flex; align-items: center; gap: 8px;">${moveIcon}<span style="font-size: 12px; font-weight: 400;">Move</span></div>`,
            onItem: () => {
              if (onMoveBooking) {
                onMoveBooking(booking);
              }
            },
          },
        ];

      // Add Status submenu if there are enabled statuses and a handler is
      // provided (only for full-access users).
      if (!restricted && (onStatusChange || onStatusChangeBookings) && enabledStatuses.length > 0) {
        const statusMenuItems = enabledStatuses.map((status) => {
          const displayText = status.customText || status.text;
          const isCurrentStatus = actionTargets.every(target => String(target.status) === String(status.id));
          const colorCircle = `<div style="width: 12px; height: 12px; border-radius: 50%; background-color: ${status.color}; flex-shrink: 0;"></div>`;
          const checkmark = isCurrentStatus ? checkIcon : '<span style="width: 14px; display: inline-block;"></span>';

          return {
            html: `<div style="display: flex; align-items: center; gap: 8px;">${colorCircle}<span style="font-size: 12px; ${isCurrentStatus ? 'font-weight: 600;' : 'font-weight: 400;'}">${displayText}</span>${checkmark}</div>`,
            onItem: () => {
              if (isBulk && onStatusChangeBookings) {
                onStatusChangeBookings(actionTargets, String(status.id), status.color);
              } else if (onStatusChange) {
                onStatusChange(booking, String(status.id), status.color);
              }
            },
          };
        });

        menuItems.push({
          html: `<div style="display: flex; align-items: center; gap: 8px;">${statusIcon}<span style="font-size: 12px; font-weight: 400;">Change Status</span>${chevronIcon}</div>`,
          menu: statusMenuItems,
        });
      }

      // Add Delete at the end (only for full-access users who also hold the
      // deleteBooking role permission).
      if (!restricted && canDeleteBookingRef.current) {
        menuItems.push({
          html: `<div style="display: flex; align-items: center; gap: 8px;">${trashIcon}<span style="font-size: 12px; font-weight: 400;">${deleteLabel}</span></div>`,
          onItem: runDelete,
        });
      }

      // Create custom context menu with smaller styling
      eventContextMenu = new Menu({
        anchor: true,
        autoShow: false,
        cls: 'custom-booking-menu',
        items: menuItems,
      });

      // Show menu at mouse position
      showMenuAtPointer(eventContextMenu, event.domEvent.clientX, event.domEvent.clientY);
    };

    // Add listener for event context menu
    const detacher = scheduler.on('eventContextMenu', handleEventContextMenu);

    return () => {
      if (typeof detacher === 'function') {
        detacher();
      }
      schedulerEl?.removeEventListener('mousedown', captureSelection, true);
      if (eventContextMenu) {
        eventContextMenu.destroy();
      }
    };
  }, [onEditBooking, onCopyBooking, onMoveBooking, onDeleteBooking, onDeleteBookings, onStatusChange, onStatusChangeBookings, statusConfigs, bookings, isBookingRestricted]);

  // Handle expand/collapse for group rows - delegated to Bryntum's tree feature
  // via expandOnCellClick: true. No manual toggleCollapse here — calling it in
  // addition to the built-in handler causes a double-toggle that reverts the state.
  const handleCellClick = useCallback((_event: { record?: { children?: unknown[]; expanded?: boolean; isParent?: boolean; childLevel?: number }; column?: { type?: string }; source?: { resourceStore?: { toggleCollapse?: (record: unknown) => void } } }) => {
    // Intentionally empty — expand/collapse is handled by treeFeature.expandOnCellClick
  }, []);

  const columns = useMemo(() => {
    // Icon shows available action: minus (collapse) when expanded, plus (expand) when collapsed
    const expandCollapseSquareIcon = allGroupsExpanded ? COLLAPSE_ALL_ICON : EXPAND_ALL_ICON;

    return [
    {
      type: 'tree',
      text: '',
      field: 'name',
      width: 260,
      htmlEncode: false,
      editor: false,
      editable: false,
      sortable: false,
      // Prevent Bryntum's JS from calculating or applying any tree indent.
      // CSS overrides alone don't survive re-mounts because Bryntum re-runs
      // its row rendering pipeline which re-applies padding-inline-start via
      // JS after our stylesheet rules are resolved. indentSize: 0 stops it
      // at the source — no indent is ever written to the DOM.
      indentSize: 0,
      headerRenderer: () => {
        // Only the expand/collapse button lives here — the grouping trigger is a
        // React overlay (see the .grouping-header-overlay div in the JSX return)
        // so it is never subject to Bryntum header click interception.
        return `
          <div style="display: flex; align-items: center; justify-content: flex-end; width: 100%; padding: 0 12px 0 0; height: 100%;">
            <button
              id="expand-collapse-all-btn"
              title="${allGroupsExpanded ? 'Collapse all' : 'Expand all'}"
              style="
                display: inline-flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
                width: 32px;
                height: 32px;
                border: 1px solid hsl(var(--border));
                border-radius: 6px;
                background: transparent;
                color: hsl(var(--foreground));
                cursor: pointer;
                transition: background 0.15s, border-color 0.15s;
              "
              onmouseover="this.style.background='hsl(var(--accent))'; this.style.borderColor='hsl(var(--ring))';"
              onmouseout="this.style.background='transparent'; this.style.borderColor='hsl(var(--border))';"
            >
              ${expandCollapseSquareIcon}
            </button>
          </div>
        `;
      },
      renderer: ({ record, cellElement }: { record: { id?: string; name: string; role?: string; imageUrl?: string; children?: unknown[]; expanded?: boolean; isParent?: boolean; data?: { id?: string; name?: string; role?: string; imageUrl?: string; children?: unknown[]; isProjectGroup?: boolean; projectColor?: string }; get?: (field: string) => unknown }; cellElement?: HTMLElement }) => {
        // Access data - Bryntum may store it in record.data or directly on record
        const getData = (field: string): string | boolean | undefined => {
          if (record.get && typeof record.get === 'function') {
            return record.get(field) as string | boolean | undefined;
          }
          if (record.data && field in record.data) {
            return (record.data as unknown as Record<string, unknown>)[field] as string | boolean | undefined;
          }
          return (record as unknown as Record<string, unknown>)[field] as string | boolean | undefined;
        };

        const id = getData('id') as string || record.id || '';
        const name = getData('name') as string || record.name || '';
        const role = getData('role') as string || record.role;
        const imageUrl = getData('imageUrl') as string || record.imageUrl;
        const children = record.children || record.data?.children;
        const isExpanded = record.expanded;
        const isParent = record.isParent || (children && (children as unknown[]).length > 0);
        const isProjectGroup = getData('isProjectGroup') as boolean || record.data?.isProjectGroup;
        const projectColor = getData('projectColor') as string || record.data?.projectColor;

        // Apply alternate row shading: add class to every other leaf row,
        // skip group-header (parent) rows entirely so striping stays consistent.
        // We apply the class to ALL rows sharing the same data-id so that both
        // the locked (resource column) and normal (timeline) subgrids are shaded.
        if (cellElement) {
          const rowEl = cellElement.closest('.b-grid-row') as HTMLElement | null;
          if (rowEl) {
            const rowDataId = rowEl.getAttribute('data-id');
            const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
            const schedulerEl = scheduler && !scheduler.isDestroyed
              ? (scheduler as any).element as HTMLElement | undefined
              : undefined;

            // Collect all row elements across subgrids that belong to this record
            const allRowEls: HTMLElement[] = [];
            if (schedulerEl && rowDataId) {
              schedulerEl.querySelectorAll(`.b-grid-row[data-id="${rowDataId}"]`).forEach((el) => {
                allRowEls.push(el as HTMLElement);
              });
            }
            if (allRowEls.length === 0) {
              allRowEls.push(rowEl);
            }

            if (isParent) {
              allRowEls.forEach((el) => el.removeAttribute('data-alt-shading'));
            } else {
              // Compute this leaf's index among all visible leaf records
              if (scheduler && !scheduler.isDestroyed) {
                const records = (scheduler.resourceStore as any).records || [];
                let leafIdx = 0;
                for (const rec of records) {
                  if (String(rec.id) === String(record.id ?? id)) break;
                  if (!rec.isParent) leafIdx++;
                }
                const shouldShade = leafIdx % 2 === 0;
                allRowEls.forEach((el) => {
                  if (shouldShade) {
                    el.setAttribute('data-alt-shading', 'true');
                  } else {
                    el.removeAttribute('data-alt-shading');
                  }
                });
              }
            }
          }
        }

        // Group header row - show arrow icon and name (smaller text)
        if (isParent) {
          const arrowIcon = isExpanded
            ? `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="min-width: 12px;"><polyline points="6 9 12 15 18 9"></polyline></svg>`
            : `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="min-width: 12px;"><polyline points="9 18 15 12 9 6"></polyline></svg>`;

          const childCount = (record as any).childCount ?? (children ? (children as unknown[]).length : 0);
          const countBadge = childCount > 0
            ? `<span style="display: inline-flex; align-items: center; justify-content: center; min-width: 20px; padding: 0 5px; border-radius: 10px; font-size: 10px; font-weight: 600; color: #fff; background: rgba(0,0,0,0.35); line-height: 1.6;">${childCount}</span>`
            : '';

          // Project group - show with color indicator and project code
          if (isProjectGroup && projectColor) {
            const projectCode = getData('projectCode') as string || (record as any).data?.projectCode;

            return `
                          <div style="display: flex; align-items: center; gap: 6px; font-weight: 600; font-size: 12px; cursor: pointer;">
                            ${arrowIcon}
                            <div style="width: 10px; height: 10px; border-radius: 3px; background-color: ${projectColor}; flex-shrink: 0;"></div>
                            <div style="display: flex; flex-direction: column; gap: 2px;">
                              <span style="line-height: 1.2;">${escapeHtml(name)}</span>
                              ${projectCode ? `<span style="font-size: 10px; font-weight: 400; opacity: 0.7; line-height: 1.2;">${escapeHtml(projectCode)}</span>` : ''}
                            </div>
                            ${countBadge}
                          </div>
                        `;
          }

          // Regular department/scheduler group
          return `
                        <div style="display: flex; align-items: center; gap: 6px; font-weight: 600; font-size: 12px; cursor: pointer;">
                          ${arrowIcon}
                          <span>${escapeHtml(name)}</span>
                          ${countBadge}
                        </div>
                      `;
        }
        // Resource row - show profile image or teal-gradient initials fallback, name, utilisation %, and role
        const initials = (() => {
          const parts = name.trim().split(/\s+/);
          if (parts.length === 0) return '?';
          const first = parts[0]?.[0] || '';
          const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
          return (first + last).toUpperCase();
        })();
        const hasPhoto = !!imageUrl && imageUrl.trim() !== '' && imageUrl !== 'null' && imageUrl !== 'undefined';

        // Compute utilisation % directly from refs every time the renderer
        // fires.  This avoids all intermediate-state issues (stale Map,
        // Bryntum change-detection, refreshRows timing) because the refs
        // are always updated during the React render phase before Bryntum
        // re-renders rows via resumeRefresh(true).
        const origResId = (getData('originalResourceId') as string) || id;
        const resIdStr = String(origResId);
        const curProfile = globalWorkingProfileRef.current;
        const curTiers = settingsRef.current?.utilisationTiers || DEFAULT_UTILIZATION_TIERS;
        const sd = startDateRef.current;
        const ed = endDateRef.current;
        let utilPct = 0;
        let utilColor = '#6b7280';
        if (curProfile && sd && ed) {
          const resObj = resourceByIdRef.current.get(resIdStr);
          if (resObj) {
            // includeOverbooking keeps the row badge consistent with the
            // heatmap cells: every booked hour counts, so a doubly-booked day
            // pushes the total above 100%.
            utilPct = calculatePeriodUtilization(
              resIdStr, sd, ed,
              bookingsRef.current, resObj, curProfile,
              bankHolidaysRef.current, absencesRef.current,
              settingsRef.current?.bookingTypes,
              { includeOverbooking: true },
            ).utilizationPercentage;
            utilColor = getUtilizationColor(utilPct, curTiers);
          }
        }
        const utilBadge = `<span style="display: inline-flex; align-items: center; justify-content: center; min-width: 36px; padding: 1px 5px; border-radius: 4px; font-size: 10px; font-weight: 600; color: #1a1a1a; background: ${utilColor}; white-space: nowrap; line-height: 1.4;">${utilPct}%</span>`;

        return `
                      <div style="display: flex; align-items: center; gap: 6px; width: 100%; min-width: 0;">
                        <div style="position: relative; flex-shrink: 0;">
                          ${hasPhoto
                            ? `<img src="${escapeHtml(imageUrl || '')}" alt="${escapeHtml(name)}" style="width: 30px; height: 30px; border-radius: 6px; object-fit: cover; display: block;" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" /><div style="width: 30px; height: 30px; border-radius: 6px; display: none; align-items: center; justify-content: center; background: oklch(50% .0896 194.8); color: rgba(255,255,255,0.9); font-weight: 600; font-size: 11px; letter-spacing: 0.5px;">${escapeHtml(initials)}</div>`
                            : `<div style="width: 30px; height: 30px; border-radius: 6px; display: flex; align-items: center; justify-content: center; background: oklch(50% .0896 194.8); color: rgba(255,255,255,0.9); font-weight: 600; font-size: 11px; letter-spacing: 0.5px;">${escapeHtml(initials)}</div>`
                          }
                        </div>
                        <div style="flex: 1; min-width: 0; overflow: hidden;">
                          <div style="font-weight: 500; font-size: 12px; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${escapeHtml(name)}</div>
                          ${role ? `<div style="font-size: 10px; opacity: 0.7; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${escapeHtml(role)}</div>` : ''}
                        </div>
                        <div style="flex-shrink: 0; margin-left: auto;">
                          ${utilBadge}
                        </div>
                      </div>
                    `;
      },
    },
  ];
  }, [allGroupsExpanded]);

  const schedulerConfig = useMemo(() => {
    return {
      // viewPreset is intentionally NOT passed as a React prop.
      // Passing it here causes Bryntum's React wrapper to call setViewPreset()
      // outside of any suspendRefresh block, which destroys the time axis and
      // wipes the event store independently of our imperative store-sync effect.
      // The viewPreset is applied exclusively inside the store-sync useEffect
      // (within suspendRefresh/resumeRefresh) so the preset change, store
      // population and setTimeSpan are always a single atomic operation.
      emptyText: ' ',
      // Ctrl/Cmd-click adds a booking to the selection instead of replacing it,
      // so a set of bookings can be deleted in one action.
      multiEventSelect: true,
      barMargin: 6,
      rowHeight: 50,
      eventStyle: 'plain' as any,
      eventColor: 'blue',
      eventLayout: 'pack',
      eventRenderer: ({ eventRecord, renderData }: { eventRecord: { data: BryntumEvent; originalData?: BryntumEvent; get?: (key: string) => unknown }; renderData: { eventColor: string; style: string; cls?: Record<string, boolean>; wrapperCls?: Record<string, boolean> | string } }) => {
        // Get the original booking to check for multi-resource
        const booking = eventRecord.data?.originalBooking ||
          eventRecord.originalData?.originalBooking ||
          (eventRecord.get ? eventRecord.get('originalBooking') as Booking | undefined : undefined);

        // ── What-if ghost tile ─────────────────────────────────────────────
        // Prevent Bryntum adding ANY color class (b-sch-event-blue etc.) by
        // blanking renderData.eventColor, then force transparent via style.
        if (eventRecord.data?.isWhatIfGhost) {
          (renderData as any).eventColor = 'none';
          // Force all visuals inline — inline styles always beat Bryntum's injected classes.
          // Use a flex column wrapper so child divs fill the full event height naturally.
          renderData.style = [
            'background:transparent',
            'background-color:transparent',
            'border:2px dashed rgba(13,148,136,0.85)',
            'border-radius:4px',
            'box-shadow:none',
            'overflow:visible',
            'position:relative',
            'animation:whatif-border-pulse 1.8s ease-in-out infinite',
          ].join(';');

          // SVG hatch fill — stretches to 100%×100% of whatever size Bryntum
          // gives the event element. SVG with preserveAspectRatio="none" is the
          // only reliable way to fill an unknown-height container from innerHTML.
          // Three pattern layers: -45°, +45° and horizontal dashes.
          const hatch =
            '<svg xmlns="http://www.w3.org/2000/svg" style="position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;" preserveAspectRatio="none">' +
              '<defs>' +
                '<pattern id="wif-diag1" patternUnits="userSpaceOnUse" width="10" height="10" patternTransform="rotate(-45)">' +
                  '<line x1="0" y1="0" x2="0" y2="10" stroke="rgba(13,148,136,0.35)" stroke-width="1.5"/>' +
                '</pattern>' +
                '<pattern id="wif-diag2" patternUnits="userSpaceOnUse" width="10" height="10" patternTransform="rotate(45)">' +
                  '<line x1="0" y1="0" x2="0" y2="10" stroke="rgba(13,148,136,0.18)" stroke-width="1"/>' +
                '</pattern>' +
                '<pattern id="wif-horiz" patternUnits="userSpaceOnUse" width="10" height="12">' +
                  '<line x1="0" y1="0" x2="10" y2="0" stroke="rgba(13,148,136,0.28)" stroke-width="1.5"/>' +
                '</pattern>' +
              '</defs>' +
              '<rect width="100%" height="100%" fill="url(#wif-diag1)"/>' +
              '<rect width="100%" height="100%" fill="url(#wif-diag2)"/>' +
              '<rect width="100%" height="100%" fill="url(#wif-horiz)"/>' +
            '</svg>';

          // Centred "Proposed" pill — sits above the SVG hatch.
          const pill =
            '<div style="' +
              'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);z-index:2;' +
              'font-size:11px;font-weight:700;letter-spacing:0.06em;white-space:nowrap;' +
              'color:#0f766e;background:rgba(255,255,255,0.92);' +
              'border:1.5px solid rgba(13,148,136,0.65);' +
              'padding:2px 9px;border-radius:12px;' +
              'box-shadow:0 1px 4px rgba(0,0,0,0.15);pointer-events:none;">' +
              '◈  Proposed' +
            '</div>';

          return hatch + pill;
        }

        // ── Absence event (annual-leave) — use Bryntum's native dashed style ──
        if (eventRecord.data?.isAbsenceEvent) {
          const absColor = eventRecord.data.eventColor || '#94a3b8';
          renderData.style = (renderData.style || '') + `; --event-color: ${absColor}; border-color: ${absColor}; border-radius: 0 !important; padding: 0 2px;`;
          (renderData as any).eventColor = absColor;
          return escapeHtml(eventRecord.data?.name || 'Annual Leave');
        }

        // Force dark text on ALL events (heatmap + booking) via renderData.style.
        // This is the only reliable way to override Bryntum's internal colour
        // logic which defaults to white via --primary-foreground.
        renderData.style = (renderData.style || '') + '; color: rgba(0,0,0,0.75) !important;';

        // Check if this is a heatmap event — force inline background-color to
        // prevent Bryntum's hover CSS from making the cell transparent, then exit.
        if (eventRecord.data?.isHeatmapEvent) {
          const heatmapColor = eventRecord.data?.eventColor;
          if (heatmapColor) {
            renderData.style = (renderData.style || '') + `; background-color: ${heatmapColor} !important;`;
          }
          // Bryntum puts EventModel.cls on the event element only, but the
          // heatmap theme rules are keyed off the wrapper, so mirror them there.
          const wrapperCls = renderData.wrapperCls;
          if (wrapperCls && typeof wrapperCls === 'object') {
            for (const cls of String(eventRecord.data.cls ?? '').split(' ')) {
              if (cls) wrapperCls[cls] = true;
            }
          }
          return escapeHtml(eventRecord.data?.name || '');
        }

        if (!booking) {
          // Still force inline background for non-booking events to prevent hover color loss
          const fallbackColor = eventRecord.data?.eventColor;
          if (fallbackColor) {
            renderData.style = (renderData.style || '') + `; background-color: ${fallbackColor} !important;`;
          }
          return escapeHtml(eventRecord.data?.name || 'Booking');
        }

        const bookingResourceIds = booking.resourceIds || (booking.resourceId ? [booking.resourceId] : []);
        const isMultiResource = bookingResourceIds.length > 1;
        const isRecurring = !!booking.recurrenceGroupId;

        // Determine icon color based on booking background luminance
        const bgColor = getBookingColor(booking, settings.statusConfigs, settings.colorOverrides);
        // Cancelled bookings are drawn as a dashed outline in the cancelled
        // colour over a faded fill, with the label struck through.
        const isCancelled = isCancelledBooking(booking);
        const luminance = getRelativeLuminance(bgColor);
        // Dark background (luminance < 0.4) → white icons; light background → dark icons
        const iconColor = !isCancelled && luminance < 0.4 ? '#ffffff' : 'rgba(0,0,0,0.75)';

        // Force background-color via inline !important so Bryntum's hover CSS cannot override it.
        // Inline !important has the highest cascade priority and prevents color from disappearing on hover.
        renderData.style = (renderData.style || '') + (isCancelled
          ? `; background-color: ${getCancelledFill(bgColor)} !important; border: 2px dashed ${getCancelledOutlineColor(bgColor)} !important; text-decoration: line-through;`
          : `; background-color: ${bgColor} !important;`);

        // Icon SVGs with explicit color
        const usersIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="${iconColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`;
        const repeatIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="${iconColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/><path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/></svg>`;
        const layersIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="${iconColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>`;

        // Build leading icons (positioned before the booking name)
        const icons: string[] = [];

        if (isMultiResource) {
          icons.push(`<span style="display: inline-flex; align-items: center; gap: 1px; flex-shrink: 0;" title="Multi-resource booking (${bookingResourceIds.length} resources)">${usersIcon}<span style="font-size: 10px; font-weight: 600; line-height: 1; color: ${iconColor};">${bookingResourceIds.length}</span></span>`);
        }

        if (isRecurring) {
          icons.push(`<span style="display: inline-flex; align-items: center; flex-shrink: 0;" title="Recurring booking">${repeatIcon}</span>`);
        }

        if (isDemandBooking(booking)) {
          icons.push(`<span style="display: inline-flex; align-items: center; flex-shrink: 0;" title="Booking created from Open Demand">${layersIcon}</span>`);
        }

        const iconsHtml = icons.length > 0
          ? `<span style="display: inline-flex; align-items: center; gap: 3px; margin-right: 4px; flex-shrink: 0;">${icons.join('')}</span>`
          : '';

        const nameStyle = `font-weight: 600; overflow: hidden; text-overflow: ellipsis;${isCancelled ? ' text-decoration: line-through;' : ''}`;

        return `<div style="display: flex; align-items: center; overflow: hidden; white-space: nowrap;">${iconsHtml}<span style="${nameStyle}">${escapeHtml(eventRecord.data?.name || 'Booking')}</span></div>`;
      },
      snap: true,
      createEventOnDblClick: false,
      zoomOnMouseWheel: false,
      zoomOnTimeAxisDoubleClick: false,
      readOnly: isHeatmapMode,
      infiniteScroll: false,
      // Feature props — Bryntum React wrapper accepts each feature as
      // a top-level prop with a "Feature" suffix instead of nesting
      // them inside a `features` object.
      treeFeature: {
        expandOnCellClick: true,
      },
      rowCheckboxesFeature: false,
      cellEditFeature: false,
      quickFindFeature: false,
      filterFeature: false,
      sortFeature: false,
      columnReorderFeature: false,
      columnResizeFeature: false,
      eventDragFeature: isHeatmapMode ? false : {
        constrainDragToResource: false,
        showExactDropPosition: true,
        snapToTimeResolution: true,
        unifiedDrag: true,
        disabled: !isDragEnabled || isCoarseGranularityView,
      },
      eventResizeFeature: isHeatmapMode ? false : {
        showExactResizePosition: true,
        snapToTimeResolution: true,
        disabled: !isDragEnabled || isCoarseGranularityView,
      },
      eventEditFeature: false,
      eventTooltipFeature: {
        allowOver: true,
        hideDelay: 300,
        // Constrain the tooltip inside the viewport so it never gets clipped
        // when hovering over rows near the top/left/right edges.
        // 'b-t' means "align tooltip bottom-centre to target top-centre" (show above),
        // but Bryntum auto-flips to below/left/right when it doesn't fit.
        align: 'b-t',
        anchorToTarget: true,
        constrainTo: document.body,
        cls: 'b-heatmap-tooltip-popup',
        template: ({ eventRecord }: { eventRecord: { data: BryntumEvent; originalData?: BryntumEvent; get?: (key: string) => unknown; id?: string } }) => {
          const hData = eventRecord.data?.heatmapData ||
            (eventRecord.get ? eventRecord.get('heatmapData') as BryntumEvent['heatmapData'] : null);
          if (hData) {
            const tickStart: Date = (eventRecord.data?.startDate as Date) || new Date();
            return generateHeatmapTooltipHtml(hData, tickStart);
          }

          // Absence events: show absence-specific tooltip
          const isAbsence = eventRecord.data?.isAbsenceEvent ||
            (eventRecord.get ? eventRecord.get('isAbsenceEvent') : false);
          if (isAbsence) {
            return generateAbsenceTooltipHtml(eventRecord.data);
          }

          const rawInitId = eventRecord.id ?? eventRecord.data?.id ?? (eventRecord.get ? eventRecord.get('id') : null);
          const eventId = rawInitId != null ? String(rawInitId) : null;
          let booking: Booking | null = null;
          if (eventId) {
            booking = findBookingById(eventId) || null;
          }
          if (!booking) {
            booking = eventRecord.data?.originalBooking ||
              eventRecord.originalData?.originalBooking ||
              (eventRecord.get ? eventRecord.get('originalBooking') as Booking : null);
          }
          if (!booking) {
            return '<div style="padding: 8px;">No booking data</div>';
          }
          return generateTooltipHtml(booking);
        },
      },
      eventDragCreateFeature: isHeatmapMode ? false : {
        showTooltip: false,
      },
      // Disable all default context menus
      scheduleMenuFeature: false,
      cellMenuFeature: false,
      headerMenuFeature: false,
      contextMenuFeature: false,
      eventMenuFeature: false,
      timeAxisHeaderMenuFeature: false,
      nonWorkingTimeFeature: true,
      eventNonWorkingTimeFeature: true,
      timeRangesFeature: {
        enableResizing: false,
        showCurrentTimeLine: false,
        showHeaderElements: false,
      },
      // Enable resource time ranges for absences and bank holidays
      resourceTimeRangesFeature: true,
      // Disable schedule tooltip for empty cells (shows incorrect dates in Year/Quarter views)
      scheduleTooltipFeature: false,
    };
  }, [viewPreset, generateTooltipHtml, generateAbsenceTooltipHtml, generateHeatmapTooltipHtml, findBookingById, calendarView, isHeatmapMode, isDragEnabled, isCoarseGranularityView]);

  useEffect(() => {
    const applyTooltipTemplate = () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler || scheduler.isDestroyed) return;
      if (scheduler.features.eventTooltip) {
        const eventTooltipFeature = scheduler.features.eventTooltip as { template?: (data: { eventRecord: { data: BryntumEvent; originalData?: BryntumEvent; get?: (key: string) => unknown; id?: string } }) => string; allowOver?: boolean; hideDelay?: number; align?: string; anchorToTarget?: boolean; constrainTo?: Element | null };
        eventTooltipFeature.allowOver = true;
        eventTooltipFeature.hideDelay = 300;
        // Ensure tooltip stays within the viewport and auto-flips when near edges
        eventTooltipFeature.align = 'b-t';
        eventTooltipFeature.anchorToTarget = true;
        eventTooltipFeature.constrainTo = document.body;
        eventTooltipFeature.template = ({ eventRecord }: { eventRecord: { data: BryntumEvent; originalData?: BryntumEvent; get?: (key: string) => unknown; id?: string } }) => {
          try {
            const hData = eventRecord.data?.heatmapData ||
              (eventRecord.get ? eventRecord.get('heatmapData') as BryntumEvent['heatmapData'] : null);
            if (hData) {
              const tickStart: Date = (eventRecord.data?.startDate as Date) || new Date();
              return generateHeatmapTooltipHtmlRef.current(hData, tickStart);
            }

            // Absence events: show absence-specific tooltip
            const isAbsenceEvt = eventRecord.data?.isAbsenceEvent ||
              (eventRecord.get ? eventRecord.get('isAbsenceEvent') : false);
            if (isAbsenceEvt) {
              return generateAbsenceTooltipHtmlRef.current(eventRecord.data);
            }

            // Resolve the event ID, coercing to string so __mr__ stripping works
            // even when Bryntum stores the id as a non-string type.
            const rawId = eventRecord.id ?? eventRecord.data?.id ?? (eventRecord.get ? eventRecord.get('id') : null);
            const eventId = rawId != null ? String(rawId) : null;
            let booking: Booking | null = null;
            if (eventId) {
              booking = findBookingByIdRef.current(eventId) || null;
            }
            if (!booking) {
              booking = eventRecord.data?.originalBooking ||
                eventRecord.originalData?.originalBooking ||
                (eventRecord.get ? eventRecord.get('originalBooking') as Booking : null);
            }
            if (!booking) {
              return '<div style="padding: 8px;">No booking data</div>';
            }
            return generateTooltipHtmlRef.current(booking);
          } catch (err) {
            console.error('Tooltip template error:', err);
            return '<div style="padding: 8px;">Error loading tooltip</div>';
          }
        };
      }
    };
    applyTooltipTemplate();
    const timerId = setTimeout(applyTooltipTemplate, 100);
    return () => clearTimeout(timerId);
  }, [generateTooltipHtml, generateAbsenceTooltipHtml, generateHeatmapTooltipHtml, findBookingById]);

  // Dynamically enable/disable drag features when the user preference changes at runtime
  // Also re-apply after groupingMode changes (e.g. switching from Resource to Project & Resource)
  // because the store sync rebuilds resources/events and Bryntum may reset feature state.
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler || scheduler.isDestroyed) return;

    const shouldDisable = isHeatmapMode || !isDragEnabled || isCoarseGranularityView;

    if (scheduler.features.eventDrag) {
      (scheduler.features.eventDrag as { disabled?: boolean }).disabled = shouldDisable;
    }
    if (scheduler.features.eventResize) {
      (scheduler.features.eventResize as { disabled?: boolean }).disabled = shouldDisable;
    }
  }, [isDragEnabled, isHeatmapMode, groupingMode, isCoarseGranularityView]);

  useEffect(() => {
    const overrideDragCreateTooltip = () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler || scheduler.isDestroyed) return;
      const dragCreate = scheduler.features.eventDragCreate as {
        showTooltip?: boolean;
        tooltipTemplate?: (data: { startDate: Date; endDate: Date; startClockHtml: string; endClockHtml: string }) => string;
        tip?: { html?: string };
      } | undefined;
      if (dragCreate) {
        dragCreate.tooltipTemplate = ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
          const clockSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>';
          const startStr = format(startDate, 'HH:mm');
          const endStr = format(endDate, 'HH:mm');
          return `<div style=\"padding: 8px; font-size: 13px; line-height: 1.8;\">\n            <div style=\"display: flex; align-items: center; gap: 6px;\">${clockSvg} ${startStr}</div>\n            <div style=\"display: flex; align-items: center; gap: 6px;\">${clockSvg} ${endStr}</div>\n          </div>`;
        };
      }
    };
    overrideDragCreateTooltip();
    const timerId = setTimeout(overrideDragCreateTooltip, 200);
    return () => clearTimeout(timerId);
  }, []);



  // When groupingMode changes, force-refresh the scheduler's resource and event stores
  // so the new tree structure and resourceId mappings take effect without a full remount.
  useEffect(() => {
    const sync = () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler || scheduler.isDestroyed) return false;

      const eventsData = isHeatmapMode ? heatmapEvents : bryntumEvents;

      // Tracks whether the incremental diff actually added or removed events
      // (as opposed to only mutating existing records in place, e.g. on
      // drag/resize).  Used after resumeRefresh to force an event-bar repaint
      // only when the event *set* changed — see the guarded repaint below.
      let eventSetChanged = false;
      // Tracks whether the resource-time-range set (public holidays / shaded
      // absences drawn via the ResourceTimeRanges feature) actually changed
      // during this pass.  Like eventSetChanged, used to force a repaint of the
      // affected rows after resumeRefresh so navigated-span shading appears
      // without a remount.
      let timeRangesChanged = false;
      // Tracks whether the global time-range set (timeRangeStore, e.g.
      // non-working hours in Day view) changed.  These render on the TimeRanges
      // feature's own canvas rather than the per-resource event pipeline, so
      // they need a separate feature refresh below.
      let globalTimeRangesChanged = false;
      // Tracks whether the resource store was actually rebuilt this pass.  The
      // rebuild (removeAll + add) detaches every event from its resource, so
      // when it happens the incremental event diff alone cannot re-attach the
      // persisting-id bars — a broad event re-layout is required afterwards.
      let resourceStoreRebuilt = false;
      // Tracks whether any absence / public-holiday event was updated in place
      // (existing.set(newEvt)).  These never appear in toAdd/toRemove, so
      // eventSetChanged stays false for them even though their bars must be
      // repainted for the navigated span.
      let absenceUpdated = false;
      // Whether the visible time span was advanced this pass (set by the
      // setTimeSpan owner in the useLayoutEffect).  Navigating to a span whose
      // events are already loaded changes neither the event set nor the
      // resource set, yet still needs a forced re-layout for the new span.
      const timeSpanAdvanced = timeSpanAdvancedRef.current;
      timeSpanAdvancedRef.current = false;
      // Resource ids whose event bars must be repainted (owners of added/removed
      // events).  Lets us do a scoped repaint instead of refreshing every row.
      const affectedResourceIds = new Set<string>();

      // Suspend ALL Bryntum rendering for the entire operation so the engine
      // never sees a partial state (empty store between removeAll and add).
      scheduler.suspendRefresh();
      try {
        // Rebuild the resource store ONLY when the resource set actually
        // changed.  bryntumResources is memoised, so plain date navigation
        // (next/prev/date-picker) keeps the same reference and we skip the
        // removeAll + add — which would otherwise detach every event from its
        // resource and leave persisting-id bars orphaned/unrendered for the
        // navigated span.  A rebuild only happens when resources, bookings
        // (row heights), grouping, etc. genuinely change.
        if (prevAppliedResourcesRef.current !== bryntumResources) {
          scheduler.resourceStore.removeAll();
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          scheduler.resourceStore.add(bryntumResources as any);

          // Imperatively apply dynamic rowHeight on leaf resource records.
          // Plain data properties are not picked up by Bryntum's tree model
          // — we must call set() on the model instance.
          scheduler.resourceStore.forEach((record: any) => {
            if (!record.isParent) {
              if (record.data?.rowHeight) {
                record.set('rowHeight', record.data.rowHeight);
              }
            }
          });

          prevAppliedResourcesRef.current = bryntumResources;
          resourceStoreRebuilt = true;
        }

        // ── Incremental event store sync ──────────────────────────────────
        // Instead of removeAll() + add() which destroys Bryntum's internal
        // event layout cache for ALL rows (causing bar heights to be
        // recalculated from scratch and potentially shrink on unrelated
        // resources), we diff the current store against the new data and
        // only add, remove, or update records that actually changed.
        // This preserves Bryntum's pack-layout state for untouched rows.
        const newEventsById = new Map<string, (typeof eventsData)[number]>();
        eventsData.forEach(e => newEventsById.set(String(e.id), e));

        // Remove events that are no longer present
        const toRemove = (scheduler.eventStore.getRange() as any[]).filter(
          (r: any) => !newEventsById.has(String(r.id)),
        );
        if (toRemove.length > 0) {
          toRemove.forEach((r: { resourceId?: unknown; data?: { resourceId?: unknown } }) => {
            const rid = r.resourceId ?? r.data?.resourceId;
            if (rid != null) affectedResourceIds.add(String(rid));
          });
          scheduler.eventStore.remove(toRemove);
        }

        // Add new events and update changed existing events
        const toAdd: typeof eventsData = [];
        newEventsById.forEach((newEvt, id) => {
          const existing = scheduler.eventStore.getById(id) as any;
          if (!existing) {
            toAdd.push(newEvt);
          } else if ((newEvt as any).isAbsenceEvent) {
            // Always force-update absence events after resource store rebuild.
            // Without this, Bryntum can lose the resource association for absence
            // events when the resource store is rebuilt (removeAll + add), causing
            // them to not render after date navigation even though they are in the store.
            existing.set(newEvt);
            // In-place updates never show up in toAdd/toRemove, so flag them
            // explicitly and record the owning row so the post-resumeRefresh
            // repaint below covers absence / public-holiday bars on navigation.
            absenceUpdated = true;
            if (newEvt.resourceId != null) affectedResourceIds.add(String(newEvt.resourceId));
          } else {
            // Update if key visual fields changed
            const d = existing.data ?? {};
            const startChanged = String(d.startDate) !== String(newEvt.startDate);
            const endChanged = String(d.endDate) !== String(newEvt.endDate);
            const resChanged = String(d.resourceId ?? '') !== String(newEvt.resourceId ?? '');
            const nameChanged = String(d.name ?? '') !== String(newEvt.name ?? '');
            const colorChanged = String(d.eventColor ?? '') !== String(newEvt.eventColor ?? '');
            const clsChanged = String(d.cls ?? '') !== String(newEvt.cls ?? '');
            if (startChanged || endChanged || resChanged || nameChanged || colorChanged || clsChanged) {
              existing.set(newEvt);
            } else if ((newEvt as any).originalBooking && d.originalBooking !== (newEvt as any).originalBooking) {
              // Keep originalBooking in sync even when visual fields are unchanged
              // so tooltips and context menus always reflect the latest booking data
              // (e.g. after editing a multi-resource booking from one resource row).
              d.originalBooking = (newEvt as any).originalBooking;
            }
          }
        });
        if (toAdd.length > 0) {
          toAdd.forEach((e) => {
            if (e.resourceId != null) affectedResourceIds.add(String(e.resourceId));
          });
          scheduler.eventStore.add(toAdd as any);
        }

        // Record whether the visible event set changed so we can force a
        // repaint of the newly added/removed bars after resumeRefresh.  Pure
        // in-place updates (drag/resize) do not flip this flag and therefore
        // never trigger the guarded repaint below.
        eventSetChanged = toAdd.length > 0 || toRemove.length > 0;

        // ── What-if ghost tile injection ──────────────────────────────────────
        // Injected here — inside suspendRefresh, after the regular events are
        // loaded — so it is atomic with the store population and fires exactly
        // when the scheduler is confirmed ready (no setTimeout guesswork).
        // Reduce/delay scenarios change a booking in place, so they carry ghost
        // assignments without a candidate resource — preview those too.
        if (
          whatIfScenario &&
          whatIfScenario.type === 'rebalance' &&
          (whatIfScenario.candidateResourceId ||
            (whatIfScenario.ghostAssignments?.length ?? 0) > 0)
        ) {
          const {
            atRiskBookingIds,
            atRiskResourceId,
            candidateResourceId,
            projectName,
            affectedWeekStarts,
            atRiskBookingDates,
            ghostAssignments,
            bookingAssignments,
          } = whatIfScenario;

          // The ghost is the *proposed new* booking, so it must render on the
          // resource that would RECEIVE the work — never the over-utilised
          // at-risk resource it is being moved off of. Per-booking branches use
          // their own mapped target; the fallbacks use the single candidate.
          const fallbackGhostResourceId = String(candidateResourceId ?? atRiskResourceId);

          // Build ghost event(s) sized to the actual booking span when available,
          // falling back to a Mon 09:00–17:00 block per affected week.
          let ghostEvents: object[];
          if (ghostAssignments && ghostAssignments.length > 0) {
            // Capacity-aware preview: draw each ghost on the resource that would
            // actually receive it, so the preview never shows an over-utilised
            // resource that isn't free.
            ghostEvents = ghostAssignments.map((ga, idx) => ({
              id: `${GHOST_EVENT_ID_PREFIX}${idx}`,
              resourceId: String(ga.resourceId),
              name: '',
              startDate: new Date(ga.startDate),
              endDate:   new Date(ga.endDate),
              cls: 'whatif-ghost-proposed',
              isWhatIfGhost: true,
              eventStyle: 'hollow',
            }));
          } else if (bookingAssignments && bookingAssignments.length > 0) {
            // Per-booking distribution plan without pre-sized ghost dates: place
            // each ghost on its mapped resource, sizing from the matching source
            // booking record when available.
            ghostEvents = bookingAssignments.map((ba, idx) => {
              const rec = scheduler.eventStore.getById(ba.bookingId) as
                | { startDate?: Date | string; endDate?: Date | string }
                | null;
              const start = rec?.startDate ? new Date(rec.startDate) : new Date(whatIfScenario.targetWeekStart);
              const end = rec?.endDate ? new Date(rec.endDate) : new Date(whatIfScenario.targetWeekStart);
              return {
                id: `${GHOST_EVENT_ID_PREFIX}${idx}`,
                resourceId: String(ba.candidateResourceId),
                name: '',
                startDate: start,
                endDate: end,
                cls: 'whatif-ghost-proposed',
                isWhatIfGhost: true,
                eventStyle: 'hollow',
              };
            });
          } else if (atRiskBookingDates && atRiskBookingDates.length > 0) {
            ghostEvents = atRiskBookingDates.map((bd, idx) => ({
              id: `${GHOST_EVENT_ID_PREFIX}${idx}`,
              resourceId: fallbackGhostResourceId,
              name: '',
              startDate: new Date(bd.startDate),
              endDate:   new Date(bd.endDate),
              cls: 'whatif-ghost-proposed',
              isWhatIfGhost: true,
              eventStyle: 'hollow',
            }));
          } else {
            const weeks = affectedWeekStarts && affectedWeekStarts.length > 0
              ? affectedWeekStarts
              : [whatIfScenario.targetWeekStart];
            ghostEvents = weeks.map((ws, idx) => {
              const dayStart = new Date(ws);
              dayStart.setHours(9, 0, 0, 0);
              const dayEnd = new Date(ws);
              dayEnd.setHours(17, 0, 0, 0);
              return {
                id: `${GHOST_EVENT_ID_PREFIX}${idx}`,
                resourceId: fallbackGhostResourceId,
                name: '',
                startDate: dayStart,
                endDate: dayEnd,
                cls: 'whatif-ghost-proposed',
                isWhatIfGhost: true,
                eventStyle: 'hollow',
              };
            });
          }
          scheduler.eventStore.add(ghostEvents as any);

          // Apply whatif-moving class to the at-risk source booking tiles.
          if (atRiskBookingIds && atRiskBookingIds.length > 0) {
            atRiskBookingIds.forEach((bid) => {
              const rec = scheduler.eventStore.getById(bid);
              if (rec) {
                const existing: string = (rec as any).data?.cls ?? '';
                if (!existing.includes(MOVING_CLS)) {
                  rec.set('cls', [existing, MOVING_CLS].filter(Boolean).join(' '));
                }
              }
            });
          } else if (atRiskResourceId) {
            ((scheduler.eventStore as any).allRecords ?? []).forEach((rec: any) => {
              const ob = rec.data?.originalBooking;
              if (!ob) return;
              if (
                String(ob.resourceId) !== String(atRiskResourceId) ||
                (projectName && ob.projectName !== projectName)
              ) return;
              const existing: string = rec.data?.cls ?? '';
              if (!existing.includes(MOVING_CLS)) {
                rec.set('cls', [existing, MOVING_CLS].filter(Boolean).join(' '));
              }
            });
          }
        }

        // Re-populate the resourceTimeRangeStore — the React wrapper only applies
        // the resourceTimeRanges prop on mount so we keep it in sync manually.
        //
        // Public holidays and shaded absences (.bank-holiday-range /
        // .absence-range) are drawn through the event render pipeline (the
        // ResourceTimeRanges feature chains getEventsToRender), so — exactly
        // like event bars — after resumeRefresh(true) they only lay out for the
        // navigated span if we force a repaint of their resource rows.  Detect
        // whether the range set actually changed (ids/span/resource/shading) so
        // the guarded repaint below fires, and record the owning rows so it can
        // be scoped.
        const rtrs = (scheduler as any).resourceTimeRangeStore;
        if (rtrs) {
          const newRanges = (resourceTimeRanges ?? []) as Array<Record<string, unknown>>;
          const current = ((rtrs.getRange?.() ?? []) as Array<{ data?: Record<string, unknown> }>);
          const rangeSig = (d: Record<string, unknown>) =>
            `${String(d.id)}|${String(d.resourceId ?? '')}|${String(d.startDate)}|${String(d.endDate)}|${String(d.cls ?? '')}|${String(d.timeRangeColor ?? '')}`;
          const currentSigs = new Set(current.map((r) => rangeSig(r.data ?? (r as Record<string, unknown>))));
          const newSigs = new Set(newRanges.map((r) => rangeSig(r)));
          timeRangesChanged =
            currentSigs.size !== newSigs.size ||
            [...newSigs].some((s) => !currentSigs.has(s));
          if (timeRangesChanged) {
            const collectRid = (d: Record<string, unknown>) => {
              const rid = d.resourceId;
              if (rid != null) affectedResourceIds.add(String(rid));
            };
            newRanges.forEach(collectRid);
            current.forEach((r) => collectRid(r.data ?? (r as Record<string, unknown>)));
          }
          rtrs.removeAll();
          rtrs.add(resourceTimeRanges as any);
        }

        // Re-populate the timeRangeStore for global time ranges (non-working
        // hours in Day view etc.).  These render on the TimeRanges feature's own
        // canvas, not the per-resource event pipeline, so a scoped resource
        // repaint will not cover them — track the change separately and refresh
        // the feature after resumeRefresh.
        const trs = (scheduler as any).timeRangeStore;
        if (trs) {
          const newGlobal = (timeRanges ?? []) as Array<Record<string, unknown>>;
          const current = ((trs.getRange?.() ?? []) as Array<{ data?: Record<string, unknown> }>);
          const sig = (d: Record<string, unknown>) =>
            `${String(d.id)}|${String(d.startDate)}|${String(d.endDate)}|${String(d.cls ?? '')}`;
          const currentSigs = new Set(current.map((r) => sig(r.data ?? (r as Record<string, unknown>))));
          const newSigs = new Set(newGlobal.map((r) => sig(r)));
          globalTimeRangesChanged =
            currentSigs.size !== newSigs.size ||
            [...newSigs].some((s) => !currentSigs.has(s));
          trs.removeAll();
          trs.add(timeRanges as any);
        }

        // Apply viewPreset only when it actually changed.  Unnecessary
        // reassignment causes Bryntum to reconfigure the time axis which
        // can trigger a full event-layout recalculation.
        if (lastSyncedViewPresetRef.current !== viewPreset) {
          (scheduler as any).viewPreset = viewPreset;
          lastSyncedViewPresetRef.current = viewPreset;
        }

        // NOTE: the visible time span is advanced by the useLayoutEffect below,
        // which is the single owner of setTimeSpan (it runs before paint and
        // updates lastSyncedStart/EndRef + timeSpanAdvancedRef).  We must NOT
        // call setTimeSpan here as well, otherwise the axis would be advanced
        // twice per navigation.  Whether it moved this pass is read from
        // timeSpanAdvanced above and used to force an event re-layout below.
      } finally {
        scheduler.resumeRefresh(true);
      }

      // Force a repaint for the rows whose event set OR resource-time-range set
      // changed.
      //
      // In the stable-key non-heatmap navigation path the Bryntum instance is
      // not remounted on date change.  When new bookings for the new date range
      // arrive *after* the time span has already been advanced (the
      // useLayoutEffect setTimeSpan and the earlier store-sync pass have both
      // run, so lastSyncedStart/EndRef already equal the new dates and the
      // setTimeSpan re-sync above is skipped), resumeRefresh(true) alone does
      // not lay out the freshly added events for the unchanged span — they stay
      // invisible until the component is remounted (e.g. switching tabs).
      //
      // The exact same race applies to resource time ranges (public holidays /
      // shaded absences): they are drawn through the event render pipeline, so
      // the freshly added ranges for the navigated span are not laid out until
      // a remount.  Since repaintEventsForResource() re-renders a row's whole
      // event canvas (event bars AND resource time ranges), the same scoped
      // repaint fixes both — guarded on timeRangesChanged as well as
      // eventSetChanged (their owning rows were added to affectedResourceIds
      // above).
      //
      // refreshRows() only re-renders grid CELLS, not event bars, so it does
      // not fix this.  repaintEventsForResource() rerenders the event bars for
      // a specific row, which is exactly the scoped repaint we need and it does
      // not recompute layout for unrelated rows (avoiding the bar-shrinking
      // regression).  Guarded so pure in-place drag/resize updates never
      // trigger it.
      //
      // repaint/refresh alone is not enough, though: freshly-added event
      // records have NO layout yet.  Bryntum's scheduling project normalises
      // added records asynchronously, and since the span did not change here,
      // resumeRefresh(true) re-renders the *existing* layout without waiting
      // for that calculation — so the new bars have nothing to draw.  We must
      // commit the project first (commitAsync resolves once the engine has laid
      // the new records into the current time span) and only then repaint.
      //
      // Trigger the forced re-layout whenever ANY of the navigation-affecting
      // conditions held this pass:
      //   • eventSetChanged     — bookings/absences added or removed
      //   • timeRangesChanged   — resource time ranges (public holidays /
      //                           shaded absences) changed
      //   • absenceUpdated      — absence / public-holiday events updated in
      //                           place (never in toAdd/toRemove)
      //   • resourceStoreRebuilt— every event was detached from its resource
      //   • timeSpanAdvanced    — the visible span moved to already-loaded data
      // Pure in-place drag/resize updates flip none of these, so they still
      // never trigger a full relayout.
      if (
        (eventSetChanged || timeRangesChanged || absenceUpdated || resourceStoreRebuilt || timeSpanAdvanced) &&
        !scheduler.isDestroyed
      ) {
        const sched = scheduler as unknown as {
          repaintEventsForResource?: (r: unknown) => void;
          refreshResources?: (records?: unknown[]) => void;
          resourceStore: {
            getById: (id: string) => unknown;
            query?: (fn: (r: { id?: unknown; resourceId?: unknown; data?: { resourceId?: unknown } }) => boolean) => unknown[];
          };
          project?: { commitAsync?: () => Promise<void> };
        };

        // Resolve the resource records owning the changed events.  In grouped
        // modes (e.g. Project & Resource) a leaf record's id is a composite
        // that differs from the raw event resourceId, so getById misses it —
        // fall back to querying the store by the underlying resourceId.
        const resolveResourceRecords = (): unknown[] => {
          const records: unknown[] = [];
          affectedResourceIds.forEach((rid) => {
            const direct = sched.resourceStore.getById(rid);
            if (direct) {
              records.push(direct);
            } else if (typeof sched.resourceStore.query === 'function') {
              const matches = sched.resourceStore.query(
                (r) => String(r.resourceId ?? r.data?.resourceId ?? r.id) === rid,
              );
              matches.forEach((m) => records.push(m));
            }
          });
          return records;
        };

        const repaint = () => {
          if (scheduler.isDestroyed) return;

          // When the resource store was rebuilt this pass, every event was
          // detached and re-attached, so a per-row repaint is not enough — the
          // whole event/time-range layout must be rebuilt for the current span.
          // The same broad refresh is required when the span was advanced onto
          // already-loaded data with no specific row to scope to.  This lays out
          // booking bars, absence/public-holiday event bars AND shaded resource
          // time ranges (all rendered through the per-resource event pipeline).
          if (resourceStoreRebuilt && typeof sched.refreshResources === 'function') {
            sched.refreshResources();
            return;
          }

          const records = resolveResourceRecords();
          let repainted = 0;
          if (records.length > 0 && typeof sched.repaintEventsForResource === 'function') {
            records.forEach((rec) => {
              sched.repaintEventsForResource!(rec);
              repainted++;
            });
          }
          // Fallback: if no specific row could be resolved (e.g. a pure
          // timeSpanAdvanced pass), repaint all resource rows' event bars
          // (still layout-preserving, unlike refreshRows()/refreshEvents()).
          if (repainted === 0 && typeof sched.refreshResources === 'function') {
            sched.refreshResources();
          }
        };

        if (sched.project && typeof sched.project.commitAsync === 'function') {
          sched.project.commitAsync().then(repaint).catch(() => {
            // Scheduler was torn down mid-commit — nothing to repaint.
          });
        } else {
          repaint();
        }
      }

      // Global time ranges (timeRangeStore) render on the TimeRanges feature's
      // own canvas rather than the per-resource event pipeline, so the resource
      // repaint above does not cover them.  Force the feature to re-render when
      // its set changed so newly added ranges appear on same-view navigation.
      if (globalTimeRangesChanged && !scheduler.isDestroyed) {
        const trFeature = (scheduler.features as unknown as {
          timeRanges?: { refresh?: () => void };
        }).timeRanges;
        if (trFeature && typeof trFeature.refresh === 'function') {
          trFeature.refresh();
        }
      }

      // Re-apply drag/resize feature state after store rebuild.
      // Switching grouping modes (e.g. Resource → Project & Resource) rebuilds
      // the resource/event stores which can reset Bryntum's internal feature state.
      const shouldDisableDrag = isHeatmapMode || !isDragEnabled || isCoarseGranularityView;
      if (scheduler.features.eventDrag) {
        (scheduler.features.eventDrag as { disabled?: boolean }).disabled = shouldDisableDrag;
      }
      if (scheduler.features.eventResize) {
        (scheduler.features.eventResize as { disabled?: boolean }).disabled = shouldDisableDrag;
      }

      // Column renderers (including the utilisation badge) fire during
      // resumeRefresh(true) because the resource records were rebuilt
      // (removeAll + add).  The refs they read from (bookingsRef,
      // startDateRef, etc.) are already updated in React's render phase
      // which runs before this useEffect, so the values are fresh.
      //
      // NOTE: Do NOT call scheduler.refreshRows() here — it triggers a
      // full event-layout recalculation that can shrink existing event
      // bars when unrelated bookings are added to other resources.

      return true;
    };

    // On a fresh remount (view change in heatmap mode) the Bryntum React wrapper
    // may not have attached the instance to schedulerRef yet when this effect
    // first fires.  Retry after a short delay so the store is always in sync
    // even if the ref attaches asynchronously.
    if (!sync()) {
      const t = setTimeout(() => sync(), 50);
      return () => clearTimeout(t);
    }

  }, [groupingMode, bryntumResources, bryntumEvents, heatmapEvents, isHeatmapMode, startDate, endDate, resourceTimeRanges, timeRanges, viewPreset, whatIfScenario, isDragEnabled, isCoarseGranularityView]);


  // Atomically update the scheduler's visible date range using setTimeSpan.
  // This prevents the "Invalid start/end dates" error that occurs when the
  // BryntumScheduler React wrapper applies startDate and endDate as individual
  // prop changes — if endDate is set before startDate (or vice versa) during a
  // large date jump (e.g. clicking "Today" after navigating to another year),
  // the Bryntum engine sees a transient state where start > end and throws.
  useLayoutEffect(() => {
    // Date navigation (next/prev arrows, Today, date picker) and view changes
    // now remount the Bryntum instance via schedulerKey, so the remounting
    // instance receives the correct dates as initial props directly.  This
    // effect remains the single owner of setTimeSpan for the residual
    // same-instance span changes that do NOT change the key — e.g. a
    // visible-hours settings change in Day view that shifts startDate/endDate
    // without changing selectedDate/calendarView — and keeps the synced-date
    // refs reflecting reality after a remount.
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler || scheduler.isDestroyed) return;
    if (!startDate || !endDate || startDate >= endDate) return;

    // Single owner of setTimeSpan.  Advance the axis only when the dates
    // actually changed, then record them and flag that the span moved this
    // commit so the store-sync effect (which runs immediately after this
    // layout effect) forces an event re-layout for the new span.  The store-
    // sync effect deliberately no longer calls setTimeSpan, so the axis is
    // advanced exactly once.
    const datesChanged =
      lastSyncedStartRef.current?.getTime() !== startDate.getTime() ||
      lastSyncedEndRef.current?.getTime() !== endDate.getTime();
    if (!datesChanged) return;

    scheduler.setTimeSpan(startDate, endDate);
    lastSyncedStartRef.current = startDate;
    lastSyncedEndRef.current = endDate;
    timeSpanAdvancedRef.current = true;
  }, [startDate, endDate]);

  // Show loading state while theme is loading
  if (!isThemeLoaded) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-sm text-muted-foreground">Loading theme...</p>
        </div>
      </div>
    );
  }

  // CRITICAL: Prevent rendering if dates are invalid
  if (!startDate || !endDate || startDate >= endDate) {
    console.error('🚨 CRITICAL: Cannot render scheduler with invalid dates:', {
      startDate: startDate?.toString(),
      endDate: endDate?.toString(),
      startTime: startDate?.getTime(),
      endTime: endDate?.getTime()
    });
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 text-destructive">⚠︝</div>
          <p className="text-sm text-destructive">Invalid date range. Please refresh the page.</p>
        </div>
      </div>
    );
  }

  // Cast to any to bypass strict Bryntum library type incompatibilities
  const AnyBryntumScheduler = BryntumScheduler as any;

  return (
    <div className="h-full w-full bryntum-scheduler-container custom-style">

      <AnyBryntumScheduler
        key={schedulerKey}
        ref={schedulerRef as any}
        id={schedulerKey}
        startDate={initialDatesRef.current.startDate}
        endDate={initialDatesRef.current.endDate}
        viewPreset={initialViewPresetRef.current}
        resources={bryntumResources}
        events={isHeatmapMode ? heatmapEvents : bryntumEvents}
        resourceTimeRanges={resourceTimeRanges}
        timeRanges={timeRanges}
        columns={columns}
        {...schedulerConfig}
        onEventClick={handleEventClick}
        onEventDblClick={handleEventDblClick}
        onScheduleClick={handleScheduleClick}
        onScheduleDblClick={handleScheduleDblClick}
        onEventDrop={handleEventDrop}
        onEventResizeEnd={handleEventResizeEnd}
        onDragCreateEnd={handleDragCreateEnd}
        onCellClick={handleCellClick}
      />


    </div>
  );
});

BryntumSchedulerTimeline.displayName = 'BryntumSchedulerTimeline';
